import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import UploadDocumentsModal from "./UploadDocumentsModal";
import { validateForm } from "../../common/utils";
import { journeyActions } from "../../store/journeySlice";
import BrandLoader from "../../common/components/BrandLoader";
import { isProposalDocumentSlotFilled } from "../../common/utils/proposalDocumentSlots";
import { SIDE_STEPSS, TEXT_CONTENT } from "../../constants";
import { DateInput } from "./NomineeTab";
import { HoverTooltip } from "../../common/components";
import { useOrganisationListed } from "../../common/hooks/useOrganisationListed";
/* ─── Side‑step configuration ─── */

const BOOLEAN_TOGGLE_FIELDS = new Set([
  "isTobacco",
  "isAlcohol",
  "hasHealthConditions",
  "hasNarcotics",
  "hasHazard",
  "isTravel",
  "medication",
  "physicalCondition",
  "conditionsDiagnosed",
  "surgery",
  "alcoholAdvice",
  "pregnancy",
]);

const normalizeBooleanField = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return !!value;
};

const normalizeMedicalBooleanFields = (data) => {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };
  BOOLEAN_TOGGLE_FIELDS.forEach((key) => {
    if (key in next) next[key] = normalizeBooleanField(next[key]);
  });
  return next;
};

const MEDICAL_STEPS = ["lifestyle", "medical"];

const normalizeMedicalStep = (value) => {
  const step = String(value || "")
    .trim()
    .toLowerCase();
  return MEDICAL_STEPS.includes(step) ? step : "lifestyle";
};

const getMedicalCompletedSteps = (activeStep, sourceData) => {
  const normalizedStep = normalizeMedicalStep(activeStep);
  const isFinalStepDone =
    normalizedStep === "medical" &&
    String(sourceData?.declarationConsentsAccepted || "").toLowerCase() ===
      "true";

  if (isFinalStepDone) return [...MEDICAL_STEPS];
  if (normalizedStep === "medical") return ["lifestyle"];
  return [];
};

/* ─── Shared Helpers ─── */
const FormField = ({ label, error, children, halfWidth, optional }) => (
  <div className={`proposal-field ${halfWidth ? "half" : ""}`}>
    <label className="proposal-field__label label-one m-0 pt-2 pb-1">
      {label}
      {optional && <span className="optional"> (Optional)</span>}
    </label>
    {children}
    {error && <div className="proposal-field__error">{error}</div>}
  </div>
);

const TextInput = ({
  placeholder,
  value,
  onChange,
  name,
  maxLength,
  disabled,
  type = "text",
  step,
  min,
}) => (
  <input
    type={type}
    className="proposal-input"
    placeholder={placeholder || "Enter"}
    value={value || ""}
    onChange={(e) => onChange(name, e.target.value)}
    disabled={disabled}
    maxLength={maxLength}
    step={step}
    min={min}
  />
);

const SelectInput = ({ placeholder, value, onChange, name, options }) => (
  <select
    className="proposal-input proposal-select"
    value={value || ""}
    onChange={(e) => onChange(name, e.target.value)}
  >
    <option value="">{placeholder || "Select"}</option>
    {(options || []).map((opt) => (
      <option
        key={typeof opt === "string" ? opt : opt.value}
        value={typeof opt === "string" ? opt : opt.value}
      >
        {typeof opt === "string" ? opt : opt.label}
      </option>
    ))}
  </select>
);

const TextArea = ({
  placeholder,
  value,
  onChange,
  name,
  rows,
  error,
  maxLength,
}) => (
  <div className="proposal-textarea-wrapper">
    <textarea
      className="proposal-input proposal-textarea"
      placeholder={placeholder || "Description"}
      value={value || ""}
      // onChange={(e) => onChange(name, e.target.value)}
      onChange={(e) => {
        const inputValue = e.target.value;
        if (/^[A-Za-z0-9 ]*$/.test(inputValue)) {
          onChange(name, inputValue);
        }
      }}
      rows={rows || 2}
      maxLength={maxLength}
      autoComplete="Descript-off"
    />
    {error && <span className="proposal-field__error">{error}</span>}
  </div>
);

/* ─── Toggle Switch ─── */
const ToggleSwitch = ({
  label,
  checked,
  error,
  onChange,
  id,
  tooltip,
  disabled,
  toogleContent,
}) => (
  <div className="proposal-toggle-row m-0">
    <label className="proposal-toggle" htmlFor={id}>
      <span >
        {label}
        {tooltip && (
          <span className="proposal-tooltip" title={tooltip}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.3125 0C5.86623 0 4.45243 0.428871 3.2499 1.23238C2.04736 2.03589 1.1101 3.17794 0.556634 4.51413C0.00316839 5.85031 -0.141643 7.32061 0.140511 8.7391C0.422665 10.1576 1.11911 11.4605 2.14178 12.4832C3.16446 13.5059 4.46742 14.2023 5.8859 14.4845C7.30439 14.7666 8.77469 14.6218 10.1109 14.0684C11.4471 13.5149 12.5891 12.5776 13.3926 11.3751C14.1961 10.1726 14.625 8.75877 14.625 7.3125C14.623 5.37373 13.8519 3.51496 12.481 2.14404C11.11 0.773127 9.25127 0.00204737 7.3125 0ZM7.3125 13.5C6.08873 13.5 4.89244 13.1371 3.87491 12.4572C2.85738 11.7773 2.06432 10.811 1.596 9.68035C1.12768 8.54974 1.00515 7.30564 1.24389 6.10538C1.48264 4.90512 2.07194 3.80261 2.93728 2.93728C3.80262 2.07194 4.90512 1.48264 6.10538 1.24389C7.30564 1.00514 8.54974 1.12768 9.68036 1.596C10.811 2.06431 11.7773 2.85738 12.4572 3.87491C13.1371 4.89244 13.5 6.08873 13.5 7.3125C13.4981 8.95296 12.8456 10.5257 11.6857 11.6857C10.5257 12.8456 8.95296 13.4981 7.3125 13.5ZM8.4375 10.6875C8.4375 10.8367 8.37824 10.9798 8.27275 11.0852C8.16726 11.1907 8.02419 11.25 7.875 11.25C7.57663 11.25 7.29049 11.1315 7.07951 10.9205C6.86853 10.7095 6.75 10.4234 6.75 10.125V7.3125C6.60082 7.3125 6.45774 7.25324 6.35225 7.14775C6.24677 7.04226 6.1875 6.89918 6.1875 6.75C6.1875 6.60081 6.24677 6.45774 6.35225 6.35225C6.45774 6.24676 6.60082 6.1875 6.75 6.1875C7.04837 6.1875 7.33452 6.30603 7.5455 6.517C7.75648 6.72798 7.875 7.01413 7.875 7.3125V10.125C8.02419 10.125 8.16726 10.1843 8.27275 10.2898C8.37824 10.3952 8.4375 10.5383 8.4375 10.6875ZM6.1875 4.21875C6.1875 4.05187 6.23699 3.88874 6.3297 3.74999C6.42241 3.61123 6.55419 3.50309 6.70836 3.43923C6.86254 3.37536 7.03219 3.35866 7.19586 3.39121C7.35953 3.42377 7.50987 3.50413 7.62787 3.62213C7.74587 3.74013 7.82623 3.89047 7.85879 4.05414C7.89135 4.21781 7.87464 4.38746 7.81078 4.54164C7.74691 4.69581 7.63877 4.82759 7.50001 4.9203C7.36126 5.01301 7.19813 5.0625 7.03125 5.0625C6.80748 5.0625 6.59286 4.9736 6.43463 4.81537C6.2764 4.65714 6.1875 4.44253 6.1875 4.21875Z"
                fill="#4E4F50"
              />
            </svg>
          </span>
        )}
      </span>
      
      {disabled ? (
        <HoverTooltip content={toogleContent} placement="top">
          
          <div className={`yes-no-group ${disabled ? "disabled" : ""}`}>
            <label className="radio-option">
              <input
                type="radio"
                name={id}
                disabled={disabled}
                checked={checked === true}
                onChange={() => onChange(true)}
              />
              <span className="radio-custom"></span>
              <span>Yes</span>
            </label>

            <label className="radio-option">
              <input
                type="radio"
                name={id}
                disabled={disabled}
                checked={checked === false}
                onChange={() => onChange(false)}
              />
              <span className="radio-custom"></span>
              <span>No</span>
            </label>
          </div>
        </HoverTooltip>
      ) : (
        
        <div className={`yes-no-group ${disabled ? "disabled" : ""}`}>
          <label className="radio-option">
            <input
              type="radio"
              name={id}
              checked={checked === true}
              onChange={() => onChange(true)}
            />
            <span className="radio-custom"></span>
            <span>Yes</span>
          </label>

          <label className="radio-option">
            <input
              type="radio"
              name={id}
              checked={checked === false}
              onChange={() => onChange(false)}
            />
            <span className="radio-custom"></span>
            <span>No</span>
          </label>
        </div>
      )}
    </label>
    {error && <p className="error-toggle-text">{error}</p>}
  </div>
);

/* ─── Checkbox Group (multi-select) ─── */
const CheckboxGroup = ({ options, selected, onChange, columns, error }) => (
  <div className="proposal-checkbox-group-wraper">
    <div
      className={`proposal-checkbox-group w-fit ${columns === 2 ? "two-col checkbox-box" : " radio-group"}`}
    >
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value;
        const lbl = typeof opt === "string" ? opt : opt.label;
        const isChecked = selected.includes(val);
        return (
          <label
            key={val}
            className={`proposal-checkbox-option ${columns === 2 ? "checkbox" : "radio-label"}  ${isChecked ? "checked selected" : ""}`}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => {
                if (isChecked) {
                  onChange(selected.filter((s) => s !== val));
                } else {
                  onChange([...selected, val]);
                }
              }}
            />
            <span>{lbl}</span>
          </label>
        );
      })}
    </div>
    {error && <span className="proposal-field__error">{error}</span>}
  </div>
);

/* ─── Discount Banner ─── */
const DiscountBanner = () => (
  <div className="proposal-discount-banner">
    Complete this journey online and get <strong>10% discount.</strong>
  </div>
);

/* ─── Progress Bar ─── */
const ProgressBar = ({ current, total }) => {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="proposal-side__progress">
      <div className="proposal-side__progress-bar">
        <div
          className="proposal-side__progress-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="proposal-side__progress-text">
        {pct === 100
          ? "You've completed these details!"
          : `You're almost there! Just ${pct}% of your quest is complete. Keep going forward!`}
      </p>
    </div>
  );
};

/* ─── Medical Illustration ─── */
const MedicalIllustration = () => (
  <div className="proposal-side__illustration">
    <svg
      width="151"
      height="150"
      viewBox="0 0 151 150"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M25.5425 60.2754L1.57543 72.4241C0.705343 72.8651 0.357532 73.928 0.798569 74.7981L25.43 123.391C25.871 124.261 26.9339 124.609 27.804 124.168L51.771 112.02C52.6411 111.579 52.9889 110.516 52.5479 109.646L27.9164 61.0523C27.4754 60.1822 26.4125 59.8344 25.5425 60.2754Z"
        fill="#FACED0"
      />
      <path
        d="M13.3385 80.5555C14.3562 79.8712 14.6264 78.4914 13.942 77.4737C13.2576 76.4561 11.8779 76.1859 10.8602 76.8702C9.84256 77.5546 9.57236 78.9344 10.2567 79.952C10.9411 80.9697 12.3208 81.2399 13.3385 80.5555Z"
        fill="#FAFAFA"
      />
      <path
        d="M24.6182 75.1357C25.771 74.7065 26.3576 73.424 25.9284 72.2712C25.4991 71.1184 24.2167 70.5319 23.0639 70.9611C21.9111 71.3903 21.3245 72.6728 21.7538 73.8256C22.183 74.9784 23.4655 75.5649 24.6182 75.1357Z"
        fill="#FAFAFA"
      />
      <path
        d="M19.5179 88.4309C19.7163 88.8226 19.7941 89.2643 19.7415 89.7002C19.6889 90.1361 19.5083 90.5466 19.2224 90.8799C18.9366 91.2132 18.5583 91.4542 18.1355 91.5726C17.7127 91.6909 17.2643 91.6813 16.847 91.5448C16.4296 91.4084 16.0621 91.1513 15.7909 90.8061C15.5196 90.4608 15.3568 90.0429 15.323 89.6052C15.2892 89.1674 15.3859 88.7294 15.601 88.3466C15.816 87.9638 16.1397 87.6534 16.5312 87.4545C17.0568 87.1885 17.6665 87.1419 18.2265 87.3249C18.7864 87.508 19.2509 87.9057 19.5179 88.4309Z"
        fill="#FAFAFA"
      />
      <path
        d="M31.2852 83.6615C31.3267 82.4359 30.3668 81.4086 29.1411 81.3671C27.9154 81.3256 26.8882 82.2855 26.8467 83.5112C26.8051 84.7369 27.7651 85.7641 28.9907 85.8056C30.2164 85.8472 31.2437 84.8872 31.2852 83.6615Z"
        fill="#FAFAFA"
      />
      <path
        d="M24.9499 99.1478C25.1505 99.5387 25.2307 99.9802 25.1803 100.417C25.1299 100.853 24.9512 101.265 24.6668 101.6C24.3824 101.934 24.005 102.177 23.5825 102.298C23.16 102.418 22.7113 102.41 22.2932 102.275C21.875 102.141 21.5063 101.885 21.2336 101.54C20.9608 101.196 20.7964 100.778 20.7611 100.341C20.7258 99.9026 20.8211 99.4641 21.0351 99.0804C21.2491 98.6967 21.5721 98.3851 21.9632 98.185C22.487 97.9171 23.0957 97.868 23.6557 98.0485C24.2157 98.229 24.6812 98.6244 24.9499 99.1478Z"
        fill="#FAFAFA"
      />
      <path
        d="M36.4762 93.3057C36.6749 93.6974 36.753 94.1393 36.7006 94.5754C36.6482 95.0115 36.4676 95.4223 36.1817 95.7557C35.8958 96.0892 35.5174 96.3304 35.0943 96.4488C34.6713 96.5671 34.2227 96.5574 33.8053 96.4208C33.3878 96.2841 33.0202 96.0268 32.749 95.6812C32.4779 95.3356 32.3153 94.9174 32.2818 94.4794C32.2484 94.0415 32.3456 93.6034 32.5612 93.2207C32.7767 92.8379 33.101 92.5277 33.4929 92.3293C34.018 92.0634 34.6272 92.0169 35.1866 92.2C35.746 92.3831 36.2098 92.7808 36.4762 93.3057Z"
        fill="#FAFAFA"
      />
      <path
        d="M30.3845 109.868C30.5823 110.26 30.6595 110.702 30.6064 111.138C30.5533 111.574 30.3721 111.984 30.0859 112.317C29.7997 112.65 29.4213 112.89 28.9984 113.008C28.5755 113.126 28.1272 113.116 27.7101 112.979C27.2929 112.842 26.9257 112.585 26.6548 112.239C26.3839 111.894 26.2215 111.476 26.1881 111.038C26.1547 110.6 26.2517 110.162 26.4671 109.78C26.6824 109.397 27.0062 109.087 27.3978 108.889C27.6582 108.757 27.942 108.677 28.2331 108.655C28.5243 108.633 28.8169 108.668 29.0943 108.759C29.3717 108.85 29.6285 108.995 29.8499 109.185C30.0712 109.376 30.2529 109.608 30.3845 109.868Z"
        fill="#FAFAFA"
      />
      <path
        d="M41.9124 104.025C42.1108 104.416 42.1886 104.858 42.136 105.294C42.0834 105.73 41.9028 106.14 41.617 106.474C41.3311 106.807 40.9529 107.048 40.5301 107.166C40.1073 107.285 39.6588 107.275 39.2415 107.139C38.8242 107.002 38.4567 106.745 38.1854 106.4C37.9141 106.055 37.7513 105.637 37.7175 105.199C37.6837 104.761 37.7805 104.323 37.9955 103.94C38.2106 103.558 38.5343 103.247 38.9257 103.048C39.4513 102.782 40.061 102.736 40.621 102.919C41.181 103.102 41.6454 103.499 41.9124 104.025Z"
        fill="#FAFAFA"
      />
      <path
        d="M12.7379 76.6953C12.5949 77.3793 12.3965 78.0505 12.1447 78.7023C11.9461 79.3715 11.6932 80.0235 11.3887 80.6516C11.528 79.9671 11.7253 79.2957 11.9786 78.6446C12.1756 77.9744 12.4296 77.3222 12.7379 76.6953Z"
        fill="#E0E0E0"
      />
      <path
        d="M24.0153 70.875C23.877 71.5598 23.6796 72.2313 23.4254 72.882C23.228 73.5532 22.974 74.2064 22.666 74.8347C22.8066 74.1501 23.005 73.4787 23.2593 72.8277C23.4583 72.1575 23.7111 71.5045 24.0153 70.875Z"
        fill="#E0E0E0"
      />
      <path
        d="M18.2813 87.3984C18.1804 88.1067 18.0205 88.8052 17.8032 89.4868C17.6434 90.1818 17.4257 90.8622 17.1523 91.5208C17.2579 90.8145 17.4223 90.1182 17.6439 89.4393C17.8006 88.7429 18.0138 88.0603 18.2813 87.3984Z"
        fill="#E0E0E0"
      />
      <path
        d="M29.6603 81.5508C29.592 82.2387 29.4638 82.9193 29.2772 83.5849C29.1505 84.2595 28.9656 84.9219 28.7246 85.5647C28.7923 84.877 28.9194 84.1964 29.1043 83.5306C29.2342 82.8563 29.4202 82.1941 29.6603 81.5508Z"
        fill="#E0E0E0"
      />
      <path
        d="M23.3464 98.1211C23.2585 98.7703 23.1121 99.4103 22.9091 100.033C22.7613 100.673 22.557 101.298 22.2988 101.901C22.387 101.252 22.5345 100.612 22.7395 99.9891C22.8853 99.3495 23.0884 98.7243 23.3464 98.1211Z"
        fill="#E0E0E0"
      />
      <path
        d="M35.1974 92.4219C35.0589 93.0724 34.8615 93.709 34.6076 94.3237C34.4086 94.9589 34.1557 95.5759 33.8516 96.168C33.9901 95.5174 34.1876 94.8809 34.4414 94.2661C34.6412 93.6312 34.8941 93.0143 35.1974 92.4219Z"
        fill="#E0E0E0"
      />
      <path
        d="M29.1301 108.953C28.971 109.629 28.7568 110.292 28.4894 110.933C28.2719 111.593 27.9998 112.234 27.6758 112.848C27.8344 112.172 28.0511 111.511 28.3233 110.872C28.5356 110.21 28.8056 109.568 29.1301 108.953Z"
        fill="#E0E0E0"
      />
      <path
        d="M40.5817 102.754C40.485 103.504 40.3285 104.246 40.1139 104.971C39.9586 105.711 39.7455 106.438 39.4766 107.144C39.5753 106.394 39.7317 105.653 39.9444 104.927C40.0997 104.187 40.3128 103.46 40.5817 102.754Z"
        fill="#E0E0E0"
      />
      <path
        d="M46.0586 109.591C46.079 109.635 42.5532 111.465 38.1833 113.679C33.8134 115.893 30.2504 117.656 30.2267 117.615C30.203 117.574 33.7321 115.74 38.102 113.523C42.4718 111.306 46.0349 109.547 46.0586 109.591Z"
        fill="#E0E0E0"
      />
      <path
        d="M23.1797 66.6578C23.2 66.7019 19.6743 68.5325 15.3044 70.7497C10.9345 72.9668 7.37151 74.7263 7.34777 74.6822C7.32404 74.6382 10.8633 72.7905 15.2231 70.5734C19.5828 68.3562 23.156 66.5968 23.1797 66.6578Z"
        fill="#FDF3F3"
      />
      <path
        d="M15.8186 72.8782C15.7985 72.8827 15.7777 72.8827 15.7576 72.8782C15.7576 72.8782 15.7372 72.8782 15.7305 72.8782L15.8864 72.7969C15.8864 72.824 15.8864 72.8647 15.8322 72.8884C15.7779 72.9121 15.744 72.9054 15.7305 72.8884L15.8864 72.807C15.8875 72.8149 15.8875 72.8229 15.8864 72.8308C15.8695 72.8534 15.8457 72.8701 15.8186 72.8782Z"
        fill="#E0E0E0"
      />
      <path
        d="M37.5901 113.781C37.546 113.805 32.5964 104.123 26.5349 92.1656C20.4733 80.2086 15.5949 70.4891 15.639 70.4688C15.683 70.4484 20.6292 80.1171 26.6942 92.0843C32.7591 104.051 37.6443 113.757 37.5901 113.781Z"
        fill="#FDF3F3"
      />
      <path
        d="M4.73176 120.728C4.69108 128.322 4.66396 140.584 4.6504 146.218C4.6486 147.016 4.96358 147.782 5.52614 148.348C6.0887 148.914 6.85284 149.233 7.65067 149.236L28.3949 149.331C29.1939 149.333 29.9614 149.019 30.529 148.456C31.0965 147.894 31.4178 147.129 31.4223 146.33L31.5376 121.064C31.5448 119.289 30.8472 117.584 29.5981 116.324C28.349 115.063 26.6505 114.35 24.8759 114.341V110.612L11.2917 110.551V114.063H11.2408C9.50228 114.11 7.8503 114.832 6.63516 116.076C5.42003 117.32 4.73731 118.989 4.73176 120.728Z"
        fill="#FACED0"
      />
      <path
        d="M11.307 111.035L11.2969 112.727L24.8778 113.269L24.8913 111.096L11.307 111.035Z"
        fill="#E0E0E0"
      />
      <path
        d="M10.7871 105.376L25.5171 105.443C25.7868 105.444 26.045 105.552 26.2349 105.744C26.4247 105.935 26.5307 106.194 26.5295 106.464L26.5064 111.549C26.5056 111.723 26.4359 111.889 26.3127 112.011C26.1894 112.133 26.0227 112.201 25.8491 112.201L10.3971 112.131C10.2235 112.13 10.0574 112.06 9.93528 111.937C9.81314 111.814 9.74496 111.647 9.74575 111.473L9.76882 106.388C9.77005 106.118 9.87837 105.86 10.07 105.67C10.2616 105.48 10.5207 105.374 10.7905 105.376L10.7871 105.376Z"
        fill="#F5F5F5"
      />
      <path
        d="M11.8805 112.15C11.7821 112.15 11.7076 110.645 11.7177 108.794C11.7279 106.943 11.8126 105.438 11.911 105.438C12.0093 105.438 12.0839 106.939 12.0737 108.794C12.0635 110.648 11.9855 112.15 11.8805 112.15Z"
        fill="#E0E0E0"
      />
      <path
        d="M13.6754 112.158C13.5771 112.158 13.5025 110.656 13.5126 108.802C13.5228 106.947 13.6076 105.445 13.7059 105.445C13.8042 105.445 13.8788 106.951 13.8686 108.805C13.8584 110.659 13.7737 112.158 13.6754 112.158Z"
        fill="#E0E0E0"
      />
      <path
        d="M15.4683 112.166C15.37 112.166 15.2954 110.66 15.3056 108.809C15.3158 106.958 15.4005 105.453 15.4989 105.453C15.5972 105.453 15.6718 106.955 15.6616 108.809C15.6514 110.664 15.5667 112.166 15.4683 112.166Z"
        fill="#E0E0E0"
      />
      <path
        d="M17.2574 112.173C17.1591 112.173 17.0845 110.672 17.0947 108.817C17.1048 106.963 17.1896 105.461 17.2879 105.461C17.3862 105.461 17.4608 106.966 17.4506 108.817C17.4405 110.668 17.3557 112.173 17.2574 112.173Z"
        fill="#E0E0E0"
      />
      <path
        d="M19.0465 112.185C18.9482 112.185 18.8736 110.68 18.8837 108.826C18.8939 106.971 18.9787 105.469 19.077 105.473C19.1753 105.476 19.2499 106.975 19.2397 108.829C19.2295 110.683 19.1448 112.185 19.0465 112.185Z"
        fill="#E0E0E0"
      />
      <path
        d="M20.8375 112.189C20.7392 112.189 20.6646 110.684 20.6748 108.833C20.6849 106.982 20.7697 105.477 20.868 105.477C20.9663 105.477 21.0409 106.978 21.0307 108.833C21.0205 110.687 20.9358 112.189 20.8375 112.189Z"
        fill="#E0E0E0"
      />
      <path
        d="M22.6265 112.197C22.5282 112.197 22.4536 110.695 22.4638 108.841C22.474 106.986 22.5587 105.484 22.6571 105.484C22.7554 105.484 22.83 106.99 22.8198 108.844C22.8096 110.698 22.7282 112.2 22.6265 112.197Z"
        fill="#E0E0E0"
      />
      <path
        d="M24.4234 112.209C24.3251 112.209 24.2505 110.703 24.2607 108.852C24.2709 107.001 24.3556 105.496 24.4539 105.496C24.5522 105.496 24.6268 106.998 24.6167 108.852C24.6065 110.707 24.515 112.209 24.4234 112.209Z"
        fill="#E0E0E0"
      />
      <path
        d="M27.4745 124.484L9.01541 124.401C8.73831 124.399 8.51265 124.623 8.5114 124.9L8.45754 136.769C8.45628 137.046 8.67989 137.272 8.95699 137.273L27.4161 137.357C27.6932 137.358 27.9188 137.134 27.9201 136.857L27.974 124.988C27.9752 124.711 27.7516 124.486 27.4745 124.484Z"
        fill="#FCE4E5"
      />
      <path
        d="M26.6128 136.472C26.6062 136.401 26.6062 136.33 26.6128 136.259C26.6128 136.099 26.6128 135.899 26.6128 135.659C26.6128 135.106 26.6128 134.35 26.5891 133.401C26.5891 131.434 26.5653 128.654 26.5518 125.298L26.6908 125.441L9.635 125.4L9.81129 125.224V126.698C9.81129 127.19 9.81129 127.675 9.81129 128.16C9.81129 129.126 9.81129 130.078 9.79433 131.014C9.79433 132.882 9.7706 134.682 9.76043 136.394L9.59771 136.228L21.8361 136.364L25.3652 136.425L26.3145 136.448L26.6535 136.469C26.5526 136.485 26.4505 136.492 26.3484 136.489H25.433L21.9412 136.52L9.59093 136.543H9.4282V136.377C9.4282 134.682 9.4282 132.868 9.4282 130.997C9.4282 130.061 9.4282 129.112 9.4282 128.143C9.4282 127.661 9.4282 127.173 9.4282 126.681V125.207C9.26886 125.366 9.64517 124.99 9.60788 125.03L26.667 125.142H26.7959V125.305C26.7518 128.695 26.7111 131.509 26.684 133.496C26.684 134.431 26.6501 135.191 26.6399 135.723C26.6399 135.953 26.6399 136.14 26.6196 136.296C26.6237 136.355 26.6214 136.414 26.6128 136.472Z"
        fill="#E35055"
      />
      <path
        d="M24.0083 128.904C24.0083 129.006 21.4115 129.074 18.2112 129.057C15.0109 129.04 12.4141 128.952 12.4141 128.853C12.4141 128.755 15.0109 128.687 18.2146 128.701C21.4183 128.715 24.0083 128.806 24.0083 128.904Z"
        fill="#E35055"
      />
      <path
        d="M23.9952 130.876C23.9952 130.975 21.4017 131.043 18.198 131.029C14.9944 131.015 12.4043 130.921 12.4043 130.822C12.4043 130.724 14.9978 130.656 18.2014 130.67C21.4051 130.683 23.9986 130.778 23.9952 130.876Z"
        fill="#E35055"
      />
      <path
        d="M23.9907 132.764C23.9907 132.862 21.3939 132.93 18.1936 132.916C14.9933 132.903 12.3965 132.811 12.3965 132.713C12.3965 132.615 14.9933 132.547 18.1936 132.56C21.3939 132.574 23.9907 132.669 23.9907 132.764Z"
        fill="#E35055"
      />
      <path
        d="M24.0856 114.221C24.0856 114.319 21.2548 114.384 17.7596 114.37C14.2644 114.357 11.4336 114.262 11.4336 114.163C11.4336 114.065 14.2677 113.997 17.763 114.014C21.2582 114.031 24.0856 114.119 24.0856 114.221Z"
        fill="#E0E0E0"
      />
      <path
        d="M5.77852 144.759C5.72419 144.362 5.70378 143.962 5.71749 143.562C5.71749 142.731 5.69715 141.633 5.68359 140.3C5.68359 137.548 5.68359 133.744 5.71411 129.544C5.73445 127.445 5.75479 125.448 5.77174 123.621C5.78869 121.794 5.97176 120.112 6.57859 118.831C7.02143 117.806 7.77896 116.949 8.7415 116.383C9.00499 116.237 9.28497 116.123 9.57547 116.044C9.67687 116.015 9.78185 116 9.88736 116C9.52292 116.137 9.16935 116.301 8.82964 116.492C7.91847 117.079 7.20869 117.93 6.79556 118.932C6.25653 120.173 6.11753 121.814 6.11753 123.611C6.11753 125.408 6.09719 127.435 6.08702 129.533C6.05651 133.734 6.00566 137.538 5.9548 140.287C5.9209 141.619 5.89378 142.721 5.87344 143.548C5.87281 143.953 5.84108 144.358 5.77852 144.759Z"
        fill="white"
      />
      <path
        d="M30.4659 148.358C30.147 148.288 29.835 148.189 29.5336 148.063C29.242 147.955 28.8827 147.826 28.4555 147.701C28.0284 147.575 27.5538 147.402 27.0113 147.27C25.7528 146.922 24.4747 146.648 23.1839 146.45C21.6016 146.22 20.0059 146.095 18.4072 146.077C16.808 146.082 15.211 146.195 13.6271 146.416C12.3337 146.602 11.0523 146.863 9.78945 147.199C9.24703 147.328 8.77241 147.49 8.34187 147.616C7.91132 147.741 7.55535 147.863 7.26041 147.972C6.95923 148.096 6.6471 148.191 6.32812 148.257C6.60774 148.089 6.90294 147.95 7.20956 147.84C7.49772 147.714 7.85369 147.568 8.27745 147.429C8.70122 147.29 9.17584 147.09 9.72165 146.955C10.9859 146.583 12.2737 146.297 13.5762 146.097C15.1745 145.859 16.7879 145.738 18.4038 145.734C20.0207 145.75 21.6342 145.885 23.2313 146.138C24.5323 146.354 25.8177 146.655 27.0791 147.04C27.6216 147.189 28.0962 147.379 28.5166 147.524C28.9369 147.67 29.2963 147.819 29.5811 147.948C29.8887 148.056 30.1851 148.193 30.4659 148.358Z"
        fill="white"
      />
      <path
        d="M149.256 41.4955C148.873 39.2546 146.866 37.2409 144.598 37.3426C143.927 37.4015 143.275 37.5952 142.681 37.9121C142.087 38.229 141.563 38.6626 141.14 39.1868C140.831 39.5332 140.549 39.9026 140.296 40.292C140.083 39.8787 139.839 39.482 139.567 39.1055C139.204 38.5385 138.73 38.0514 138.172 37.6737C137.615 37.296 136.987 37.0357 136.326 36.9087C134.078 36.5696 131.875 38.3562 131.241 40.5429C130.607 42.7295 131.336 45.123 132.597 47.0079C133.858 48.8928 136.017 51.3371 139.821 52.4321C142.388 51.9812 145.74 49.5301 147.201 47.791C148.663 46.0519 149.639 43.7296 149.256 41.4955Z"
        fill="#FACED0"
      />
      <path
        d="M148.624 45.5796H148.329L147.485 45.5491L144.383 45.4101L144.437 45.3728C144.098 46.3593 143.725 47.5187 143.305 48.7867L143.237 48.9935L143.176 48.7833C142.708 47.173 142.186 45.3694 141.634 43.4709C141.464 42.881 141.295 42.3013 141.132 41.7386L141.264 41.7555L139.057 45.1084L139.037 45.1389H139L133.545 44.8236L131.999 44.7219L131.592 44.6914C131.545 44.6886 131.499 44.6829 131.453 44.6744C131.453 44.6744 131.501 44.6744 131.592 44.6744H132.002L133.548 44.7354L139.006 44.9965L138.945 45.027L141.142 41.6708L141.23 41.5352L141.274 41.6877C141.437 42.2539 141.613 42.8336 141.776 43.4235C142.322 45.3253 142.844 47.1289 143.305 48.7392H143.176L144.336 45.3491V45.3084H144.376L147.475 45.4948L148.316 45.5559H148.536C148.566 45.5591 148.596 45.5671 148.624 45.5796Z"
        fill="#E35055"
      />
      <path
        d="M115.037 51.8198C115.078 51.7844 115.123 51.7526 115.169 51.7249L115.566 51.4639C115.785 51.3302 115.996 51.183 116.197 51.0231C116.481 50.8196 116.755 50.6012 117.017 50.3688L117.485 49.9552C117.641 49.8027 117.803 49.6366 117.97 49.4671C118.148 49.2917 118.318 49.1072 118.478 48.9145C118.644 48.7145 118.834 48.5178 118.993 48.2873C119.777 47.2879 120.427 46.1904 120.926 45.0226C121.538 43.61 121.921 42.1083 122.058 40.5747C122.136 39.7233 122.136 38.8666 122.058 38.0152C121.951 37.1126 121.796 36.2163 121.594 35.3302C121.207 33.5131 120.695 31.5672 120.851 29.5026C121.007 27.438 121.828 25.4344 122.919 23.5597C124.011 21.6849 125.292 19.9661 126.889 18.6575C127.665 18.01 128.536 17.4863 129.472 17.1048C130.374 16.7602 131.316 16.5323 132.276 16.4268C134.045 16.2595 135.825 16.2312 137.598 16.3421C139.132 16.422 140.671 16.3562 142.192 16.1454C143.449 15.9688 144.656 15.5348 145.738 14.8707C146.561 14.3688 147.271 13.7016 147.823 12.9112C148.173 12.4161 148.432 11.8628 148.589 11.2772C148.694 10.8772 148.715 10.6568 148.728 10.6602C148.713 10.8719 148.679 11.0817 148.627 11.2874C148.483 11.8831 148.232 12.4474 147.884 12.9519C147.34 13.7561 146.633 14.4374 145.809 14.9521C144.719 15.634 143.499 16.0819 142.226 16.2675C140.693 16.4885 139.142 16.56 137.595 16.4811C135.83 16.381 134.06 16.415 132.3 16.5828C131.355 16.6924 130.428 16.9202 129.54 17.2608C128.622 17.6373 127.768 18.153 127.008 18.7897C125.441 20.0746 124.177 21.7934 123.082 23.641C121.987 25.4886 121.19 27.482 121.048 29.5026C120.905 31.5231 121.387 33.4555 121.777 35.2827C121.978 36.1779 122.131 37.0832 122.234 37.9948C122.316 38.8574 122.316 39.7257 122.234 40.5883C122.08 42.1388 121.677 43.6543 121.041 45.0768C120.528 46.2504 119.865 47.3523 119.068 48.3551C118.902 48.5856 118.729 48.7789 118.543 48.9789C118.356 49.1789 118.203 49.3654 118.027 49.5315L117.532 50.0163L117.054 50.4265C116.788 50.6561 116.51 50.8712 116.22 51.0706C116.015 51.2272 115.8 51.3699 115.576 51.4978L115.169 51.7418C115.128 51.7719 115.084 51.798 115.037 51.8198Z"
        fill="#E35055"
      />
      <path
        d="M105.824 42.6479C105.824 42.6479 105.845 42.5937 105.892 42.4953C105.939 42.397 106.017 42.2411 106.109 42.0546C106.201 41.8682 106.35 41.6376 106.509 41.3766C106.668 41.1156 106.875 40.8037 107.133 40.485C107.739 39.6935 108.438 38.9775 109.214 38.3526C110.24 37.5189 111.391 36.8519 112.625 36.3761C113.344 36.1046 114.086 35.897 114.842 35.7558C115.663 35.6181 116.494 35.5421 117.327 35.5286C119.046 35.4778 120.883 35.6201 122.802 35.5049C123.783 35.4495 124.755 35.2858 125.701 35.0167C126.683 34.7264 127.627 34.3177 128.511 33.7996C130.372 32.7018 131.96 31.1947 133.152 29.3925C134.336 27.5806 135.143 25.5492 135.525 23.4191C135.889 21.4381 135.944 19.4128 135.688 17.4151C135.447 15.5065 134.983 13.7267 134.715 12.0248C134.412 10.4489 134.39 8.832 134.647 7.24811C134.901 5.94734 135.405 4.70837 136.132 3.60032C136.677 2.76433 137.339 2.01083 138.098 1.36284C138.582 0.96061 139.099 0.601039 139.644 0.288162L140.082 0.067803L140.234 0L140.092 0.0847534L139.665 0.322063C139.129 0.64566 138.621 1.01305 138.146 1.42047C137.402 2.07224 136.754 2.82546 136.22 3.65796C135.511 4.75911 135.02 5.98583 134.773 7.27184C134.529 8.8405 134.56 10.4396 134.864 11.9977C135.135 13.6928 135.607 15.4658 135.857 17.3914C136.119 19.4078 136.067 21.4527 135.701 23.453C135.32 25.6072 134.507 27.6621 133.311 29.4942C132.104 31.3228 130.495 32.8511 128.606 33.9624C127.708 34.4865 126.749 34.8998 125.751 35.193C124.794 35.466 123.809 35.6309 122.816 35.6846C120.876 35.7964 119.042 35.6473 117.33 35.6846C116.506 35.6962 115.683 35.7687 114.869 35.9015C114.121 36.0366 113.386 36.2374 112.672 36.5016C110.515 37.3268 108.621 38.7195 107.19 40.5325C106.933 40.8443 106.74 41.1529 106.556 41.4071C106.373 41.6614 106.248 41.8987 106.143 42.0851C106.038 42.2716 105.967 42.4241 105.909 42.5123C105.851 42.6004 105.824 42.6479 105.824 42.6479Z"
        fill="#E35055"
      />
      <path
        d="M136.021 3.48438C136.204 3.56971 136.379 3.67066 136.544 3.7861C136.883 3.9895 137.337 4.29801 137.9 4.69126C140.866 6.7511 143.612 9.11186 146.094 11.736C146.565 12.2411 146.941 12.6547 147.192 12.9496C147.331 13.099 147.457 13.2601 147.568 13.431C147.417 13.2996 147.277 13.1568 147.148 13.0039C146.853 12.7055 146.47 12.3258 146.005 11.8309C145.036 10.8477 143.663 9.5222 142.066 8.1492C140.469 6.77619 138.957 5.61338 137.839 4.80314L136.51 3.84712C136.337 3.73959 136.174 3.61828 136.021 3.48438Z"
        fill="#FACED0"
      />
      <path
        d="M134.682 7.10547C134.819 7.20827 134.948 7.32162 135.068 7.44448L136.085 8.42084C136.943 9.24464 138.143 10.3634 139.516 11.5499C140.889 12.7365 142.17 13.7569 143.113 14.4858L144.225 15.3469C144.368 15.4473 144.502 15.5607 144.625 15.6859C144.47 15.61 144.322 15.5191 144.184 15.4147C143.906 15.2282 143.506 14.9502 143.032 14.6011C142.069 13.8993 140.767 12.906 139.391 11.7025C138.014 10.499 136.824 9.37347 136 8.52593C135.583 8.09878 135.251 7.74959 135.027 7.50889C134.897 7.38779 134.781 7.25246 134.682 7.10547Z"
        fill="#FACED0"
      />
      <path
        d="M135.129 11.5508C135.438 11.7377 135.73 11.9522 136 12.1915C136.522 12.6051 137.231 13.2086 137.997 13.8866C138.763 14.5646 139.438 15.1918 139.916 15.6562C140.19 15.8933 140.439 16.1568 140.662 16.4427C140.365 16.2394 140.085 16.0127 139.824 15.7647L137.872 14.0425L135.915 12.3203C135.631 12.088 135.367 11.8305 135.129 11.5508Z"
        fill="#FACED0"
      />
      <path
        d="M131.762 16.8867C131.999 17.0242 132.22 17.1868 132.423 17.3715L133.965 18.6021L135.525 19.8158C135.754 19.9684 135.964 20.147 136.152 20.348C135.901 20.2356 135.664 20.0933 135.447 19.9243C135.026 19.6429 134.457 19.2463 133.85 18.7547C133.243 18.2631 132.708 17.819 132.335 17.4766C132.119 17.3051 131.927 17.1069 131.762 16.8867Z"
        fill="#FACED0"
      />
      <path
        d="M127.768 18.5164C127.788 18.496 128.188 18.8859 128.852 19.4894C129.517 20.0928 130.453 20.8895 131.554 21.7201C132.656 22.5506 133.68 23.2388 134.439 23.7202C134.86 23.9616 135.269 24.2241 135.663 24.5068C135.538 24.467 135.417 24.4136 135.304 24.3474C135.08 24.2355 134.758 24.0593 134.368 23.8355C133.354 23.2397 132.376 22.5843 131.439 21.8726C130.503 21.1608 129.612 20.392 128.771 19.5707C128.449 19.2554 128.195 18.991 128.029 18.8045C127.929 18.7211 127.841 18.6241 127.768 18.5164Z"
        fill="#FACED0"
      />
      <path
        d="M125.229 21.54C125.259 21.496 127.236 22.9571 129.636 24.8013C132.036 26.6456 133.962 28.1711 133.928 28.2118C133.894 28.2525 131.924 26.7947 129.521 24.9505C127.117 23.1063 125.198 21.5705 125.229 21.54Z"
        fill="#FACED0"
      />
      <path
        d="M122.162 24.2812C122.3 24.3597 122.428 24.4529 122.545 24.5592L123.562 25.3627C124.417 26.0407 125.596 26.9764 126.932 27.9765C128.268 28.9766 129.488 29.8512 130.38 30.4784L131.438 31.2276C131.572 31.3114 131.697 31.4091 131.81 31.5192C131.665 31.4563 131.527 31.3791 131.397 31.2887C131.132 31.1293 130.759 30.892 130.305 30.5903C129.393 29.9902 128.149 29.1291 126.817 28.1291C125.484 27.129 124.308 26.1763 123.477 25.4678C123.06 25.1288 122.728 24.8237 122.501 24.6169C122.376 24.5176 122.263 24.4051 122.162 24.2812Z"
        fill="#FACED0"
      />
      <path
        d="M121.231 28.255C121.262 28.211 122.926 29.4518 124.977 31.0214C127.028 32.591 128.638 33.8996 128.605 33.9403C128.571 33.981 126.909 32.7436 124.858 31.174C122.807 29.6043 121.197 28.2957 121.231 28.255Z"
        fill="#FACED0"
      />
      <path
        d="M120.365 32.3867C121.124 32.7849 121.853 33.2381 122.545 33.7428C123.273 34.1923 123.968 34.6943 124.623 35.2446C123.862 34.8485 123.132 34.3952 122.44 33.8885C121.711 33.4419 121.017 32.9398 120.365 32.3867Z"
        fill="#FACED0"
      />
      <path
        d="M116.07 36.3398C116.412 36.4963 116.74 36.6823 117.05 36.8958C117.64 37.2654 118.443 37.7942 119.305 38.4112C120.166 39.0282 120.928 39.6215 121.464 40.0588C121.768 40.2818 122.05 40.5339 122.305 40.8114C121.981 40.6205 121.673 40.4052 121.383 40.1673C120.823 39.7537 120.054 39.1842 119.193 38.5672C118.332 37.9502 117.548 37.4043 116.979 37.0077C116.658 36.8108 116.354 36.5874 116.07 36.3398Z"
        fill="#FACED0"
      />
      <path
        d="M112.334 36.4102C112.475 36.4842 112.607 36.574 112.727 36.678C112.975 36.861 113.324 37.1356 113.744 37.478C114.599 38.1561 115.758 39.1392 116.995 40.2647C118.233 41.3903 119.311 42.4548 120.074 43.2413C120.457 43.6345 120.752 43.96 120.965 44.1871C121.078 44.2986 121.179 44.4213 121.267 44.5533C121.137 44.4612 121.018 44.3555 120.911 44.238L119.979 43.3396C119.192 42.5768 118.101 41.5327 116.867 40.4071C115.633 39.2816 114.494 38.2917 113.66 37.5798L112.676 36.7356C112.551 36.639 112.437 36.53 112.334 36.4102Z"
        fill="#FACED0"
      />
      <path
        d="M109.197 39.1992C109.349 39.2853 109.492 39.3852 109.624 39.4976C109.889 39.701 110.269 39.9959 110.733 40.3688C111.665 41.1146 112.937 42.1622 114.316 43.3487C115.696 44.5353 116.927 45.6303 117.805 46.4372C118.246 46.8406 118.595 47.1728 118.822 47.4033C118.954 47.5163 119.074 47.6424 119.181 47.7796C119.035 47.6843 118.898 47.5766 118.771 47.4576L117.703 46.5389C116.774 45.7354 115.54 44.6709 114.181 43.4877L110.635 40.4705L109.577 39.5382C109.44 39.4371 109.313 39.3237 109.197 39.1992Z"
        fill="#FACED0"
      />
      <path
        d="M111.464 25.75H32.8297C30.6671 25.75 28.9141 27.5031 28.9141 29.6656V145.92C28.9141 148.083 30.6671 149.836 32.8297 149.836H111.464C113.626 149.836 115.379 148.083 115.379 145.92V29.6656C115.379 27.5031 113.626 25.75 111.464 25.75Z"
        fill="#263238"
      />
      <path d="M112.61 34.6016H31.5996V138.228H112.61V34.6016Z" fill="white" />
      <path
        d="M75.4621 143.616C75.4621 144.272 75.2674 144.914 74.9025 145.46C74.5377 146.006 74.0191 146.431 73.4125 146.682C72.8058 146.934 72.1383 146.999 71.4944 146.871C70.8505 146.742 70.2591 146.426 69.7951 145.961C69.3311 145.497 69.0153 144.905 68.8876 144.261C68.7599 143.617 68.8261 142.95 69.0778 142.343C69.3296 141.737 69.7555 141.219 70.3017 140.855C70.848 140.49 71.49 140.296 72.1465 140.297C72.5822 140.297 73.0137 140.383 73.4161 140.55C73.8186 140.716 74.1843 140.961 74.4922 141.269C74.8001 141.577 75.0443 141.943 75.2107 142.346C75.3771 142.749 75.4625 143.18 75.4621 143.616Z"
        fill="#455A64"
      />
      <path
        d="M84.7544 29.3242H59.5386C59.3102 29.3242 59.125 29.5094 59.125 29.7378V30.2802C59.125 30.5087 59.3102 30.6938 59.5386 30.6938H84.7544C84.9828 30.6938 85.168 30.5087 85.168 30.2802V29.7378C85.168 29.5094 84.9828 29.3242 84.7544 29.3242Z"
        fill="#455A64"
      />
      <path
        d="M105.21 42.6953H40.6719V53.5912H105.21V42.6953Z"
        fill="#AFB0B1"
      />
      <path
        d="M60.3945 46.4453H60.8827V49.8693H60.3945V46.4453Z"
        fill="white"
      />
      <path
        d="M64.8562 46.4453V49.8693H64.4562L62.3983 47.3335V49.8863H61.9102V46.4623H62.3102L64.368 49.015V46.4623L64.8562 46.4453Z"
        fill="white"
      />
      <path
        d="M65.5703 49.4815L65.75 49.0984C66.0694 49.3589 66.4701 49.4993 66.8823 49.495C67.4722 49.495 67.7264 49.2645 67.7264 48.9662C67.7264 48.1288 65.6517 48.6576 65.6517 47.3863C65.6517 46.8575 66.0653 46.4066 66.9637 46.4066C67.3527 46.3993 67.7355 46.5053 68.0655 46.7117L67.9027 47.1016C67.6221 46.9224 67.2966 46.8261 66.9637 46.8236C66.3806 46.8236 66.1331 47.0677 66.1331 47.366C66.1331 48.2034 68.2078 47.6779 68.2078 48.9356C68.2078 49.4611 67.7875 49.912 66.8823 49.912C66.4081 49.9259 65.9441 49.7736 65.5703 49.4815Z"
        fill="white"
      />
      <path
        d="M68.8906 48.3912V46.4453H69.3788V48.3709C69.3788 49.1303 69.7178 49.4727 70.345 49.4727C70.9722 49.4727 71.3112 49.1337 71.3112 48.3709V46.4453H71.7892V48.3912C71.7892 49.3846 71.2434 49.91 70.3382 49.91C69.433 49.91 68.8906 49.3846 68.8906 48.3912Z"
        fill="white"
      />
      <path
        d="M75.0808 49.8717L74.3417 48.8208H74.1282H73.2806V49.8615H72.7891V46.4375H74.1451C75.0333 46.4375 75.5724 46.885 75.5724 47.6308C75.5836 47.8688 75.5159 48.1038 75.3798 48.2994C75.2437 48.495 75.0468 48.6401 74.8197 48.7123L75.63 49.8615L75.0808 49.8717ZM75.0808 47.641C75.0808 47.1528 74.7418 46.8714 74.1248 46.8714H73.2942V48.4139H74.1248C74.7384 48.4139 75.0672 48.1292 75.0672 47.641H75.0808Z"
        fill="white"
      />
      <path
        d="M78.534 49.0312H76.7135L76.3338 49.8889H75.832L77.3813 46.4648H77.8661L79.4222 49.8889H78.9103L78.534 49.0312ZM78.3611 48.6413L77.622 46.9462L76.883 48.6413H78.3611Z"
        fill="white"
      />
      <path
        d="M82.8762 46.4453V49.8693H82.4762L80.4218 47.3335V49.8863H79.9336V46.4623H80.3336L82.388 49.015V46.4623L82.8762 46.4453Z"
        fill="white"
      />
      <path
        d="M83.6251 48.1571C83.6221 47.9206 83.6675 47.686 83.7586 47.4677C83.8496 47.2494 83.9845 47.0521 84.1547 46.8878C84.3249 46.7236 84.527 46.596 84.7485 46.5129C84.9699 46.4297 85.206 46.3928 85.4423 46.4044C85.6847 46.3948 85.9264 46.4373 86.151 46.5292C86.3756 46.621 86.5778 46.76 86.7441 46.9367L86.4254 47.2452C86.3028 47.1135 86.1535 47.0093 85.9875 46.9398C85.8214 46.8702 85.6425 46.8368 85.4626 46.8418C85.2899 46.8418 85.1188 46.8758 84.9592 46.9419C84.7996 47.008 84.6546 47.1049 84.5325 47.227C84.4103 47.3492 84.3135 47.4942 84.2474 47.6538C84.1813 47.8133 84.1472 47.9844 84.1472 48.1571C84.1472 48.3299 84.1813 48.5009 84.2474 48.6605C84.3135 48.8201 84.4103 48.9651 84.5325 49.0872C84.6546 49.2094 84.7996 49.3063 84.9592 49.3724C85.1188 49.4385 85.2899 49.4725 85.4626 49.4725C85.643 49.4762 85.8221 49.4415 85.988 49.3708C86.154 49.3 86.3031 49.1949 86.4254 49.0623L86.7441 49.3708C86.5792 49.5479 86.3783 49.6876 86.1549 49.7806C85.9316 49.8735 85.6909 49.9176 85.449 49.9098C85.2122 49.9224 84.9754 49.8862 84.7531 49.8035C84.5309 49.7208 84.328 49.5933 84.157 49.429C83.986 49.2647 83.8506 49.067 83.7591 48.8482C83.6676 48.6295 83.622 48.3942 83.6251 48.1571Z"
        fill="white"
      />
      <path
        d="M89.9317 49.4422V49.8693H87.4434V46.4453H89.8571V46.8691H87.9315V47.9234H89.6469V48.3506H87.9315V49.4557L89.9317 49.4422Z"
        fill="white"
      />
      <path
        d="M106.807 67.944C106.807 67.944 106.807 67.8829 106.807 67.7609C106.807 67.6388 106.807 67.4524 106.807 67.2083C106.807 66.7133 106.807 65.9641 106.79 64.9403L106.817 64.9674C98.7923 65.0047 72.2069 65.0284 40.7091 65.0284L40.7973 64.9403V67.944L40.7091 67.8558L87.5438 67.8931L101.65 67.9202H105.484H106.481H105.495H101.681L87.5947 67.9473L40.6956 67.9846H40.6074V64.8047H40.699C72.2035 64.8047 98.7889 64.8318 106.807 64.8691V64.8928C106.807 65.9099 106.807 66.676 106.807 67.1744C106.807 67.4185 106.807 67.5982 106.807 67.7202C106.807 67.8423 106.807 67.944 106.807 67.944Z"
        fill="#263238"
      />
      <path
        d="M52.6864 60.8655C52.6864 60.9163 49.7811 60.9536 46.1977 60.9536C42.6143 60.9536 39.709 60.9163 39.709 60.8655C39.709 60.8146 42.6143 60.7773 46.1977 60.7773C49.7811 60.7773 52.6864 60.818 52.6864 60.8655Z"
        fill="#263238"
      />
      <path
        d="M105.82 113.858C105.82 113.858 105.82 113.793 105.82 113.671C105.82 113.549 105.82 113.366 105.82 113.118C105.82 112.623 105.82 111.878 105.82 110.854L105.844 110.878C97.8196 110.915 71.2376 110.942 39.7365 110.942L39.8246 110.854V113.858L39.7365 113.769L86.5678 113.803L100.678 113.83H104.508H105.508C105.732 113.83 105.847 113.83 105.847 113.83H105.508H104.519H100.708L86.622 113.858L39.7229 113.895H39.6348V110.715H39.7263C71.2308 110.715 97.8129 110.742 105.834 110.779H105.861V110.803C105.861 111.82 105.861 112.583 105.844 113.085C105.844 113.325 105.844 113.505 105.844 113.63C105.844 113.756 105.82 113.858 105.82 113.858Z"
        fill="#263238"
      />
      <path
        d="M53.2685 107.834C53.2685 107.885 50.3631 107.922 46.7797 107.922C43.1964 107.922 40.291 107.885 40.291 107.834C40.291 107.783 43.1964 107.746 46.7797 107.746C50.3631 107.746 53.2685 107.787 53.2685 107.834Z"
        fill="#263238"
      />
      <path
        d="M105.82 79.4205C105.82 79.4205 105.82 79.3595 105.82 79.2374C105.82 79.1154 105.82 78.9323 105.82 78.6849C105.82 78.1899 105.82 77.4441 105.82 76.4202L105.844 76.444C97.8196 76.4813 71.2376 76.5084 39.7365 76.5084L39.8246 76.4202V79.4205L39.7365 79.3324L86.5678 79.3697L100.678 79.3968H104.508H105.508H105.847C105.847 79.3968 105.739 79.3968 105.508 79.3968H104.519H100.708L86.622 79.4239L39.7229 79.4578H39.6348V76.2812H39.7263C71.2308 76.2812 97.8129 76.3084 105.834 76.3457H105.861V76.3694C105.861 77.3864 105.861 78.1492 105.844 78.6476C105.844 78.8916 105.844 79.0713 105.844 79.1968C105.844 79.3222 105.82 79.4205 105.82 79.4205Z"
        fill="#263238"
      />
      <path
        d="M52.6864 73.3967C52.6864 73.4476 49.7811 73.4849 46.1977 73.4849C42.6143 73.4849 39.709 73.4476 39.709 73.3967C39.709 73.3459 42.6143 73.3086 46.1977 73.3086C49.7811 73.3086 52.6864 73.3493 52.6864 73.3967Z"
        fill="#263238"
      />
      <path
        d="M67.8199 90.8971C67.8199 90.8971 67.8199 90.8259 67.8199 90.6971C67.8199 90.5682 67.8199 90.3581 67.8199 90.1174C67.8199 89.6054 67.8199 88.8596 67.7996 87.8934L67.8369 87.9307C62.9517 87.9612 52.1914 87.9816 39.7191 87.9816C39.7598 87.9443 39.631 88.0697 39.8073 87.8934V90.8869L39.7191 90.7988L59.6734 90.8361L65.6435 90.8632H67.264H67.6877H67.8369H67.6979H67.2877H65.6875L59.7345 90.8903L39.7327 90.9276H39.6445V87.846L39.7327 87.7578C52.205 87.7578 62.9653 87.7782 67.8505 87.8087H67.8877V87.846C67.8877 88.8223 67.8708 89.5749 67.864 90.0902C67.864 90.3377 67.864 90.5276 67.864 90.6632C67.864 90.7988 67.8199 90.8971 67.8199 90.8971Z"
        fill="#263238"
      />
      <path
        d="M52.6864 84.8772C52.6864 84.9247 49.7811 84.9653 46.1977 84.9653C42.6143 84.9653 39.709 84.9247 39.709 84.8772C39.709 84.8297 42.6143 84.7891 46.1977 84.7891C49.7811 84.7891 52.6864 84.8263 52.6864 84.8772Z"
        fill="#263238"
      />
      <path
        d="M44.3849 101.811C44.3795 101.737 44.3795 101.662 44.3849 101.587C44.3849 101.425 44.3849 101.221 44.3679 100.977C44.3679 100.431 44.3442 99.6956 44.3306 98.8108L44.3984 98.8786L39.72 98.8989C39.6997 98.8989 39.8862 98.7294 39.8082 98.8108V99.5939C39.8082 99.855 39.8082 100.109 39.8082 100.36C39.8082 100.862 39.8082 101.347 39.8082 101.808L39.7268 101.726C41.0829 101.726 42.2423 101.75 43.0729 101.767L44.0458 101.791C44.1659 101.79 44.2858 101.795 44.4052 101.808C44.2931 101.825 44.1796 101.832 44.0662 101.828L43.1102 101.848C42.2864 101.865 41.1066 101.882 39.72 101.889H39.6387V101.808C39.6387 101.347 39.6387 100.862 39.6387 100.36V98.8108L39.7268 98.7227L44.4052 98.7396H44.4764V98.8108C44.4764 99.7431 44.4527 100.506 44.4391 101.021C44.4391 101.258 44.4391 101.455 44.4222 101.614C44.4185 101.681 44.406 101.747 44.3849 101.811Z"
        fill="#263238"
      />
      <path
        d="M50.5676 101.813C50.5623 101.738 50.5623 101.663 50.5676 101.589C50.5676 101.426 50.5608 101.223 50.5473 100.979C50.5473 100.433 50.5269 99.6972 50.51 98.8124L50.5812 98.8802L45.9028 98.9005C45.8825 98.9005 46.0689 98.731 45.9909 98.8124V99.2056C45.9909 99.3378 45.9909 99.4667 45.9909 99.5955V100.362C45.9909 100.863 45.9909 101.348 45.9909 101.809L45.9096 101.728C47.2656 101.728 48.4251 101.752 49.2522 101.769L50.2252 101.792C50.3463 101.791 50.4674 101.797 50.588 101.809C50.4758 101.826 50.3623 101.833 50.2489 101.83L49.2929 101.85C48.4691 101.867 47.2894 101.884 45.9028 101.891H45.8214V101.809C45.8214 101.348 45.8214 100.863 45.8214 100.362V98.8124C45.7401 98.8903 45.9265 98.7039 45.9096 98.7242L50.588 98.7412H50.6592V98.8124C50.6592 99.7447 50.6354 100.507 50.6219 101.023C50.6219 101.26 50.6219 101.457 50.6219 101.616C50.6127 101.684 50.5945 101.75 50.5676 101.813Z"
        fill="#263238"
      />
      <path
        d="M56.7466 101.811C56.7343 101.737 56.7286 101.662 56.7296 101.587C56.7296 101.425 56.7296 101.221 56.7127 100.977C56.7127 100.431 56.6923 99.6956 56.6754 98.8108L56.7466 98.8786L52.0648 98.8989L52.153 98.8108V100.36C52.153 100.862 52.153 101.347 52.153 101.808L52.0716 101.726C53.4276 101.726 54.5905 101.75 55.4177 101.767L56.3906 101.791C56.5118 101.79 56.6328 101.796 56.7534 101.808C56.6412 101.825 56.5277 101.831 56.4144 101.828L55.4617 101.848C54.6379 101.865 53.4548 101.882 52.0716 101.889H51.9902V101.808C51.9902 101.347 51.9902 100.862 51.9902 100.36C51.9902 100.109 51.9902 99.855 51.9902 99.5939V98.8108L52.0784 98.7227L56.7602 98.7396H56.8313V98.8108C56.8313 99.7431 56.8076 100.506 56.7974 101.021C56.7974 101.258 56.7974 101.455 56.7771 101.614C56.7739 101.681 56.7637 101.747 56.7466 101.811Z"
        fill="#263238"
      />
      <path
        d="M62.9226 101.811C62.9226 101.811 62.9226 101.73 62.9226 101.587C62.9226 101.445 62.9226 101.221 62.9226 100.977C62.9226 100.431 62.8989 99.6956 62.8853 98.8108L62.9531 98.8786L58.2747 98.8989C58.2544 98.8989 58.4408 98.7294 58.3629 98.8108V99.5939C58.3629 99.855 58.3629 100.109 58.3629 100.36C58.3629 100.862 58.3629 101.347 58.3629 101.808L58.2815 101.726C59.6376 101.726 60.797 101.75 61.6276 101.767L62.6005 101.791C62.7205 101.79 62.8405 101.795 62.9599 101.808C62.8478 101.825 62.7343 101.832 62.6209 101.828L61.6649 101.848C60.8444 101.865 59.6613 101.882 58.2747 101.889H58.1934V101.808C58.1934 101.347 58.1934 100.862 58.1934 100.36V98.8108L58.2815 98.7227L62.9599 98.7396H63.0311V98.8108C63.0311 99.7431 63.0073 100.506 62.9938 101.021C62.9938 101.258 62.9938 101.455 62.9768 101.614C62.9677 101.682 62.9495 101.748 62.9226 101.811Z"
        fill="#263238"
      />
      <path
        d="M69.1017 101.811C69.0963 101.737 69.0963 101.662 69.1017 101.587C69.1017 101.425 69.1017 101.221 69.0813 100.977C69.0813 100.431 69.061 99.6956 69.0474 98.8108L69.1152 98.8786L64.4368 98.8989C64.4165 98.8989 64.6029 98.7294 64.525 98.8108V99.5939C64.525 99.855 64.525 100.109 64.525 100.36C64.525 100.862 64.525 101.347 64.525 101.808L64.4436 101.726C65.7997 101.726 66.9591 101.75 67.7863 101.767L68.7593 101.791C68.8804 101.79 69.0015 101.795 69.122 101.808C69.0098 101.825 68.8964 101.832 68.783 101.828L67.827 101.848C67.0032 101.865 65.8234 101.882 64.4368 101.889H64.3555V101.808C64.3555 101.347 64.3555 100.862 64.3555 100.36V98.8108L64.4436 98.7227L69.122 98.7396H69.1762V98.8108C69.1762 99.7431 69.1525 100.506 69.1389 101.021C69.1389 101.258 69.1389 101.455 69.1389 101.614C69.1353 101.681 69.1227 101.747 69.1017 101.811Z"
        fill="#263238"
      />
      <path
        d="M75.2794 101.811C75.2727 101.737 75.2727 101.662 75.2794 101.587C75.2794 101.425 75.2794 101.221 75.2624 100.977C75.2624 100.431 75.2421 99.6956 75.2251 98.8108L75.2963 98.8786L70.6146 98.8989L70.7027 98.8108V100.36C70.7027 100.862 70.7027 101.347 70.7027 101.808L70.6213 101.726C71.9774 101.726 73.1402 101.75 73.9674 101.767L74.9404 101.791C75.0615 101.79 75.1826 101.795 75.3031 101.808C75.191 101.825 75.0775 101.831 74.9641 101.828L74.0115 101.848C73.1877 101.865 72.0045 101.882 70.6213 101.889H70.5332V101.808C70.5332 101.347 70.5332 100.862 70.5332 100.36C70.5332 100.109 70.5332 99.855 70.5332 99.5939V98.8108L70.6213 98.7227L75.3031 98.7396H75.3743V98.8108C75.3743 99.7431 75.3506 100.506 75.3404 101.021C75.3404 101.258 75.3404 101.455 75.3201 101.614C75.3136 101.681 75.2999 101.747 75.2794 101.811Z"
        fill="#263238"
      />
      <path
        d="M81.4614 101.811C81.4491 101.737 81.4435 101.662 81.4445 101.587C81.4445 101.425 81.4445 101.221 81.4275 100.977C81.4275 100.431 81.4038 99.6956 81.3902 98.8108L81.4614 98.8786L76.7797 98.8989C76.7593 98.8989 76.9458 98.7294 76.8678 98.8108V100.36C76.8678 100.862 76.8678 101.347 76.8678 101.808L76.7864 101.726C78.1425 101.726 79.3019 101.75 80.1325 101.767L81.1055 101.791C81.2266 101.79 81.3477 101.796 81.4682 101.808C81.356 101.824 81.2426 101.831 81.1292 101.828L80.1766 101.848C79.3528 101.865 78.1696 101.882 76.7864 101.889H76.7051V101.808C76.7051 101.347 76.7051 100.862 76.7051 100.36C76.7051 100.109 76.7051 99.855 76.7051 99.5939V98.8108L76.7932 98.7227L81.475 98.7396H81.5428V98.8108C81.5428 99.7431 81.5191 100.506 81.5055 101.021C81.5055 101.258 81.5055 101.455 81.4886 101.614C81.4865 101.681 81.4774 101.747 81.4614 101.811Z"
        fill="#263238"
      />
      <path
        d="M87.6399 101.813C87.6346 101.738 87.6346 101.663 87.6399 101.589C87.6399 101.426 87.6399 101.223 87.6229 100.979C87.6229 100.433 87.5992 99.6972 87.5856 98.8124L87.6535 98.8802L82.9751 98.9005C82.9547 98.9005 83.1412 98.731 83.0632 98.8124V99.5955C83.0632 99.8565 83.0632 100.111 83.0632 100.362C83.0632 100.863 83.0632 101.348 83.0632 101.809L82.9818 101.728C84.3379 101.728 85.4973 101.752 86.3279 101.769L87.3009 101.792C87.4209 101.791 87.5408 101.797 87.6602 101.809C87.5481 101.826 87.4346 101.833 87.3212 101.83L86.3652 101.85C85.5414 101.867 84.3616 101.884 82.9751 101.891H82.8937V101.809C82.8937 101.348 82.8937 100.863 82.8937 100.362V98.8124C82.8123 98.8903 82.9988 98.7039 82.9818 98.7242L87.6602 98.7412H87.7314V98.8124C87.7314 99.7447 87.7077 100.507 87.6941 101.023C87.6941 101.26 87.6941 101.457 87.6772 101.616C87.6735 101.683 87.661 101.749 87.6399 101.813Z"
        fill="#263238"
      />
      <path
        d="M93.8204 101.813C93.8103 101.738 93.8046 101.664 93.8035 101.589C93.8035 101.426 93.8035 101.223 93.7865 100.979C93.7865 100.433 93.7662 99.6972 93.7492 98.8124L93.8204 98.8802L89.142 98.9005C89.1217 98.9005 89.3082 98.731 89.2268 98.8124V100.362C89.2268 100.863 89.2268 101.348 89.2268 101.809L89.1454 101.728C90.5015 101.728 91.6643 101.752 92.4915 101.769L93.4645 101.792C93.5856 101.791 93.7067 101.797 93.8272 101.809C93.715 101.826 93.6016 101.833 93.4882 101.83L92.5322 101.85C91.7084 101.867 90.5252 101.884 89.142 101.891H89.0641V101.809C89.0641 101.348 89.0641 100.863 89.0641 100.362V99.5955C89.0641 99.4667 89.0641 99.3378 89.0641 99.2056V98.8124C88.9861 98.8903 89.1726 98.7039 89.1556 98.7242L93.834 98.7412H93.9052V98.8124C93.9052 99.7447 93.8781 100.507 93.8679 101.023C93.8679 101.26 93.8679 101.457 93.8475 101.616C93.8455 101.682 93.8364 101.748 93.8204 101.813Z"
        fill="#263238"
      />
      <path
        d="M99.9985 101.811C99.9862 101.737 99.9806 101.662 99.9816 101.587C99.9816 101.425 99.9816 101.221 99.9646 100.977C99.9646 100.431 99.9443 99.6956 99.9273 98.8108L99.9985 98.8786L95.3168 98.8989C95.2964 98.8989 95.4829 98.7294 95.4049 98.8108V100.36C95.4049 100.862 95.4049 101.347 95.4049 101.808L95.3235 101.726C96.6796 101.726 97.839 101.75 98.6696 101.767L99.6426 101.791C99.7637 101.79 99.8848 101.796 100.005 101.808C99.8932 101.825 99.7797 101.831 99.6663 101.828L98.7137 101.848C97.8899 101.865 96.7067 101.882 95.3235 101.889H95.2422V101.808C95.2422 101.347 95.2422 100.862 95.2422 100.36C95.2422 100.109 95.2422 99.855 95.2422 99.5939V98.8108L95.3303 98.7227L100.012 98.7396H100.08V98.8108C100.08 99.7431 100.056 100.506 100.043 101.021C100.043 101.258 100.043 101.455 100.026 101.614C100.024 101.681 100.015 101.747 99.9985 101.811Z"
        fill="#263238"
      />
      <path
        d="M106.178 101.811C106.172 101.737 106.172 101.662 106.178 101.587C106.178 101.425 106.178 101.221 106.161 100.977C106.161 100.431 106.137 99.6956 106.124 98.8108L106.191 98.8786L101.513 98.8989C101.493 98.8989 101.679 98.7294 101.601 98.8108V99.5939C101.601 99.855 101.601 100.109 101.601 100.36C101.601 100.862 101.601 101.347 101.601 101.808L101.52 101.726C102.876 101.726 104.035 101.75 104.866 101.767L105.839 101.791C105.959 101.79 106.079 101.795 106.198 101.808C106.086 101.825 105.973 101.832 105.859 101.828L104.903 101.848C104.079 101.865 102.9 101.882 101.513 101.889H101.432V101.808C101.432 101.347 101.432 100.862 101.432 100.36V98.8108L101.52 98.7227L106.198 98.7396H106.269V98.8108C106.269 99.7431 106.246 100.506 106.232 101.021C106.232 101.258 106.232 101.455 106.215 101.614C106.211 101.681 106.199 101.747 106.178 101.811Z"
        fill="#263238"
      />
      <path
        d="M53.2685 95.7913C53.2685 95.8421 50.3631 95.8794 46.7797 95.8794C43.1964 95.8794 40.291 95.8421 40.291 95.7913C40.291 95.7404 43.1964 95.7031 46.7797 95.7031C50.3631 95.7031 53.2685 95.7438 53.2685 95.7913Z"
        fill="#263238"
      />
      <path
        d="M105.355 90.8971C105.355 90.8971 105.355 90.8259 105.355 90.6971C105.355 90.5682 105.355 90.3581 105.355 90.1174C105.355 89.6054 105.355 88.8596 105.334 87.8934L105.372 87.9307C100.486 87.9612 89.7296 87.9816 77.2573 87.9816L77.3454 87.8934V90.8869L77.2573 90.7988L97.2116 90.8361L103.182 90.8632H104.805H105.229H105.375H105.239H104.826H103.226L97.276 90.8903L77.2742 90.9276H77.1895V90.8394C77.1895 90.3614 77.1895 89.8665 77.1895 89.3647V87.846L77.281 87.7578C89.7533 87.7578 100.51 87.7782 105.395 87.8087H105.433V87.846C105.433 88.8223 105.416 89.5749 105.412 90.0902C105.412 90.3377 105.412 90.5276 105.412 90.6632C105.412 90.7988 105.355 90.8971 105.355 90.8971Z"
        fill="#263238"
      />
      <path
        d="M90.8017 84.8772C90.8017 84.9247 87.8963 84.9653 84.3129 84.9653C80.7296 84.9653 77.8242 84.9247 77.8242 84.8772C77.8242 84.8297 80.7296 84.7891 84.3129 84.7891C87.8963 84.7891 90.8017 84.8263 90.8017 84.8772Z"
        fill="#263238"
      />
      <path
        d="M84.6529 129.49H59.5659C58.4279 129.488 57.3371 129.034 56.533 128.229C55.729 127.424 55.2773 126.332 55.2773 125.194C55.2782 124.057 55.7298 122.967 56.533 122.163C57.3362 121.358 58.4256 120.905 59.5625 120.902H84.6495C85.7887 120.902 86.8812 121.355 87.6867 122.16C88.4922 122.966 88.9448 124.058 88.9448 125.198C88.9439 126.336 88.4914 127.427 87.6867 128.231C86.882 129.036 85.7909 129.489 84.6529 129.49Z"
        fill="#EE7B7F"
      />
      <path
        d="M51.4843 46.2305H50.6367V50.0274H51.4843V46.2305Z"
        fill="white"
      />
      <path
        d="M49.1621 47.7032V48.5508H52.9591V47.7032H49.1621Z"
        fill="white"
      />
      <path
        d="M54.215 48.1402C54.215 48.1402 54.215 48.1233 54.215 48.0894V47.9368V47.8284C54.211 47.7839 54.2043 47.7397 54.1947 47.6961C54.1832 47.5815 54.1617 47.468 54.1303 47.3571C53.9784 46.7769 53.6654 46.2515 53.2275 45.8418C52.7895 45.432 52.2445 45.1546 51.6555 45.0417C50.9532 44.9173 50.2295 45.0294 49.5977 45.3603C49.464 45.4332 49.336 45.5159 49.2146 45.6078C49.0949 45.7006 48.9816 45.8014 48.8756 45.9095C48.7655 46.0241 48.6625 46.1453 48.5671 46.2723C48.373 46.5349 48.2219 46.8267 48.1196 47.1368C47.907 47.7784 47.907 48.4715 48.1196 49.1132C48.2205 49.4239 48.3717 49.7159 48.5671 49.9777C48.6617 50.1054 48.7647 50.2266 48.8756 50.3404C48.9808 50.4494 49.0941 50.5503 49.2146 50.6422C49.336 50.734 49.464 50.8168 49.5977 50.8896C49.7293 50.9606 49.8665 51.0207 50.0079 51.0693C50.6935 51.3197 51.445 51.3233 52.133 51.0795C52.821 50.8357 53.4026 50.3598 53.7777 49.7336C53.9311 49.4726 54.0453 49.1905 54.1167 48.8962C54.1477 48.7853 54.1693 48.6719 54.1811 48.5572C54.1907 48.5136 54.1975 48.4695 54.2015 48.425V48.3131V48.164C54.2015 48.1301 54.2015 48.1131 54.2015 48.1131V48.164C54.2015 48.2013 54.2015 48.2521 54.2015 48.3131V48.425C54.2015 48.4657 54.2015 48.5098 54.1845 48.5606C54.1721 48.6749 54.1517 48.7882 54.1235 48.8996C54.0539 49.1987 53.9396 49.4856 53.7845 49.7506C53.5554 50.1401 53.2457 50.4762 52.8762 50.7364C52.5067 50.9965 52.0859 51.1748 51.6419 51.2592C51.0752 51.3667 50.49 51.3211 49.9468 51.127C49.8046 51.0737 49.6653 51.0126 49.5299 50.9439C49.3941 50.8679 49.2627 50.7841 49.1366 50.693C49.0103 50.5967 48.8892 50.4937 48.7739 50.3845C48.6617 50.2685 48.5575 50.145 48.462 50.015C48.2613 49.7488 48.1066 49.4509 48.0043 49.1336C47.7798 48.4741 47.7798 47.7589 48.0043 47.0995C48.1066 46.7822 48.2613 46.4842 48.462 46.218C48.5577 46.0881 48.6619 45.9647 48.7739 45.8485C48.8875 45.7374 49.0087 45.6343 49.1366 45.54C49.3827 45.3545 49.656 45.2081 49.9468 45.1061C50.4489 44.9228 50.9892 44.8696 51.5173 44.9514C52.0455 45.0332 52.5444 45.2473 52.9674 45.5739C53.2917 45.8217 53.5646 46.1303 53.7709 46.4825C53.9261 46.7474 54.0404 47.0343 54.1099 47.3334C54.1381 47.4448 54.1585 47.5581 54.1709 47.6724C54.1709 47.7199 54.1709 47.7639 54.1879 47.8046V47.9165C54.1879 47.9809 54.1879 48.0318 54.1879 48.0691L54.215 48.1402Z"
        fill="white"
      />
      <path
        d="M68.7627 125.914H67.6101L67.3897 126.446H66.8066L67.9118 123.961H68.4814L69.5899 126.446H68.9865L68.7627 125.914ZM68.5831 125.476L68.1898 124.527L67.7966 125.476H68.5831Z"
        fill="white"
      />
      <path
        d="M69.6523 125.203C69.6627 124.932 69.754 124.67 69.9146 124.452C70.0751 124.233 70.2975 124.068 70.5529 123.977C70.8084 123.886 71.0853 123.874 71.3478 123.942C71.6103 124.01 71.8463 124.155 72.0254 124.359L71.6559 124.698C71.5805 124.605 71.4853 124.531 71.3773 124.481C71.2693 124.43 71.1513 124.405 71.0321 124.406C70.8303 124.419 70.641 124.509 70.5027 124.656C70.3644 124.804 70.2874 124.999 70.2874 125.201C70.2874 125.403 70.3644 125.598 70.5027 125.746C70.641 125.893 70.8303 125.983 71.0321 125.996C71.1517 125.998 71.27 125.972 71.3781 125.921C71.4862 125.87 71.5812 125.795 71.6559 125.701L72.0254 126.04C71.8463 126.244 71.6103 126.389 71.3478 126.457C71.0853 126.525 70.8084 126.513 70.5529 126.422C70.2975 126.331 70.0751 126.166 69.9146 125.947C69.754 125.729 69.6627 125.467 69.6523 125.196V125.203Z"
        fill="white"
      />
      <path
        d="M72.1992 125.202C72.1984 125.027 72.2336 124.854 72.3024 124.693C72.3712 124.532 72.4724 124.387 72.5995 124.267C72.7266 124.147 72.877 124.054 73.0414 123.995C73.2058 123.935 73.3807 123.91 73.5553 123.92C73.7466 123.914 73.937 123.949 74.1129 124.025C74.2888 124.1 74.4457 124.214 74.5723 124.358L74.2028 124.697C74.1274 124.604 74.0322 124.53 73.9242 124.48C73.8162 124.429 73.6982 124.404 73.579 124.405C73.4702 124.398 73.3612 124.413 73.2586 124.45C73.1559 124.487 73.062 124.544 72.9824 124.619C72.9029 124.693 72.8395 124.783 72.7961 124.883C72.7528 124.983 72.7304 125.091 72.7304 125.2C72.7304 125.309 72.7528 125.417 72.7961 125.517C72.8395 125.617 72.9029 125.707 72.9824 125.782C73.062 125.856 73.1559 125.913 73.2586 125.95C73.3612 125.987 73.4702 126.002 73.579 125.995C73.6986 125.997 73.8169 125.971 73.925 125.92C74.0331 125.869 74.1281 125.794 74.2028 125.7L74.5723 126.039C74.3932 126.243 74.1571 126.388 73.8947 126.456C73.6322 126.524 73.3553 126.512 73.0998 126.421C72.8443 126.33 72.622 126.165 72.4614 125.946C72.3009 125.728 72.2096 125.466 72.1992 125.195V125.202Z"
        fill="white"
      />
      <path
        d="M76.8786 125.987V126.448H74.9395V123.953H76.8176V124.414H75.5124V124.97H76.665V125.418H75.5124V125.987H76.8786Z"
        fill="white"
      />
      <path
        d="M79.4926 124.865C79.4926 125.418 79.079 125.763 78.4145 125.763H77.9128V126.441H77.3398V123.953H78.4145C79.079 123.953 79.4926 124.309 79.4926 124.865ZM78.9095 124.865C78.9095 124.59 78.7332 124.431 78.3806 124.431H77.9128V125.309H78.3806C78.7332 125.309 78.9095 125.136 78.9095 124.865Z"
        fill="white"
      />
      <path
        d="M80.3441 124.431H79.5508V123.953H81.7205V124.421H80.9272V126.438H80.3508L80.3441 124.431Z"
        fill="white"
      />
      <path
        d="M61.1191 125.255L61.5463 124.828L62.3701 125.56L63.9872 124.004L64.3838 124.431L62.3091 126.384L61.1191 125.255Z"
        fill="white"
      />
      <path
        d="M149.961 149.615C149.961 149.663 116.823 149.704 75.9548 149.704C35.0868 149.704 1.94141 149.663 1.94141 149.615C1.94141 149.568 35.0732 149.527 75.9548 149.527C116.836 149.527 149.961 149.565 149.961 149.615Z"
        fill="#263238"
      />
      <path
        d="M135.715 141.041L135.376 146.696C135.376 146.696 141.007 148.951 141.044 150.001L130.047 149.717L130.23 140.902L135.715 141.041Z"
        fill="#FF725E"
      />
      <g opacity="0.6">
        <path
          d="M131.978 146.133C131.872 146.163 131.78 146.231 131.721 146.324C131.662 146.417 131.639 146.529 131.656 146.638C131.678 146.748 131.742 146.845 131.835 146.908C131.927 146.971 132.041 146.996 132.151 146.977C132.264 146.946 132.361 146.874 132.424 146.775C132.487 146.676 132.51 146.557 132.49 146.441C132.452 146.332 132.373 146.242 132.27 146.189C132.167 146.137 132.048 146.126 131.937 146.16"
          fill="white"
        />
      </g>
      <g opacity="0.6">
        <path
          d="M130.049 149.716L130.103 148.828L140.612 149.428C140.612 149.428 141.097 149.655 141.033 149.998L130.049 149.716Z"
          fill="white"
        />
      </g>
      <path
        d="M135.547 146.647C135.547 146.701 135.276 146.718 135.005 146.884C134.733 147.05 134.591 147.27 134.543 147.25C134.496 147.23 134.581 146.911 134.91 146.724C135.238 146.538 135.56 146.599 135.547 146.647Z"
        fill="#263238"
      />
      <path
        d="M136.689 147.152C136.689 147.206 136.466 147.291 136.279 147.515C136.093 147.739 136.032 147.976 135.977 147.973C135.923 147.969 135.899 147.664 136.137 147.389C136.374 147.115 136.689 147.101 136.689 147.152Z"
        fill="#263238"
      />
      <path
        d="M137.315 148.547C137.264 148.547 137.196 148.29 137.342 147.998C137.488 147.707 137.742 147.598 137.762 147.642C137.783 147.686 137.627 147.842 137.511 148.08C137.396 148.317 137.362 148.544 137.315 148.547Z"
        fill="#263238"
      />
      <path
        d="M135.486 145.348C135.463 145.399 135.219 145.318 134.913 145.331C134.608 145.345 134.371 145.433 134.344 145.386C134.317 145.338 134.551 145.155 134.91 145.145C135.269 145.135 135.514 145.311 135.486 145.348Z"
        fill="#263238"
      />
      <g opacity="0.3">
        <path
          d="M135.643 142.159L135.582 143.169L130.182 142.983L130.202 141.816L135.643 142.159Z"
          fill="black"
        />
      </g>
      <path
        d="M135.532 144.204C135.351 144.25 135.161 144.244 134.983 144.187C134.785 144.149 134.594 144.085 134.413 143.997C134.305 143.947 134.2 143.888 134.101 143.821C134.04 143.786 133.985 143.742 133.939 143.689C133.904 143.649 133.883 143.6 133.879 143.548C133.876 143.496 133.889 143.444 133.918 143.4C133.947 143.364 133.984 143.335 134.025 143.315C134.067 143.295 134.113 143.285 134.159 143.285C134.228 143.286 134.297 143.297 134.362 143.319C134.479 143.35 134.593 143.392 134.701 143.444C134.885 143.531 135.054 143.646 135.203 143.784C135.451 144.028 135.542 144.228 135.515 144.238C135.488 144.248 135.369 144.085 135.115 143.899C134.967 143.78 134.804 143.682 134.63 143.607C134.529 143.564 134.425 143.528 134.318 143.502C134.203 143.468 134.105 143.465 134.071 143.502C134.037 143.539 134.071 143.529 134.071 143.567C134.108 143.603 134.149 143.635 134.193 143.661C134.284 143.726 134.379 143.785 134.478 143.838C134.647 143.926 134.824 143.998 135.006 144.055C135.185 144.093 135.36 144.142 135.532 144.204Z"
        fill="#263238"
      />
      <path
        d="M135.462 144.293C135.365 144.132 135.319 143.945 135.33 143.758C135.327 143.554 135.358 143.351 135.421 143.158C135.458 143.04 135.508 142.926 135.57 142.819C135.601 142.746 135.651 142.683 135.714 142.636C135.777 142.589 135.852 142.56 135.93 142.551C135.979 142.552 136.026 142.568 136.066 142.597C136.106 142.625 136.136 142.664 136.154 142.71C136.18 142.775 136.197 142.844 136.204 142.914C136.223 143.036 136.223 143.161 136.204 143.283C136.168 143.488 136.082 143.682 135.953 143.846C135.733 144.124 135.506 144.185 135.503 144.164C135.499 144.144 135.676 144.039 135.842 143.768C135.941 143.613 136.006 143.438 136.031 143.256C136.047 143.15 136.047 143.043 136.031 142.937C136.031 142.822 135.977 142.727 135.933 142.737C135.889 142.747 135.781 142.819 135.733 142.914C135.678 143.007 135.63 143.104 135.591 143.205C135.525 143.382 135.484 143.566 135.469 143.754C135.442 144.073 135.492 144.273 135.462 144.293Z"
        fill="#263238"
      />
      <path
        d="M137.992 106.594L129.256 109.401C129.361 109.479 130.876 119.297 130.876 119.297L129.615 145.36L136.548 145.445L139.65 118.107L137.992 106.594Z"
        fill="#263238"
      />
      <path
        d="M125.79 141.02L125.614 146.681C125.614 146.681 131.309 148.763 131.377 149.814L120.383 149.864L120.295 141.03L125.79 141.02Z"
        fill="#FF725E"
      />
      <g opacity="0.6">
        <path
          d="M122.22 146.225C122.115 146.258 122.026 146.328 121.969 146.423C121.912 146.517 121.892 146.629 121.912 146.737C121.941 146.844 122.009 146.936 122.104 146.994C122.199 147.052 122.312 147.072 122.42 147.049C122.532 147.014 122.626 146.939 122.686 146.838C122.745 146.737 122.765 146.618 122.742 146.503C122.701 146.395 122.619 146.307 122.514 146.257C122.409 146.208 122.289 146.201 122.18 146.239"
          fill="white"
        />
      </g>
      <g opacity="0.6">
        <path
          d="M120.396 149.865L120.424 148.977L130.953 149.258C130.953 149.258 131.442 149.468 131.391 149.814L120.396 149.865Z"
          fill="white"
        />
      </g>
      <path
        d="M125.79 146.628C125.79 146.679 125.518 146.706 125.257 146.879C124.996 147.052 124.854 147.279 124.803 147.259C124.752 147.238 124.834 146.92 125.142 146.723C125.451 146.526 125.813 146.577 125.79 146.628Z"
        fill="#263238"
      />
      <path
        d="M126.958 147.099C126.958 147.15 126.738 147.241 126.558 147.468C126.379 147.696 126.324 147.94 126.27 147.936C126.216 147.933 126.182 147.631 126.413 147.353C126.643 147.075 126.955 147.048 126.958 147.099Z"
        fill="#263238"
      />
      <path
        d="M127.621 148.475C127.573 148.475 127.495 148.217 127.621 147.922C127.746 147.627 128.01 147.512 128.031 147.556C128.051 147.6 127.902 147.756 127.793 148C127.685 148.244 127.675 148.468 127.621 148.475Z"
        fill="#263238"
      />
      <path
        d="M125.698 145.333C125.674 145.38 125.427 145.309 125.125 145.333C124.823 145.356 124.586 145.451 124.555 145.407C124.525 145.363 124.755 145.167 125.111 145.146C125.467 145.126 125.722 145.289 125.698 145.333Z"
        fill="#263238"
      />
      <g opacity="0.3">
        <path
          d="M125.762 142.145L125.732 143.151L120.328 143.131L120.314 141.965L125.762 142.145Z"
          fill="black"
        />
      </g>
      <path
        d="M125.713 144.187C125.534 144.238 125.344 144.238 125.164 144.187C124.965 144.153 124.77 144.095 124.585 144.014C124.476 143.964 124.371 143.909 124.269 143.848C124.208 143.814 124.152 143.77 124.103 143.719C124.067 143.681 124.045 143.633 124.04 143.581C124.035 143.529 124.048 143.478 124.076 143.434C124.103 143.397 124.138 143.366 124.178 143.344C124.219 143.323 124.264 143.31 124.31 143.309C124.38 143.308 124.449 143.317 124.517 143.336C124.633 143.365 124.746 143.405 124.856 143.454C125.043 143.539 125.216 143.654 125.368 143.793C125.622 144.027 125.707 144.227 125.707 144.238C125.707 144.248 125.558 144.088 125.296 143.899C125.145 143.787 124.979 143.695 124.805 143.624C124.701 143.584 124.595 143.552 124.486 143.529C124.371 143.498 124.273 143.498 124.242 143.549C124.212 143.6 124.242 143.58 124.242 143.614C124.278 143.651 124.319 143.682 124.364 143.705C124.457 143.768 124.555 143.823 124.656 143.871C124.826 143.956 125.004 144.023 125.188 144.071C125.365 144.1 125.541 144.138 125.713 144.187Z"
        fill="#263238"
      />
      <path
        d="M125.642 144.264C125.539 144.108 125.488 143.923 125.497 143.736C125.486 143.532 125.51 143.328 125.568 143.132C125.602 143.015 125.649 142.901 125.707 142.793C125.736 142.722 125.782 142.659 125.841 142.61C125.9 142.561 125.97 142.527 126.046 142.512C126.095 142.513 126.142 142.528 126.183 142.555C126.224 142.582 126.256 142.62 126.276 142.664C126.305 142.729 126.325 142.797 126.334 142.868C126.353 142.99 126.353 143.115 126.334 143.237C126.305 143.444 126.224 143.639 126.1 143.807C125.886 144.095 125.663 144.146 125.659 144.146C125.782 144.02 125.896 143.885 125.998 143.742C126.093 143.585 126.151 143.407 126.168 143.224C126.185 143.118 126.185 143.011 126.168 142.905C126.151 142.79 126.107 142.695 126.063 142.705C126.019 142.715 125.914 142.793 125.869 142.888C125.817 142.984 125.773 143.083 125.737 143.186C125.675 143.363 125.64 143.548 125.632 143.736C125.615 144.058 125.673 144.258 125.642 144.264Z"
        fill="#263238"
      />
      <path
        d="M118.768 107.984C118.768 109.957 119.547 146.544 119.547 146.544L126.128 145.957L127.484 123.474L128.192 121.514L129.016 109.401L118.768 107.984Z"
        fill="#263238"
      />
      <path
        d="M132.709 60.5547C132.798 60.6733 137.937 68.83 137.937 68.83L143.717 62.782L146.673 67.0502C146.673 67.0502 140.493 75.773 139.585 76.0883C138.676 76.4036 135.754 75.9086 135.754 75.9086L131.686 70.5217L132.709 60.5547Z"
        fill="#EBEBEB"
      />
      <path
        d="M137.994 70.694C137.894 70.3917 137.857 70.0721 137.886 69.755C137.853 69.4394 137.875 69.1207 137.95 68.8125C138.05 69.1148 138.088 69.4342 138.062 69.7516C138.092 70.0673 138.069 70.3858 137.994 70.694Z"
        fill="#263238"
      />
      <path
        d="M119.64 54.4531L118.752 56.6465C118.752 56.6465 114.192 60.4943 113.779 61.0876C113.365 61.6809 117.033 80.8623 117.033 80.8623C117.033 80.8623 116.796 109.044 117.033 109.044C117.27 109.044 120.423 110.062 126.865 109.577C133.306 109.092 134.503 108.451 134.503 108.451L132.492 83.5269L129.411 66.7695L124.125 59.9621L119.64 54.4531Z"
        fill="#EBEBEB"
      />
      <path
        d="M130.031 63.969C130.031 63.969 129.353 60.7178 129.41 60.6161C129.468 60.5144 127.891 56.6734 127.891 56.6734C127.891 56.6734 127.363 54.9139 127.363 54.9207C127.363 54.9275 120.179 53.707 120.179 53.707L119.84 54.7919L129.42 66.898L130.403 72.0951L131.421 70.678L130.031 63.969Z"
        fill="#FF725E"
      />
      <path
        d="M136.44 88.4414L134.881 69.6906L133.603 62.3713C133.418 61.3282 133.028 60.3323 132.453 59.4422L128.724 57.2827C128.343 56.8267 127.986 56.3502 127.656 55.8555L129.423 60.3677L130.043 63.9715L130.426 72.24L132.46 83.3393L134.453 107.748L138.786 106.901L136.44 88.4414Z"
        fill="#EBEBEB"
      />
      <path
        d="M130.57 66.5954L131.408 60.4423L128.712 57.2827L127.645 55.8555L129.302 60.6186L130.57 66.5954Z"
        fill="#EBEBEB"
      />
      <path
        d="M126.311 78.0743L132.823 68.8125L144.156 72.2399L135.583 83.3324L124.938 79.8711L126.311 78.0743Z"
        fill="#FF725E"
      />
      <path
        d="M140.848 71.8165C140.848 71.8722 140.831 71.9266 140.8 71.9729C140.77 72.0191 140.726 72.0552 140.674 72.0765C140.623 72.0978 140.566 72.1034 140.512 72.0925C140.457 72.0817 140.407 72.0549 140.368 72.0155C140.328 71.9762 140.301 71.926 140.291 71.8714C140.28 71.8168 140.285 71.7603 140.307 71.7089C140.328 71.6574 140.364 71.6135 140.41 71.5826C140.456 71.5517 140.511 71.5352 140.567 71.5352C140.641 71.5352 140.713 71.5648 140.766 71.6176C140.818 71.6703 140.848 71.7419 140.848 71.8165Z"
        fill="#263238"
      />
      <path
        d="M136.317 70.5509C136.317 70.6067 136.3 70.6613 136.269 70.7076C136.238 70.7539 136.194 70.79 136.142 70.8112C136.091 70.8324 136.034 70.8378 135.979 70.8266C135.924 70.8155 135.874 70.7884 135.835 70.7487C135.796 70.709 135.769 70.6585 135.759 70.6037C135.748 70.5489 135.754 70.4922 135.776 70.4409C135.798 70.3895 135.835 70.3458 135.881 70.3153C135.928 70.2848 135.983 70.2689 136.039 70.2696C136.113 70.2704 136.183 70.3005 136.235 70.3532C136.287 70.4058 136.317 70.4769 136.317 70.5509Z"
        fill="#263238"
      />
      <path
        d="M131.003 62.8594C130.986 62.8594 131.125 63.4628 131.319 64.2154C131.421 64.6627 131.548 65.104 131.698 65.5376C131.715 65.5376 131.573 64.9342 131.383 64.1985C131.193 63.4628 131.02 62.856 131.003 62.8594Z"
        fill="#263238"
      />
      <path
        d="M133.165 67.0128C133.205 67.2782 133.17 67.5495 133.064 67.7959C133.01 67.9217 132.929 68.0346 132.828 68.127C132.727 68.2194 132.607 68.2891 132.477 68.3315C132.301 68.3877 132.112 68.3969 131.931 68.3583C131.75 68.3196 131.582 68.2345 131.443 68.1112C130.978 67.5697 130.746 66.8669 130.796 66.1551C130.812 65.9332 130.891 65.7204 131.022 65.5406C131.153 65.3609 131.332 65.2215 131.538 65.138C131.772 65.0755 132.02 65.0938 132.242 65.1897C132.464 65.2857 132.647 65.4538 132.762 65.6669"
        fill="#E0E0E0"
      />
      <path
        d="M132.763 65.6757C132.719 65.5845 132.661 65.5009 132.59 65.4282C132.508 65.343 132.415 65.27 132.312 65.2113C132.174 65.1338 132.02 65.0897 131.861 65.0825C131.769 65.0799 131.677 65.089 131.587 65.1096C131.487 65.1308 131.392 65.1686 131.305 65.2214C131.123 65.3482 130.978 65.523 130.888 65.7266C130.787 65.9484 130.739 66.1913 130.749 66.4351C130.761 66.6934 130.808 66.9489 130.888 67.1945C130.99 67.5834 131.199 67.9359 131.492 68.2115C131.637 68.3247 131.811 68.3961 131.994 68.4183C132.16 68.4384 132.328 68.4233 132.489 68.3743C132.607 68.3444 132.715 68.2849 132.804 68.2014C132.885 68.1301 132.956 68.048 133.014 67.9573C133.101 67.808 133.158 67.6434 133.184 67.4725C133.199 67.3601 133.199 67.2459 133.184 67.1335C133.187 67.1053 133.187 67.0769 133.184 67.0487C133.184 67.0386 133.184 67.0284 133.184 67.0182C133.195 67.1642 133.189 67.3109 133.167 67.4556C133.138 67.6213 133.078 67.7801 132.99 67.9234C132.933 68.0097 132.863 68.0873 132.784 68.1539C132.7 68.2297 132.598 68.2833 132.489 68.3099C132.331 68.3586 132.164 68.3693 132.001 68.341C131.839 68.3128 131.685 68.2463 131.553 68.1471C131.275 67.8817 131.074 67.5453 130.973 67.1742C130.797 66.716 130.797 66.2085 130.973 65.7503C131.057 65.5573 131.192 65.3908 131.363 65.2689C131.443 65.218 131.531 65.1813 131.624 65.1604C131.859 65.0996 132.109 65.1299 132.322 65.2452C132.425 65.2981 132.519 65.3668 132.6 65.4486C132.66 65.5202 132.715 65.5962 132.763 65.6757Z"
        fill="#263238"
      />
      <path
        d="M133.468 66.9626C133.508 67.2269 133.473 67.4972 133.366 67.7424C133.313 67.8687 133.232 67.9822 133.131 68.0751C133.03 68.1681 132.91 68.2384 132.78 68.2814C132.603 68.3376 132.415 68.3468 132.234 68.3081C132.052 68.2695 131.884 68.1843 131.746 68.061C131.281 67.5196 131.049 66.8168 131.098 66.1049C131.114 65.8832 131.192 65.6703 131.322 65.4905C131.453 65.3107 131.631 65.1712 131.837 65.0879C132.071 65.0249 132.32 65.0425 132.542 65.1378C132.765 65.2331 132.949 65.4007 133.065 65.6134"
        fill="#E0E0E0"
      />
      <path
        d="M133.064 65.6221C133.02 65.5317 132.962 65.4492 132.891 65.378C132.81 65.2918 132.716 65.2186 132.613 65.161C132.474 65.083 132.318 65.0389 132.159 65.0322C132.067 65.0281 131.974 65.0372 131.884 65.0593C131.786 65.0797 131.692 65.1176 131.606 65.1712C131.422 65.2963 131.277 65.4716 131.189 65.6763C131.087 65.8969 131.04 66.1387 131.05 66.3815C131.062 66.6408 131.109 66.8974 131.189 67.1443C131.292 67.5328 131.501 67.885 131.793 68.1613C131.938 68.2744 132.112 68.3459 132.294 68.3681C132.461 68.388 132.629 68.373 132.789 68.324C132.909 68.295 133.019 68.2354 133.108 68.1511C133.189 68.0799 133.26 67.9977 133.318 67.907C133.405 67.7577 133.463 67.5932 133.488 67.4222C133.504 67.3098 133.504 67.1957 133.488 67.0832C133.489 67.055 133.489 67.0267 133.488 66.9985C133.489 66.9883 133.489 66.9781 133.488 66.968C133.499 67.114 133.492 67.2609 133.467 67.4053C133.439 67.5711 133.379 67.7299 133.291 67.8731C133.236 67.9602 133.167 68.038 133.088 68.1037C133.004 68.1794 132.903 68.233 132.793 68.2596C132.635 68.3089 132.468 68.3201 132.306 68.2924C132.143 68.2647 131.989 68.1989 131.857 68.1003C131.579 67.8349 131.378 67.4984 131.277 67.1273C131.101 66.6691 131.101 66.1616 131.277 65.7034C131.36 65.5099 131.495 65.3432 131.667 65.222C131.747 65.1706 131.836 65.1338 131.928 65.1136C132.014 65.0919 132.101 65.0817 132.189 65.0831C132.341 65.0856 132.49 65.1251 132.623 65.1983C132.727 65.2504 132.82 65.3191 132.901 65.4017C132.96 65.472 133.014 65.5456 133.064 65.6221Z"
        fill="#263238"
      />
      <path
        d="M132.543 65.6077C132.678 65.5557 132.826 65.5484 132.966 65.5868C133.106 65.6252 133.229 65.7074 133.319 65.8213C133.492 66.0576 133.568 66.351 133.532 66.6417C133.527 66.7906 133.489 66.9364 133.421 67.0689C133.385 67.1354 133.337 67.1943 133.279 67.242C133.221 67.2897 133.154 67.3254 133.082 67.3469C132.975 67.3687 132.865 67.3647 132.761 67.3352C132.656 67.3057 132.561 67.2516 132.481 67.1773C132.348 67.0642 132.24 66.9235 132.166 66.765C132.092 66.6064 132.052 66.4337 132.051 66.2586C132.041 66.1406 132.068 66.0223 132.129 65.9206C132.19 65.819 132.281 65.7391 132.39 65.6925"
        fill="#E0E0E0"
      />
      <path
        d="M132.388 65.6936C132.372 65.6905 132.354 65.6905 132.338 65.6936C132.29 65.71 132.247 65.738 132.212 65.775C132.15 65.8435 132.103 65.9239 132.073 66.0114C132.043 66.0988 132.032 66.1914 132.039 66.2835C132.043 66.5577 132.141 66.8222 132.317 67.0327C132.417 67.1608 132.546 67.2631 132.694 67.331C132.774 67.3718 132.863 67.3931 132.953 67.3931C133.043 67.3931 133.132 67.3718 133.212 67.331C133.363 67.2341 133.472 67.0841 133.517 66.9107C133.559 66.7562 133.575 66.5958 133.565 66.436C133.563 66.2991 133.533 66.164 133.478 66.0388C133.423 65.9135 133.343 65.8007 133.243 65.7072C133.099 65.5867 132.914 65.5271 132.727 65.541C132.679 65.5475 132.631 65.56 132.585 65.5783C132.555 65.5953 132.541 65.6021 132.541 65.6055C132.603 65.5866 132.666 65.5741 132.731 65.5682C132.907 65.5626 133.078 65.6258 133.209 65.7445C133.302 65.8347 133.376 65.9427 133.427 66.0621C133.477 66.1814 133.504 66.3098 133.504 66.4394C133.51 66.5926 133.493 66.7458 133.453 66.8937C133.409 67.0489 133.312 67.1835 133.178 67.2734C133.107 67.3084 133.029 67.3266 132.95 67.3266C132.87 67.3266 132.792 67.3084 132.721 67.2734C132.582 67.2093 132.46 67.1142 132.365 66.9954C132.195 66.7959 132.097 66.5452 132.087 66.2835C132.07 66.108 132.122 65.9329 132.232 65.7953C132.278 65.7528 132.331 65.7184 132.388 65.6936Z"
        fill="#263238"
      />
      <path
        d="M132.446 67.8429C132.446 67.8429 132.446 67.7988 132.49 67.7378C132.528 67.6568 132.59 67.5886 132.666 67.5412C132.747 67.4965 132.832 67.4601 132.92 67.4327C132.988 67.409 133.032 67.392 133.032 67.3819C132.992 67.3727 132.95 67.3727 132.91 67.3819C132.812 67.3974 132.718 67.4307 132.632 67.4802C132.542 67.534 132.475 67.6182 132.442 67.7175C132.422 67.7955 132.435 67.8463 132.446 67.8429Z"
        fill="#263238"
      />
      <path
        d="M131.762 65.6431C131.762 65.6431 131.792 65.6837 131.863 65.7142C131.957 65.7529 132.059 65.769 132.16 65.7614C132.261 65.7537 132.36 65.7225 132.447 65.6702C132.511 65.6261 132.541 65.5888 132.535 65.582C132.414 65.6357 132.286 65.6733 132.155 65.6939C132.022 65.6908 131.891 65.6738 131.762 65.6431Z"
        fill="#263238"
      />
      <path
        d="M126.524 70.1981C126.185 70.2964 125.799 70.0456 125.467 70.1744C125.407 70.1996 125.352 70.2363 125.306 70.2825C125.26 70.3288 125.224 70.3836 125.199 70.4439C125.174 70.5042 125.162 70.5687 125.162 70.6339C125.162 70.699 125.175 70.7635 125.201 70.8236C125.226 70.8837 125.262 70.9383 125.309 70.9842C125.355 71.0301 125.41 71.0664 125.47 71.0911C125.53 71.1158 125.595 71.1284 125.66 71.1282C125.725 71.128 125.79 71.1149 125.85 71.0897C126.189 70.9338 126.267 70.4456 126.592 70.2524"
        fill="#263238"
      />
      <path
        d="M123.754 71.4237C123.861 71.3889 123.964 71.3458 124.063 71.2949L124.009 71.183C123.842 71.2814 123.657 71.3443 123.465 71.3676C123.273 71.391 123.079 71.3743 122.893 71.3186C122.643 71.2112 122.416 71.0552 122.227 70.8597C122.037 70.6642 121.888 70.433 121.788 70.1795V70.1558C121.666 69.9117 119.988 64.9147 119.68 63.962C119.523 63.5683 119.52 63.1303 119.671 62.7344C119.821 62.3385 120.115 62.0133 120.493 61.8229C120.541 61.7957 121.663 61.2126 122.819 62.6331C124.029 64.1214 126.145 67.0403 126.341 67.42L126.433 67.5827C126.7 68.037 127.26 68.9862 126.772 69.7998C126.226 70.7016 125.541 70.6406 125.514 70.6406V70.7626C125.544 70.7626 126.3 70.8372 126.89 69.8609C127.423 68.9896 126.836 67.9963 126.551 67.5183L126.463 67.3623C126.267 66.9962 124.141 64.0468 122.931 62.5551C121.72 61.0635 120.463 61.7076 120.449 61.7144C120.051 61.9242 119.743 62.2719 119.582 62.6925C119.422 63.1131 119.42 63.5776 119.578 63.9993C119.832 64.7824 121.561 69.9524 121.69 70.2101V70.2338C121.795 70.5025 121.953 70.7474 122.155 70.9541C122.356 71.1608 122.597 71.3251 122.863 71.4373C123.154 71.5288 123.466 71.5241 123.754 71.4237Z"
        fill="#263238"
      />
      <path
        d="M119.906 64.9351C119.891 64.7132 119.91 64.4905 119.96 64.274C120.021 64.0397 120.123 63.8181 120.262 63.6197C120.431 63.3778 120.648 63.1734 120.899 63.0196C121.366 62.7484 121.912 62.6478 122.445 62.7349C122.665 62.7752 122.88 62.8436 123.082 62.9383C123.082 62.9383 123.082 62.9383 123.042 62.9078C123.004 62.8844 122.966 62.8629 122.927 62.8434C122.776 62.7735 122.618 62.7223 122.455 62.6908C122.212 62.6423 121.962 62.6354 121.716 62.6705C121.416 62.7107 121.126 62.811 120.865 62.9654C120.605 63.1235 120.381 63.3363 120.211 63.5892C120.072 63.7953 119.972 64.0251 119.916 64.2672C119.877 64.4291 119.863 64.5961 119.875 64.7622C119.875 64.8198 119.875 64.8639 119.892 64.8944C119.909 64.9249 119.903 64.9385 119.906 64.9351Z"
        fill="#263238"
      />
      <path
        d="M118.756 56.6504L124.377 63.6138L129.411 66.7734L119.644 54.457L118.756 56.6504Z"
        fill="#EBEBEB"
      />
      <path
        d="M113.78 61.0926C113.78 61.0926 112.6 62.9504 112.424 64.8488C112.349 65.5913 114.201 79.5349 114.407 80.1621C114.614 80.7892 117.075 82.3555 117.797 82.6708C118.52 82.9861 127.466 85.9626 131.124 85.9117L131.907 79.0128L122.5 77.4432C122.5 77.4432 121.401 76.399 120.828 76.1888L120.042 68.5068L118.892 62.3401C118.892 62.3401 115.618 60.3128 113.78 61.0926Z"
        fill="#EBEBEB"
      />
      <path
        d="M134.922 69.443C134.874 69.443 134.369 67.4089 133.793 64.8866C133.216 62.3644 132.776 60.3032 132.833 60.293C132.891 60.2828 133.386 62.3271 133.965 64.846C134.545 67.3648 134.969 69.4294 134.922 69.443Z"
        fill="#263238"
      />
      <path
        d="M119.582 67.826C119.582 67.826 119.582 67.8735 119.609 67.9684C119.636 68.0633 119.653 68.2125 119.684 68.3956C119.745 68.7787 119.833 69.3313 119.945 70.0466C120.165 71.4908 120.487 73.5791 120.887 76.1793L120.85 76.1353C121.166 76.2783 121.46 76.4643 121.725 76.6879C122.003 76.9082 122.274 77.1455 122.545 77.3964L122.511 77.3794L131.922 78.9321H131.997V79.0067C131.753 81.1628 131.492 83.4885 131.217 85.9056V85.987H131.139C129.39 85.9042 127.654 85.6337 125.962 85.1801C123.793 84.6615 121.704 84.0411 119.701 83.4003C119.203 83.2376 118.708 83.0613 118.219 82.902C117.731 82.7339 117.265 82.5061 116.833 82.2239C116.402 81.9595 115.982 81.6815 115.582 81.39C115.172 81.1094 114.799 80.7791 114.47 80.4068C114.386 80.311 114.331 80.1935 114.311 80.0678C114.28 79.9424 114.26 79.8271 114.243 79.7085C114.202 79.4711 114.165 79.2372 114.128 79.0067C114.06 78.5389 113.992 78.0812 113.924 77.6269L113.558 74.9928C113.327 73.2977 113.121 71.7044 112.941 70.2466C112.761 68.7888 112.602 67.487 112.49 66.331C112.463 66.0428 112.429 65.7648 112.412 65.4936C112.378 65.2337 112.378 64.9704 112.412 64.7105C112.486 64.2614 112.592 63.8184 112.731 63.385C112.919 62.785 113.163 62.2039 113.46 61.6492C113.558 61.4594 113.646 61.3102 113.704 61.2254C113.731 61.1776 113.763 61.1322 113.799 61.0898L113.717 61.2356C113.666 61.3305 113.585 61.4729 113.49 61.6662C113.21 62.2252 112.977 62.807 112.795 63.4053C112.663 63.8371 112.562 64.2778 112.493 64.7241C112.463 64.9774 112.463 65.2335 112.493 65.4868C112.514 65.7547 112.548 66.0327 112.578 66.3208C112.704 67.4735 112.87 68.7821 113.056 70.2296C113.243 71.6772 113.463 73.2808 113.694 74.9758C113.813 75.8256 113.938 76.7037 114.067 77.61C114.134 78.0609 114.206 78.5185 114.277 78.983L114.389 79.6847C114.409 79.8 114.429 79.9187 114.456 80.0237C114.47 80.1329 114.516 80.2355 114.589 80.3187C114.909 80.6772 115.272 80.9949 115.67 81.2645C116.067 81.5561 116.484 81.8307 116.911 82.0917C117.333 82.3722 117.789 82.5999 118.267 82.7698C118.752 82.9393 119.247 83.1088 119.745 83.2647C121.745 83.9021 123.813 84.5191 125.993 85.0378C127.671 85.4861 129.391 85.7555 131.126 85.8412L131.041 85.9192C131.319 83.502 131.583 81.173 131.831 79.0203L131.899 79.1084L122.491 77.5083H122.471C122.203 77.2574 121.935 77.0201 121.66 76.8031C121.407 76.5861 121.125 76.4047 120.823 76.2641L120.789 76.2471V76.2166C120.413 73.6096 120.111 71.5179 119.901 70.0703C119.803 69.3584 119.725 68.8024 119.67 68.4193C119.647 68.2362 119.63 68.0803 119.616 67.9888C119.601 67.9354 119.59 67.881 119.582 67.826Z"
        fill="#263238"
      />
      <path
        d="M119.025 65.7528C118.858 65.4858 118.713 65.2058 118.591 64.9154C118.277 64.2317 117.884 63.5876 117.418 62.9966C116.944 62.4173 116.324 61.9749 115.621 61.7151C115.324 61.6285 115.02 61.5628 114.713 61.5185C114.795 61.4921 114.881 61.4828 114.967 61.4914C115.203 61.4931 115.437 61.5285 115.662 61.5965C116.404 61.8311 117.061 62.2791 117.55 62.8847C118.031 63.4832 118.42 64.1492 118.706 64.8611C118.843 65.1465 118.95 65.4453 119.025 65.7528Z"
        fill="#263238"
      />
      <path
        d="M117.521 77.4703C117.915 77.0033 118.421 76.6438 118.991 76.4256C119.562 76.2074 120.178 76.1376 120.783 76.2227C120.195 76.2823 119.615 76.405 119.054 76.5889C118.514 76.8292 118.001 77.1246 117.521 77.4703Z"
        fill="#263238"
      />
      <path
        d="M118.588 78.5606C118.749 78.002 119.047 77.4924 119.455 77.078C119.863 76.6636 120.368 76.3576 120.924 76.1875C120.44 76.4817 119.986 76.8223 119.568 77.2045C119.2 77.6257 118.872 78.0796 118.588 78.5606Z"
        fill="#263238"
      />
      <path
        d="M129.409 66.7726C129.272 66.7151 129.142 66.6447 129.019 66.5624L127.961 65.9284L124.33 63.6842L122.635 61.5992C121.082 59.6736 119.689 57.9311 118.723 56.7073L118.699 56.6768V56.6462L119.377 55.0495L119.567 54.6223C119.585 54.57 119.612 54.5208 119.645 54.4766C119.636 54.5315 119.62 54.585 119.598 54.6359C119.557 54.7545 119.503 54.8969 119.438 55.0732C119.282 55.48 119.072 56.0225 118.811 56.6903V56.6293C119.801 57.8328 121.211 59.5618 122.767 61.4907L124.462 63.5791L124.442 63.5621L128.042 65.8572L129.059 66.5353C129.182 66.6052 129.299 66.6846 129.409 66.7726Z"
        fill="#263238"
      />
      <path
        d="M130.231 68.0837C130.23 67.9315 130.242 67.7795 130.268 67.6294C130.309 67.3107 130.359 66.8971 130.424 66.3954C130.563 65.3512 130.763 63.8969 131.007 62.2594V62.3137C130.902 62.117 130.787 61.9069 130.668 61.6933C129.807 60.0931 129.034 58.6422 128.485 57.581L127.844 56.3267C127.759 56.1757 127.686 56.0181 127.627 55.8555C127.732 55.9923 127.824 56.1388 127.902 56.2928L128.597 57.5234C129.173 58.5642 129.953 60.0084 130.82 61.5916C130.936 61.8052 131.048 62.0153 131.159 62.212V62.2391V62.2696C130.912 63.9036 130.685 65.3546 130.515 66.3954C130.431 66.8937 130.359 67.3039 130.305 67.6226C130.298 67.7786 130.273 67.9333 130.231 68.0837Z"
        fill="#263238"
      />
      <path
        d="M119.921 62.0463L120.209 61.87L119.917 62.0463C119.436 61.2886 119.013 60.4953 118.653 59.6732L118.592 59.5274C118.253 58.6968 117.86 57.7578 118.026 56.8458V56.8289C118.11 56.4288 118.329 56.0697 118.646 55.8118L118.738 55.7305H119.009V56.2559L119.07 56.3271L119.009 56.3848V56.4085C118.854 56.559 118.749 56.7529 118.707 56.9645V56.9814C118.585 57.6934 118.931 58.5307 119.236 59.2698L119.297 59.4155C119.644 60.2009 120.052 60.9581 120.517 61.6802L119.921 62.0463Z"
        fill="#263238"
      />
      <path
        d="M132.32 82.707C132.345 82.7898 132.361 82.875 132.368 82.9613C132.392 83.1444 132.425 83.3884 132.466 83.6936C132.551 84.3309 132.663 85.253 132.805 86.4057C133.066 88.6872 133.388 91.8299 133.683 95.3353C133.978 98.8407 134.188 101.994 134.317 104.285C134.378 105.431 134.426 106.36 134.449 106.997C134.449 107.306 134.466 107.55 134.473 107.736C134.481 107.822 134.481 107.908 134.473 107.994C134.454 107.911 134.444 107.825 134.443 107.74C134.443 107.553 134.409 107.309 134.385 107.001C134.337 106.343 134.273 105.421 134.195 104.289C134.036 102 133.805 98.8373 133.517 95.3455C133.229 91.8536 132.91 88.694 132.666 86.4057C132.554 85.2768 132.466 84.3716 132.398 83.6936C132.371 83.3884 132.351 83.1444 132.334 82.9579C132.324 82.8746 132.32 82.7909 132.32 82.707Z"
        fill="#263238"
      />
      <path
        d="M127.97 90.6602C127.97 90.6602 127.895 90.7483 127.743 90.8805C127.59 91.0127 127.36 91.2026 127.065 91.4263C126.319 92.0202 125.533 92.5606 124.712 93.0434C124.255 93.3061 123.781 93.5394 123.295 93.7418C122.897 93.902 122.488 94.0335 122.071 94.135C121.789 94.206 121.502 94.2547 121.213 94.2808C121.108 94.2985 121 94.2985 120.895 94.2808C120.895 94.2503 121.342 94.1995 122.037 94.0028C122.445 93.8881 122.845 93.7466 123.234 93.5791C123.709 93.3715 124.173 93.1383 124.624 92.8807C125.58 92.3281 126.397 91.7382 126.997 91.3178C127.306 91.0765 127.631 90.8568 127.97 90.6602Z"
        fill="#263238"
      />
      <path
        d="M128.503 91.9813C128.455 92.0801 128.394 92.1714 128.32 92.2525C128.14 92.492 127.944 92.7186 127.733 92.9305C126.342 94.3589 124.539 95.3177 122.577 95.6731C122.284 95.7299 121.987 95.7662 121.689 95.7816C121.576 95.7958 121.462 95.7958 121.35 95.7816C121.35 95.7477 121.811 95.7138 122.543 95.5511C123.496 95.3379 124.415 94.9958 125.275 94.534C126.139 94.0778 126.937 93.5077 127.648 92.839C128.164 92.3271 128.466 91.9609 128.503 91.9813Z"
        fill="#263238"
      />
      <path
        d="M121.242 95.906C121.124 95.6103 121.062 95.2954 121.059 94.9771C120.999 94.6647 120.999 94.3436 121.059 94.0312C121.239 94.6388 121.304 95.2747 121.249 95.906H121.242Z"
        fill="#263238"
      />
      <path
        d="M120.429 50.9761C120.304 50.9722 120.183 50.9328 120.08 50.8626C119.976 50.7923 119.895 50.6942 119.846 50.5795C119.737 50.3396 119.67 50.0827 119.649 49.8201C119.49 48.6675 119.334 47.5114 119.174 46.3554C119.011 45.3796 118.931 44.3919 118.934 43.4026C119.013 42.4438 119.311 41.516 119.805 40.6905C120.327 39.6734 122.442 37.914 125.951 38.9073"
        fill="#263238"
      />
      <path
        d="M120.247 46.8189C120.2 46.8189 118.373 46.3036 118.464 48.175C118.556 50.0463 120.403 49.5547 120.403 49.5005C120.403 49.4463 120.247 46.8189 120.247 46.8189Z"
        fill="#FFBE9D"
      />
      <path
        d="M119.803 48.8103C119.803 48.8103 119.772 48.834 119.718 48.8611C119.643 48.8972 119.557 48.9044 119.477 48.8814C119.354 48.8105 119.251 48.7083 119.18 48.5851C119.108 48.4619 119.071 48.3221 119.071 48.1797C119.058 48.0217 119.081 47.8629 119.138 47.7152C119.152 47.6561 119.181 47.6014 119.221 47.5559C119.261 47.5103 119.312 47.4754 119.369 47.4542C119.406 47.4423 119.445 47.4436 119.481 47.4578C119.517 47.4721 119.547 47.4984 119.566 47.5322C119.593 47.583 119.583 47.6203 119.593 47.6237C119.603 47.6271 119.63 47.5898 119.593 47.5152C119.588 47.4923 119.579 47.4705 119.565 47.4513C119.552 47.432 119.535 47.4157 119.515 47.4033C119.462 47.3668 119.398 47.3522 119.335 47.3627C119.257 47.3801 119.185 47.4173 119.125 47.4707C119.066 47.5242 119.022 47.5921 118.996 47.6678C118.927 47.8327 118.898 48.0116 118.911 48.1899C118.911 48.3557 118.959 48.5182 119.05 48.6568C119.141 48.7955 119.271 48.9043 119.423 48.9696C119.474 48.9807 119.527 48.9806 119.578 48.9695C119.629 48.9584 119.676 48.9365 119.718 48.9052C119.803 48.8543 119.81 48.8136 119.803 48.8103Z"
        fill="#EB996E"
      />
      <path
        d="M129.786 50.2714C129.786 48.9594 129.542 46.2608 129.301 43.8301C129.175 42.5647 128.578 41.393 127.628 40.5481C126.677 39.7033 125.443 39.2471 124.172 39.2704H123.911C121.087 39.4873 119.697 42.0028 119.992 44.82L120.389 53.7971L127.118 57.489L127.094 53.4649C127.094 53.4649 129.803 52.9869 129.786 50.2714Z"
        fill="#FFBE9D"
      />
      <path
        d="M127.097 53.4586C125.845 53.5064 124.607 53.1794 123.541 52.5195C123.541 52.5195 124.395 54.4858 127.101 54.1943L127.097 53.4586Z"
        fill="#EB996E"
      />
      <path
        d="M128.402 45.5985C128.405 45.6993 128.368 45.7972 128.3 45.8713C128.231 45.9454 128.137 45.9899 128.036 45.9951C127.987 46.0003 127.937 45.9955 127.89 45.9811C127.843 45.9668 127.799 45.9431 127.761 45.9115C127.723 45.8799 127.692 45.841 127.669 45.7972C127.646 45.7533 127.633 45.7053 127.629 45.6561C127.626 45.5553 127.663 45.4574 127.731 45.3833C127.8 45.3092 127.894 45.2648 127.995 45.2595C128.044 45.2548 128.094 45.2599 128.141 45.2745C128.188 45.289 128.231 45.3127 128.269 45.3442C128.307 45.3758 128.338 45.4145 128.361 45.4581C128.384 45.5017 128.398 45.5495 128.402 45.5985Z"
        fill="#263238"
      />
      <path
        d="M128.78 45.0146C128.732 45.0655 128.441 44.8519 128.017 44.8621C127.593 44.8723 127.298 45.0858 127.251 45.035C127.203 44.9841 127.278 44.9197 127.407 44.8214C127.586 44.6936 127.8 44.6238 128.02 44.6214C128.237 44.6162 128.449 44.6803 128.627 44.8044C128.759 44.8994 128.803 44.9909 128.78 45.0146Z"
        fill="#263238"
      />
      <path
        d="M124.346 45.6848C124.35 45.7858 124.313 45.8842 124.245 45.9585C124.176 46.0328 124.081 46.077 123.98 46.0814C123.931 46.0865 123.881 46.0817 123.834 46.0673C123.787 46.0529 123.744 46.0292 123.706 45.9976C123.668 45.9659 123.638 45.927 123.615 45.8832C123.593 45.8393 123.58 45.7915 123.576 45.7424C123.573 45.6413 123.609 45.5429 123.678 45.4686C123.746 45.3943 123.842 45.3502 123.943 45.3457C123.991 45.3406 124.041 45.3454 124.088 45.3598C124.135 45.3742 124.179 45.3979 124.216 45.4296C124.254 45.4612 124.285 45.5001 124.307 45.544C124.329 45.5878 124.343 45.6357 124.346 45.6848Z"
        fill="#263238"
      />
      <path
        d="M124.619 44.9622C124.578 45.0165 124.259 44.8402 123.846 44.8944C123.432 44.9487 123.168 45.1927 123.103 45.1487C123.039 45.1046 123.103 45.0334 123.236 44.9181C123.398 44.7703 123.604 44.6777 123.822 44.6537C124.037 44.6246 124.256 44.665 124.446 44.769C124.585 44.8639 124.639 44.9622 124.619 44.9622Z"
        fill="#263238"
      />
      <path
        d="M126.212 48.3772C126.432 48.3033 126.659 48.2577 126.89 48.2416C126.999 48.2416 127.097 48.2077 127.114 48.1331C127.121 48.0226 127.094 47.9125 127.036 47.8179L126.697 47.0144C126.392 46.3317 126.142 45.6253 125.951 44.9023C126.307 45.5562 126.613 46.2362 126.867 46.9364C126.975 47.2212 127.08 47.489 127.182 47.7467C127.252 47.8748 127.275 48.0237 127.246 48.167C127.231 48.2046 127.208 48.2383 127.178 48.2654C127.148 48.2924 127.112 48.3121 127.073 48.323C127.015 48.3417 126.955 48.3519 126.894 48.3535C126.668 48.39 126.439 48.398 126.212 48.3772Z"
        fill="#263238"
      />
      <path
        d="M124.988 48.6914C125.056 48.6914 125.066 49.1389 125.46 49.4508C125.853 49.7627 126.334 49.7084 126.341 49.7729C126.348 49.8373 126.233 49.861 126.029 49.8712C125.764 49.8799 125.505 49.7947 125.297 49.6305C125.097 49.4721 124.966 49.2425 124.931 48.9897C124.91 48.7999 124.958 48.6914 124.988 48.6914Z"
        fill="#263238"
      />
      <path
        d="M124.643 43.1752C124.605 43.2871 124.185 43.243 123.697 43.3108C123.209 43.3786 122.815 43.5176 122.748 43.4193C122.68 43.321 122.785 43.2633 122.944 43.1481C123.153 43.008 123.393 42.9197 123.643 42.8904C123.892 42.8556 124.146 42.88 124.385 42.9616C124.568 43.0328 124.66 43.1209 124.643 43.1752Z"
        fill="#263238"
      />
      <path
        d="M128.565 43.9437C128.493 44.0386 128.205 43.9437 127.86 43.9437C127.514 43.9437 127.222 44.0183 127.151 43.9234C127.08 43.8285 127.151 43.7776 127.293 43.6861C127.461 43.5721 127.66 43.514 127.863 43.5199C128.066 43.5196 128.264 43.5824 128.429 43.6996C128.554 43.8081 128.595 43.9064 128.565 43.9437Z"
        fill="#263238"
      />
      <path
        d="M126.16 38.9723C125.2 38.5926 124.227 38.7926 123.207 38.9418C122.421 39.041 121.673 39.3385 121.034 39.8063C120.54 40.1946 120.139 40.689 119.862 41.253C119.586 41.8169 119.439 42.4359 119.433 43.0642C119.379 44.2981 119.515 45.533 119.837 46.7255L119.939 46.7018C119.847 47.0085 119.841 47.3345 119.922 47.6443C119.968 47.7971 120.072 47.9256 120.213 48.0023C120.353 48.079 120.517 48.0977 120.671 48.0545C121.01 47.9053 121.01 47.4341 121.01 47.0612C120.922 45.8061 120.922 44.5464 121.01 43.2913C121.064 42.3556 121.596 41.2064 122.329 40.7623C123.661 39.8741 125.397 40.5996 125.983 40.7013C126.888 39.9283 127.733 40.5318 128.282 41.203C128.051 40.1927 127.119 39.3486 126.16 38.9723Z"
        fill="#263238"
      />
      <path
        d="M125.952 40.6997C126.549 40.6997 127.023 40.2997 127.684 40.4082C128.084 40.4726 128.488 41.1269 128.837 41.9405C129.037 41.8197 129.197 41.6419 129.296 41.4299C129.395 41.2179 129.428 40.9812 129.392 40.7501C129.355 40.519 129.251 40.3041 129.092 40.1326C128.932 39.9612 128.726 39.8412 128.498 39.7878C128.656 39.8903 128.839 39.9498 129.027 39.9607C129.12 39.9618 129.211 39.9275 129.28 39.8646C129.349 39.8017 129.392 39.7149 129.4 39.6217C129.389 39.5528 129.364 39.487 129.326 39.4286C129.288 39.3702 129.238 39.3205 129.179 39.2826C128.578 38.806 127.811 38.5875 127.049 38.6752C126.286 38.7629 125.589 39.1496 125.111 39.7505"
        fill="#263238"
      />
      <path
        d="M129.015 39.5775C129.015 39.6013 128.822 39.4758 128.5 39.3741C128.087 39.2411 127.642 39.2411 127.229 39.3741C126.816 39.5227 126.461 39.7966 126.212 40.1573C126.022 40.4353 125.964 40.6421 125.934 40.6353C125.937 40.4415 125.997 40.253 126.106 40.0928C126.337 39.679 126.712 39.3644 127.159 39.2088C127.607 39.0532 128.096 39.0674 128.534 39.2487C128.876 39.3945 129.029 39.564 129.015 39.5775Z"
        fill="#455A64"
      />
      <path
        d="M128.891 41.6233C128.92 41.4141 128.92 41.2019 128.891 40.9927C128.843 40.7733 128.74 40.5694 128.593 40.3994C128.411 40.1999 128.176 40.0564 127.915 39.9858C127.669 39.928 127.414 39.9205 127.166 39.9636C126.917 40.0067 126.679 40.0996 126.467 40.2367C126.128 40.4469 125.966 40.6367 125.945 40.6164C125.925 40.5961 126.064 40.3825 126.4 40.135C126.598 39.9899 126.821 39.882 127.057 39.8163C127.353 39.7383 127.664 39.7383 127.959 39.8163C128.259 39.8925 128.528 40.0594 128.729 40.2943C128.888 40.4887 128.99 40.7234 129.024 40.9724C129.049 41.084 129.05 41.1998 129.027 41.312C129.004 41.4242 128.958 41.5303 128.891 41.6233Z"
        fill="#455A64"
      />
      <path
        d="M119.232 46.0889C119.232 46.0889 119.191 46.0041 119.161 45.8414C119.117 45.6112 119.097 45.3773 119.1 45.143C119.097 44.363 119.291 43.5948 119.663 42.9089C120.036 42.2235 120.572 41.6411 121.225 41.2139C121.42 41.082 121.627 40.9685 121.842 40.8748C121.919 40.8325 122.003 40.806 122.09 40.7969C122.09 40.824 121.751 40.9664 121.293 41.3088C120.679 41.7477 120.174 42.3208 119.815 42.9847C119.456 43.6486 119.253 44.3856 119.222 45.1396C119.195 45.7261 119.263 46.0855 119.232 46.0889Z"
        fill="#455A64"
      />
      <path
        d="M119.646 46.4315C119.527 45.7503 119.58 45.0504 119.798 44.3944C120.016 43.7383 120.394 43.1465 120.897 42.6719C120.518 43.2261 120.205 43.8222 119.964 44.4483C119.781 45.0948 119.674 45.7603 119.646 46.4315Z"
        fill="#455A64"
      />
      <path
        d="M150.328 61.9522C150.25 61.7454 149.857 61.7013 149.857 61.7013L149.935 61.5826C149.991 61.5002 150.024 61.4044 150.031 61.305C150.039 61.2056 150.019 61.1061 149.976 61.0165L149.932 60.9317C149.818 60.7752 149.66 60.657 149.477 60.5927C149.477 60.5927 149.766 60.1995 149.694 60.0062C149.614 59.8422 149.488 59.7054 149.33 59.6131C149.173 59.5208 148.992 59.4771 148.809 59.4875C148.182 59.6272 147.564 59.8108 146.962 60.0367C146.906 60.0575 146.847 60.0661 146.788 60.0617C146.729 60.0574 146.671 60.0403 146.619 60.0116C146.568 59.9829 146.523 59.9434 146.487 59.8956C146.452 59.8479 146.428 59.7931 146.416 59.735C146.307 59.1428 146.107 58.571 145.823 58.0399C145.484 57.4704 145.087 57.4501 144.846 57.5043C144.819 57.5102 144.794 57.5219 144.772 57.5387C144.75 57.5555 144.731 57.5769 144.718 57.6015C144.706 57.626 144.698 57.6531 144.697 57.6809C144.695 57.7086 144.7 57.7363 144.711 57.7619C144.88 58.1502 145.023 58.5488 145.141 58.9553C145.206 59.2604 144.985 61.4674 144.985 61.4674L143.863 62.976L146.406 66.6475L147.525 64.9863L149.921 63.932C149.921 63.932 150.376 63.3591 150.328 63.1624C150.287 63.0336 150.203 62.9225 150.091 62.8472L150.389 62.4302C150.399 62.2684 150.379 62.1062 150.328 61.9522Z"
        fill="#FFBE9D"
      />
      <path
        d="M149.692 60.6553L149.644 60.6858L149.495 60.7706L148.949 61.0757L148.136 61.5164C147.98 61.5978 147.824 61.6927 147.644 61.7673C147.553 61.814 147.452 61.8384 147.349 61.8384C147.247 61.8384 147.146 61.814 147.054 61.7673C146.965 61.7076 146.899 61.6172 146.871 61.513C146.848 61.4139 146.848 61.3105 146.871 61.2113C146.919 61.0279 147.028 60.8664 147.18 60.7536C147.311 60.6501 147.451 60.5572 147.597 60.4756L147.966 60.2553L148.505 59.9366L148.655 59.8552L148.705 59.8281C148.705 59.8281 148.705 59.8281 148.658 59.8654L148.516 59.9569L147.987 60.296L147.621 60.5231C147.479 60.6052 147.343 60.697 147.214 60.7977C147.073 60.907 146.972 61.0594 146.926 61.2316C146.9 61.3209 146.901 61.416 146.93 61.5043C146.959 61.5926 147.014 61.6701 147.088 61.7266C147.169 61.7757 147.262 61.8004 147.356 61.7978C147.449 61.7932 147.541 61.7726 147.627 61.7368C147.8 61.6656 147.966 61.5707 148.112 61.4927L148.933 61.0587L149.489 60.774L149.641 60.696L149.692 60.6553Z"
        fill="#EB996E"
      />
      <path
        d="M150.018 61.7573C149.983 61.785 149.944 61.8078 149.903 61.8251C149.829 61.8658 149.72 61.9234 149.581 61.9878C149.306 62.1234 148.923 62.2963 148.489 62.4692C148.385 62.5151 148.277 62.5536 148.167 62.5845C148.056 62.6156 147.939 62.6156 147.828 62.5845C147.737 62.559 147.655 62.5083 147.591 62.4381C147.528 62.3679 147.485 62.2811 147.469 62.1878C147.453 62.0682 147.483 61.9469 147.554 61.8488C147.571 61.8206 147.593 61.7955 147.618 61.7742L147.649 61.7539C147.649 61.7539 147.615 61.7878 147.571 61.859C147.509 61.9605 147.486 62.0809 147.506 62.198C147.527 62.2797 147.57 62.3542 147.629 62.4139C147.689 62.4735 147.764 62.516 147.845 62.537C147.944 62.5621 148.048 62.5621 148.147 62.537C148.249 62.5099 148.354 62.4624 148.462 62.4218C148.893 62.2489 149.279 62.0828 149.557 61.9573L149.896 61.8081C149.935 61.7866 149.976 61.7695 150.018 61.7573Z"
        fill="#EB996E"
      />
      <path
        d="M150.144 62.8024C150.11 62.823 150.072 62.8379 150.032 62.8465L149.714 62.9516L148.656 63.2702C148.551 63.2973 148.453 63.3312 148.347 63.3482C148.247 63.3705 148.142 63.3635 148.046 63.3279C148.001 63.3108 147.96 63.2845 147.926 63.2506C147.892 63.2167 147.866 63.176 147.849 63.1312C147.824 63.053 147.824 62.9688 147.849 62.8905C147.891 62.7818 147.969 62.6908 148.069 62.6329C148.103 62.6093 148.141 62.592 148.181 62.582C148.15 62.6063 148.117 62.6289 148.083 62.6498C147.994 62.7125 147.926 62.8013 147.89 62.9041C147.867 62.9734 147.867 63.0483 147.89 63.1177C147.905 63.1555 147.928 63.1895 147.958 63.2171C147.988 63.2446 148.024 63.2651 148.063 63.277C148.152 63.3029 148.248 63.3029 148.337 63.277C148.432 63.277 148.534 63.2262 148.636 63.199C149.049 63.077 149.426 62.9685 149.697 62.8973L150.036 62.8092L150.144 62.8024Z"
        fill="#EB996E"
      />
      <path
        d="M150.157 63.757C150.037 63.7774 149.916 63.7899 149.794 63.7943L148.923 63.8417H148.672C148.593 63.8519 148.513 63.8519 148.435 63.8417C148.36 63.8291 148.292 63.7922 148.241 63.7366C148.218 63.7114 148.202 63.6809 148.193 63.6479C148.184 63.6149 148.183 63.5802 148.191 63.5468C148.201 63.5014 148.222 63.459 148.252 63.4226C148.281 63.3863 148.318 63.3569 148.36 63.3366C148.388 63.3189 148.422 63.3106 148.455 63.3129C148.426 63.3255 148.397 63.3403 148.37 63.3569C148.334 63.3782 148.303 63.4066 148.279 63.4405C148.254 63.4743 148.237 63.5128 148.228 63.5536C148.224 63.5809 148.227 63.6087 148.235 63.6351C148.243 63.6614 148.257 63.6857 148.275 63.7061C148.321 63.7527 148.38 63.7825 148.445 63.7909C148.52 63.7993 148.596 63.7993 148.672 63.7909H148.919C149.258 63.7909 149.57 63.7637 149.794 63.7604C149.915 63.7493 150.036 63.7481 150.157 63.757Z"
        fill="#EB996E"
      />
      <path
        d="M147.86 62.9284C147.662 62.8556 147.458 62.8023 147.25 62.7691C147.039 62.757 146.828 62.7661 146.619 62.7962C146.822 62.7197 147.04 62.6919 147.255 62.7148C147.471 62.7378 147.678 62.8109 147.86 62.9284Z"
        fill="#EB996E"
      />
      <path
        d="M131.861 79.4148L132.39 79.2792C132.39 79.2792 132.573 78.3944 132.807 78.2079C133.041 78.0215 135.37 76.9603 135.882 76.6824C136.394 76.4044 138.394 75.4924 138.645 75.9738C138.929 76.523 138.112 76.9909 137.672 77.1841C137.231 77.3773 135.977 77.8825 136.224 78.1842C136.471 78.4859 139.299 78.3503 139.299 78.3503L139.638 77.8757C139.638 77.8757 140.258 78.4622 140.187 78.9741C140.152 79.2691 140.057 79.5539 139.909 79.8114H139.699C139.758 79.9007 139.792 80.0045 139.796 80.1115C139.8 80.2186 139.775 80.3247 139.723 80.4183C139.556 80.6643 139.37 80.8967 139.167 81.1133C139.023 81.3566 138.832 81.5681 138.604 81.7348C138.376 81.9015 138.116 82.0199 137.841 82.0828C137.722 82.1133 133.071 82.5202 133.071 82.5202L131.512 82.6761L131.861 79.4148Z"
        fill="#FFBE9D"
      />
      <path
        d="M38.6106 10.28L26.2637 5.73047L14.0389 10.219C12.476 10.975 11.1098 11.4259 11.1098 12.9311V20.3453C11.082 25.5481 12.4745 30.6599 15.1373 35.1297C17.7576 39.5911 21.6838 43.1409 26.3857 45.3001C31.3828 43.6356 35.1119 39.1979 37.3935 35.3975C40.054 30.9292 41.4464 25.8203 41.421 20.6199V13.9549C41.421 11.8293 40.3226 10.7919 38.6106 10.28Z"
        fill="#FF725E"
      />
      <g opacity="0.8">
        <path
          d="M38.6106 10.28L26.2637 5.73047L14.0389 10.219C12.476 10.975 11.1098 11.4259 11.1098 12.9311V20.3453C11.082 25.5481 12.4745 30.6599 15.1373 35.1297C17.7576 39.5911 21.6838 43.1409 26.3857 45.3001C31.3828 43.6356 35.1119 39.1979 37.3935 35.3975C40.054 30.9292 41.4464 25.8203 41.421 20.6199V13.9549C41.421 11.8293 40.3226 10.7919 38.6106 10.28Z"
          fill="white"
        />
      </g>
      <path
        d="M36.1683 13.2923L26.2623 9.64453L16.4546 13.2415C15.2003 13.8483 14.1019 14.211 14.1019 15.4078V21.3676C14.0798 25.5434 15.198 29.6461 17.3361 33.2331C19.435 36.8194 22.5854 39.6739 26.3606 41.4101C30.3677 40.0744 33.3612 36.5113 35.1919 33.467C37.3273 29.879 38.4443 25.7768 38.4227 21.6015V16.2417C38.4227 14.5365 37.5413 13.7025 36.1683 13.2923Z"
        fill="#97262A"
      />
      <path
        d="M33.7616 23.5308H27.9374V17.7031H24.5913V23.5308H18.7637V26.8768H24.5913V32.7011H27.9374V26.8768H33.7616V23.5308Z"
        fill="white"
      />
      <path
        d="M44.6484 40.5014C44.6484 40.5014 44.7026 40.42 44.8179 40.2675L44.9162 40.142C44.9501 40.0912 44.9772 40.0336 45.0145 39.9725C45.1015 39.834 45.1753 39.6875 45.2349 39.5352C45.2688 39.4471 45.3095 39.3589 45.3434 39.2606C45.3773 39.1623 45.401 39.0538 45.4315 38.9453C45.4637 38.8343 45.4875 38.7209 45.5027 38.6063C45.5283 38.4775 45.5464 38.3473 45.557 38.2164C45.6085 37.5927 45.5801 36.965 45.4722 36.3485C45.1503 34.6526 44.5695 33.0164 43.75 31.4972C42.6961 29.4778 41.4616 27.5579 40.0615 25.7611C38.3761 23.5754 36.5386 21.5114 34.5627 19.5843C32.304 17.3667 29.8922 15.3106 27.3451 13.4312C25.9891 12.4379 24.5754 11.4785 23.077 10.5733C21.5507 9.64243 19.9653 8.81233 18.3308 8.08832C16.6489 7.3176 14.8766 6.76176 13.0557 6.43393C12.1266 6.26896 11.1779 6.24609 10.2419 6.36613C9.77403 6.4345 9.31774 6.56682 8.88587 6.75938C8.45148 6.95441 8.06306 7.23875 7.74588 7.59389C7.42871 7.94903 7.1899 8.36701 7.04502 8.82059C6.90365 9.27226 6.82383 9.74093 6.80771 10.2139C6.79569 11.1583 6.92715 12.0991 7.19758 13.004C7.72966 14.7762 8.48253 16.4744 9.43846 18.0587C10.3462 19.5974 11.3517 21.0763 12.4489 22.4862C13.5202 23.8728 14.6355 25.1746 15.7746 26.4018C17.9391 28.7172 20.2636 30.8776 22.7312 32.8668C24.8767 34.6065 27.1425 36.1925 29.5114 37.613C31.4504 38.7987 33.4929 39.8063 35.6137 40.6234C37.2115 41.2625 38.898 41.6524 40.6141 41.7795C41.2397 41.8174 41.8676 41.7741 42.4821 41.6507C42.6105 41.627 42.7374 41.5953 42.8618 41.5557C42.978 41.5273 43.0914 41.4888 43.2008 41.4405L43.5059 41.3184C43.5975 41.2743 43.6822 41.2235 43.7636 41.1794C43.9094 41.1048 44.0469 41.0151 44.1738 40.9116C44.2314 40.8709 44.2857 40.8336 44.3297 40.7963L44.445 40.6845C44.584 40.5522 44.6586 40.4878 44.6586 40.4878L44.4552 40.6912L44.3399 40.8065C44.2958 40.8438 44.2416 40.8811 44.1839 40.9252C44.0504 41.0418 43.905 41.1441 43.75 41.2303L43.4924 41.3727L43.1872 41.4981C43.0777 41.5459 42.9643 41.5845 42.8482 41.6134C42.7223 41.6537 42.5945 41.6876 42.4652 41.7151C41.8486 41.8405 41.2187 41.8872 40.5904 41.8541C38.8681 41.733 37.1747 41.3477 35.5696 40.7116C33.4376 39.8995 31.3837 38.8952 29.4335 37.7113C27.0651 36.2898 24.7995 34.7038 22.6532 32.9651C20.1825 30.9735 17.8555 28.8097 15.6899 26.49C14.5508 25.2593 13.432 23.9575 12.3574 22.5676C11.2553 21.1555 10.2463 19.6731 9.33675 18.1299C8.3756 16.5358 7.61821 14.8274 7.08231 13.0447C6.80969 12.1298 6.67822 11.1787 6.69245 10.2241C6.70832 9.73971 6.78928 9.25966 6.93315 8.79685C7.09463 8.33408 7.34063 7.90531 7.65864 7.53233C7.99066 7.1673 8.38862 6.86824 8.83162 6.6509C9.27377 6.45523 9.74009 6.31955 10.2182 6.24747C11.1655 6.12673 12.1258 6.15074 13.0659 6.31867C14.8956 6.64922 16.6768 7.20727 18.3681 7.97983C20.0023 8.70827 21.5877 9.54173 23.1143 10.475C24.6161 11.3835 26.0399 12.3463 27.3892 13.3396C29.9454 15.2317 32.3652 17.3014 34.6305 19.5334C36.6051 21.468 38.4415 23.5388 40.126 25.7306C41.5268 27.5351 42.7592 29.4642 43.8076 31.4938C44.6195 33.0162 45.1913 34.6549 45.5027 36.3519C45.6086 36.972 45.6348 37.6032 45.5807 38.23C45.5807 38.369 45.54 38.4978 45.5231 38.6199C45.5061 38.74 45.4812 38.859 45.4485 38.9758C45.4146 39.0843 45.3841 39.1928 45.3569 39.2911C45.3298 39.3894 45.279 39.481 45.2451 39.5657C45.1826 39.7191 45.1066 39.8666 45.0179 40.0064C44.9806 40.0675 44.9501 40.1251 44.9196 40.1726L44.8179 40.298L44.6484 40.5014Z"
        fill="#FF725E"
      />
      <path
        d="M51.4728 24.0271C51.4728 24.0271 51.4728 23.9288 51.4457 23.7389C51.4457 23.6915 51.4457 23.6372 51.4457 23.5796C51.4457 23.522 51.4186 23.4576 51.405 23.3898C51.3769 23.2279 51.3338 23.069 51.2762 22.9151C51.2457 22.8304 51.2152 22.7355 51.1745 22.6372L51.0321 22.3456C50.98 22.2366 50.9188 22.1323 50.849 22.0337C50.7844 21.916 50.7119 21.8027 50.6321 21.6947C50.2573 21.1872 49.8173 20.7313 49.3235 20.3386C47.9583 19.2908 46.4397 18.4595 44.8214 17.874C42.6909 17.0683 40.4919 16.4567 38.2513 16.0467C35.5336 15.5484 32.7842 15.2414 30.0234 15.128C26.8584 14.9734 23.6864 15.0425 20.5311 15.3348C18.8631 15.4941 17.1647 15.7247 15.4459 16.0467C13.6884 16.367 11.9536 16.8007 10.2522 17.3452C8.48574 17.8919 6.79452 18.6573 5.21783 19.6233C4.41281 20.1171 3.688 20.731 3.06848 21.4438C2.76456 21.808 2.51138 22.2117 2.31587 22.6439C2.12188 23.0787 2.02162 23.5494 2.02162 24.0254C2.02162 24.5015 2.12188 24.9722 2.31587 25.4069C2.51138 25.8391 2.76456 26.2428 3.06848 26.607C3.68779 27.3196 4.41129 27.9346 5.21444 28.4309C6.79131 29.4023 8.48375 30.1723 10.2522 30.7226C11.9538 31.267 13.6886 31.7018 15.4459 32.0244C17.1681 32.3635 18.8665 32.5736 20.5311 32.733C23.6864 33.0251 26.8584 33.0942 30.0234 32.9398C32.7805 32.8175 35.5255 32.5003 38.2377 31.9905C40.4789 31.58 42.6789 30.9696 44.8112 30.1666C46.4313 29.5714 47.9498 28.7297 49.3133 27.6715C49.8071 27.2789 50.2471 26.8229 50.6219 26.3155C50.7015 26.2066 50.7751 26.0934 50.8423 25.9764C50.9097 25.877 50.9698 25.7728 51.0219 25.6645L51.1677 25.373C51.205 25.2747 51.2355 25.1831 51.266 25.095C51.3236 24.9411 51.3667 24.7822 51.3949 24.6204C51.3949 24.5526 51.4254 24.4882 51.4355 24.4305C51.4457 24.3729 51.4355 24.3187 51.4355 24.2712C51.4525 24.0814 51.4627 23.983 51.4627 23.983C51.4627 23.983 51.4627 24.0814 51.4627 24.2712V24.4339C51.4627 24.4916 51.4389 24.556 51.4254 24.6238C51.4003 24.787 51.3583 24.9472 51.2999 25.1018C51.2694 25.1899 51.2423 25.2849 51.2016 25.3832C51.1609 25.4815 51.1101 25.5764 51.0592 25.6781C51.0078 25.7878 50.9478 25.8933 50.8796 25.9934C50.8135 26.1103 50.7411 26.2235 50.6626 26.3324C50.2865 26.8388 49.8467 27.2946 49.354 27.6885C47.9893 28.7459 46.4696 29.5863 44.8485 30.1802C42.7163 30.992 40.515 31.6092 38.2716 32.0244C35.5552 32.539 32.8057 32.8607 30.0438 32.9872C26.879 33.1447 23.707 33.079 20.5514 32.7906C18.8835 32.6347 17.1782 32.4041 15.4662 32.0821C13.7039 31.7623 11.9644 31.3274 10.259 30.7803C8.48157 30.2324 6.77998 29.4636 5.19409 28.4919C4.38123 27.9912 3.64957 27.3693 3.02441 26.6477C2.71417 26.2749 2.45535 25.8621 2.25485 25.4205C2.06629 24.9676 1.96397 24.4836 1.95312 23.9932C1.96385 23.5036 2.06739 23.0204 2.25824 22.5694C2.45737 22.127 2.71631 21.714 3.0278 21.3421C3.64405 20.6377 4.36155 20.0287 5.1568 19.5352C6.74185 18.5642 8.44223 17.7954 10.2183 17.2468C11.9239 16.6993 13.6633 16.2633 15.4255 15.9416C17.1511 15.6026 18.8563 15.3924 20.5107 15.2331C23.6663 14.9447 26.8383 14.879 30.0031 15.0365C32.7641 15.1623 35.5126 15.4852 38.2276 16.0027C40.4814 16.4125 42.6931 17.0276 44.8349 17.8401C46.4558 18.4329 47.9755 19.2723 49.3404 20.3285C49.8331 20.7223 50.273 21.1781 50.649 21.6845C50.7275 21.7935 50.7999 21.9066 50.866 22.0235C50.9328 22.1246 50.9928 22.2299 51.0457 22.3388L51.1881 22.6372C51.2287 22.7321 51.2559 22.827 51.2864 22.9151C51.3442 23.0698 51.3862 23.23 51.4118 23.3932C51.4118 23.461 51.4389 23.5254 51.4491 23.5864V23.7457C51.4694 23.9424 51.4728 24.0271 51.4728 24.0271Z"
        fill="#FF725E"
      />
      <path
        d="M42.429 3.51893L42.1883 3.36298L42.0561 3.26128C42.0052 3.23076 41.9442 3.20703 41.8832 3.17652C41.7377 3.09993 41.5857 3.0364 41.4289 2.98667L41.1475 2.89514L40.8255 2.83073C40.7083 2.80548 40.5892 2.78961 40.4695 2.78327C40.3401 2.76713 40.21 2.75808 40.0796 2.75615C39.4527 2.74795 38.8273 2.81972 38.2185 2.96972C36.555 3.40266 34.968 4.0887 33.513 5.0038C31.5712 6.19208 29.7409 7.55369 28.0447 9.07197C25.977 10.9142 24.0431 12.9014 22.2577 15.0183C20.1956 17.4238 18.3071 19.9728 16.6064 22.6461C15.7114 24.0598 14.8503 25.5446 14.0502 27.1007C13.2219 28.6857 12.4982 30.3233 11.8839 32.0028C11.2309 33.734 10.7981 35.5404 10.5956 37.3796C10.4951 38.3173 10.5373 39.2649 10.7211 40.19C10.8231 40.6525 10.9861 41.0994 11.2059 41.5189C11.4315 41.9381 11.7423 42.3054 12.1183 42.5973C12.4943 42.8892 12.9271 43.0993 13.3891 43.214C13.8507 43.3246 14.3252 43.3713 14.7994 43.353C15.7409 43.3016 16.6691 43.1073 17.5522 42.7767C19.2834 42.1222 20.9258 41.2532 22.4408 40.19C23.9155 39.1809 25.3221 38.0756 26.6513 36.8812C27.9633 35.7184 29.1838 34.5081 30.3296 33.2945C32.4947 30.981 34.4931 28.5169 36.3098 25.9209C37.907 23.6699 39.3434 21.309 40.6085 18.8559C41.6577 16.8337 42.5223 14.721 43.1918 12.5435C43.7173 10.905 43.9894 9.19587 43.9986 7.47521C43.9925 6.8486 43.9059 6.22537 43.741 5.62081C43.7139 5.48859 43.6562 5.36994 43.6189 5.2479C43.5823 5.13165 43.537 5.01833 43.4833 4.90888C43.4336 4.80718 43.3862 4.70886 43.341 4.61394C43.2867 4.5258 43.2325 4.44443 43.1816 4.36646C43.0966 4.2259 42.9978 4.09416 42.8867 3.9732C42.8426 3.92235 42.8019 3.86811 42.7612 3.82743L42.6426 3.71894L42.429 3.52232C42.429 3.52232 42.507 3.58334 42.6494 3.71216L42.7714 3.81726C42.8121 3.85794 42.8528 3.90879 42.9002 3.96303C43.0127 4.08332 43.1137 4.21384 43.202 4.3529C43.2528 4.43087 43.3104 4.51224 43.3613 4.60377C43.4121 4.6953 43.4562 4.79362 43.5071 4.89871C43.5639 5.00711 43.6105 5.12061 43.6461 5.23773C43.6867 5.35977 43.7444 5.47842 43.7749 5.61064C43.9434 6.21817 44.0334 6.8448 44.0427 7.47521C44.0407 9.20094 43.7731 10.9161 43.2494 12.5604C42.5861 14.7441 41.7248 16.8627 40.6763 18.8898C39.4206 21.3504 37.9908 23.7182 36.398 25.9752C34.5837 28.5795 32.5852 31.0504 30.4178 33.3691C29.2719 34.5929 28.0447 35.7998 26.7361 36.966C25.4026 38.1616 23.9927 39.2691 22.5154 40.2815C20.9945 41.3525 19.3439 42.2262 17.6031 42.8818C16.7095 43.2184 15.7697 43.4162 14.8164 43.4683C14.3319 43.4852 13.8473 43.4373 13.3756 43.3259C12.9028 43.1957 12.4581 42.9797 12.0636 42.6885C11.6835 42.3775 11.365 41.998 11.1245 41.5698C10.8981 41.1423 10.7305 40.6861 10.6262 40.2137C10.4392 39.2776 10.3969 38.3184 10.5007 37.3694C10.7035 35.5212 11.1375 33.7057 11.7924 31.9655C12.4065 30.2831 13.129 28.6423 13.9553 27.0532C14.7587 25.4904 15.6198 24.0021 16.5182 22.5918C18.2248 19.9162 20.119 17.3649 22.1865 14.9572C23.9788 12.8516 25.9183 10.8758 27.9904 9.04485C29.6916 7.52371 31.5289 6.16194 33.4791 4.97668C34.9458 4.05615 36.5471 3.36988 38.2252 2.9426C38.8363 2.79282 39.4642 2.7233 40.0932 2.73581C40.2248 2.7387 40.356 2.74889 40.4865 2.76632C40.6074 2.77399 40.7275 2.79099 40.8458 2.81717L41.1848 2.88497C41.2865 2.91548 41.3781 2.94938 41.4662 2.97989C41.6231 3.03066 41.7751 3.09533 41.9205 3.17313C41.9815 3.20703 42.0425 3.23076 42.0934 3.26128L42.2256 3.35281L42.429 3.51893Z"
        fill="#FF725E"
      />
      <path
        d="M1.71026 34.2444C1.83838 34.3731 1.92553 34.5368 1.96069 34.7149C1.99585 34.893 1.97744 35.0775 1.9078 35.2452C1.83816 35.4129 1.7204 35.5562 1.5694 35.6569C1.41839 35.7577 1.24091 35.8115 1.05935 35.8115C0.877798 35.8115 0.700314 35.7577 0.549308 35.6569C0.398302 35.5562 0.280545 35.4129 0.210903 35.2452C0.141261 35.0775 0.122858 34.893 0.158017 34.7149C0.193175 34.5368 0.280319 34.3731 0.408446 34.2444C0.493574 34.1583 0.594937 34.09 0.706667 34.0433C0.818397 33.9967 0.938273 33.9727 1.05935 33.9727C1.18043 33.9727 1.30031 33.9967 1.41204 34.0433C1.52377 34.09 1.62513 34.1583 1.71026 34.2444Z"
        fill="#FF725E"
      />
      <path
        d="M53.5816 5.75466C53.5816 5.93637 53.5277 6.114 53.4268 6.26508C53.3258 6.41616 53.1823 6.53392 53.0145 6.60346C52.8466 6.67299 52.6619 6.69119 52.4836 6.65574C52.3054 6.62029 52.1417 6.53279 52.0132 6.4043C51.8847 6.27582 51.7972 6.11211 51.7618 5.9339C51.7263 5.75568 51.7445 5.57096 51.8141 5.40308C51.8836 5.23521 52.0014 5.09172 52.1525 4.99077C52.3035 4.88982 52.4812 4.83594 52.6629 4.83594C52.9065 4.83594 53.1402 4.93273 53.3125 5.10503C53.4848 5.27732 53.5816 5.511 53.5816 5.75466Z"
        fill="#FF725E"
      />
      <path
        d="M7.503 37.2652C7.503 37.3114 7.48928 37.3567 7.46357 37.3951C7.43787 37.4336 7.40134 37.4636 7.35859 37.4813C7.31585 37.499 7.26882 37.5036 7.22344 37.4946C7.17806 37.4856 7.13638 37.4633 7.10367 37.4306C7.07096 37.3979 7.04868 37.3562 7.03965 37.3108C7.03063 37.2654 7.03526 37.2184 7.05296 37.1757C7.07067 37.1329 7.10065 37.0964 7.13912 37.0707C7.17759 37.045 7.22281 37.0312 7.26908 37.0312C7.33112 37.0312 7.39061 37.0559 7.43448 37.0998C7.47835 37.1436 7.503 37.2031 7.503 37.2652Z"
        fill="#FF725E"
      />
      <path
        d="M12.4893 11.5074C12.4893 11.5536 12.4756 11.5988 12.4499 11.6373C12.4242 11.6758 12.3877 11.7058 12.3449 11.7235C12.3022 11.7412 12.2551 11.7458 12.2098 11.7368C12.1644 11.7278 12.1227 11.7055 12.09 11.6728C12.0573 11.64 12.035 11.5984 12.026 11.553C12.017 11.5076 12.0216 11.4606 12.0393 11.4178C12.057 11.3751 12.087 11.3386 12.1254 11.3129C12.1639 11.2872 12.2091 11.2734 12.2554 11.2734C12.3174 11.2734 12.3769 11.2981 12.4208 11.342C12.4647 11.3858 12.4893 11.4453 12.4893 11.5074Z"
        fill="#FF725E"
      />
      <path
        d="M48.9464 24.0269C48.9464 24.0732 48.9326 24.1184 48.9069 24.1568C48.8812 24.1953 48.8447 24.2253 48.802 24.243C48.7592 24.2607 48.7122 24.2653 48.6668 24.2563C48.6214 24.2473 48.5797 24.225 48.547 24.1923C48.5143 24.1596 48.492 24.1179 48.483 24.0725C48.474 24.0271 48.4786 23.9801 48.4963 23.9374C48.514 23.8946 48.544 23.8581 48.5825 23.8324C48.6209 23.8067 48.6662 23.793 48.7124 23.793C48.7745 23.793 48.834 23.8176 48.8778 23.8615C48.9217 23.9054 48.9464 23.9648 48.9464 24.0269Z"
        fill="#FF725E"
      />
      <path
        d="M52.1905 13.1329C52.1911 13.1793 52.178 13.2249 52.1527 13.2638C52.1274 13.3027 52.0911 13.3332 52.0484 13.3514C52.0058 13.3696 51.9586 13.3748 51.913 13.3661C51.8674 13.3575 51.8254 13.3355 51.7924 13.3029C51.7593 13.2703 51.7367 13.2287 51.7274 13.1832C51.7181 13.1377 51.7225 13.0905 51.7401 13.0476C51.7577 13.0046 51.7877 12.9679 51.8262 12.942C51.8648 12.9162 51.9101 12.9024 51.9566 12.9024C51.9871 12.9019 52.0174 12.9075 52.0458 12.9189C52.0742 12.9303 52.1 12.9472 52.1218 12.9687C52.1435 12.9901 52.1608 13.0157 52.1726 13.0439C52.1844 13.0721 52.1905 13.1023 52.1905 13.1329Z"
        fill="#FF725E"
      />
    </svg>
  </div>
);

/* ─── Side Progress ─── */
const SideProgress = ({ steps, currentStepId, completedSteps }) => {
  const completedCount = completedSteps.length;
  return (
    <aside className="proposal-side">
      <MedicalIllustration />
      <h3 className="proposal-side__heading">
        Share your health details with us it&apos;s easy, and we&apos;re here to
        support you!
      </h3>
      <p className="proposal-side__subtext">{TEXT_CONTENT.MED_SUBHEADING}</p>
      <ul className="proposal-side__steps">
        {steps.map((step) => {
          const isActive = step.id === currentStepId;
          const isCompleted = completedSteps.includes(step.id);
          return (
            <li
              key={step.id}
              className={`proposal-side__step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
            >
              <div className="proposal-side__step-dot">
                {isCompleted ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.0306 6.96937C14.1004 7.03903 14.1557 7.12175 14.1934 7.21279C14.2312 7.30384 14.2506 7.40144 14.2506 7.5C14.2506 7.59856 14.2312 7.69616 14.1934 7.78721C14.1557 7.87825 14.1004 7.96097 14.0306 8.03063L8.78063 13.2806C8.71097 13.3504 8.62826 13.4057 8.53721 13.4434C8.44616 13.4812 8.34857 13.5006 8.25 13.5006C8.15144 13.5006 8.05385 13.4812 7.9628 13.4434C7.87175 13.4057 7.78903 13.3504 7.71938 13.2806L5.46938 11.0306C5.32865 10.8899 5.24959 10.699 5.24959 10.5C5.24959 10.301 5.32865 10.1101 5.46938 9.96937C5.61011 9.82864 5.80098 9.74958 6 9.74958C6.19903 9.74958 6.3899 9.82864 6.53063 9.96937L8.25 11.6897L12.9694 6.96937C13.039 6.89964 13.1218 6.84432 13.2128 6.80658C13.3038 6.76884 13.4014 6.74941 13.5 6.74941C13.5986 6.74941 13.6962 6.76884 13.7872 6.80658C13.8783 6.84432 13.961 6.89964 14.0306 6.96937ZM19.5 9.75C19.5 11.6784 18.9282 13.5634 17.8568 15.1668C16.7855 16.7702 15.2627 18.0199 13.4812 18.7578C11.6996 19.4958 9.73919 19.6889 7.84787 19.3127C5.95656 18.9365 4.21928 18.0079 2.85571 16.6443C1.49215 15.2807 0.563554 13.5434 0.187348 11.6521C-0.188858 9.76082 0.00422452 7.80042 0.742179 6.01884C1.48013 4.23726 2.72982 2.71451 4.33319 1.64317C5.93657 0.571828 7.82164 0 9.75 0C12.335 0.00272983 14.8134 1.03084 16.6413 2.85872C18.4692 4.68661 19.4973 7.16498 19.5 9.75ZM18 9.75C18 8.1183 17.5161 6.52325 16.6096 5.16655C15.7031 3.80984 14.4146 2.75242 12.9071 2.12799C11.3997 1.50357 9.74085 1.34019 8.14051 1.65852C6.54017 1.97685 5.07016 2.76259 3.91637 3.91637C2.76259 5.07015 1.97685 6.54016 1.65853 8.1405C1.3402 9.74085 1.50358 11.3996 2.128 12.9071C2.75242 14.4146 3.80984 15.7031 5.16655 16.6096C6.52326 17.5161 8.11831 18 9.75 18C11.9373 17.9975 14.0343 17.1275 15.5809 15.5809C17.1275 14.0343 17.9975 11.9373 18 9.75Z"
                      fill="#03BF43"
                    />
                  </svg>
                ) : isActive ? (
                  <div className="dot active-dot">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="24" cy="24" r="24" fill="#EAD4D4" />
                      <circle cx="24" cy="24" r="16" fill="#CB9394" />
                      <circle cx="24" cy="24" r="10" fill="#97262A" />
                    </svg>
                  </div>
                ) : (
                  <div className="dot">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="10" cy="10" r="10" fill="#AFB0B1" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="proposal-side__step-info">
                <strong>{step.label}</strong>
                <span>{step.description}</span>
              </div>
            </li>
          );
        })}
      </ul>
      <ProgressBar current={completedCount} total={steps.length} />
    </aside>
  );
};

/* ─── Pending Documents Bar ─── */
const PendingDocumentsBar = ({ docs, onUploadClick }) => {
  if (!docs || docs.length === 0) return null;
  return (
    <div className="proposal-pending-docs">
      <span className="proposal-pending-docs__icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="3"
            stroke="#dc2626"
            strokeWidth="2"
          />
          <path
            d="M8 12h8M12 8v8"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        Pending Documents:
      </span>
      {docs.map((doc, i) => (
        <React.Fragment key={doc}>
          {i > 0 && <span className="proposal-pending-docs__sep">|</span>}
          <span className="proposal-pending-docs__item">{doc}</span>
        </React.Fragment>
      ))}
      <button
        className="proposal-pending-docs__upload"
        onClick={onUploadClick}
        type="button"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.5 8.43794V12.9379C13.5 13.0871 13.4407 13.2302 13.3352 13.3357C13.2298 13.4412 13.0867 13.5004 12.9375 13.5004H0.5625C0.413316 13.5004 0.270242 13.4412 0.164753 13.3357C0.0592633 13.2302 0 13.0871 0 12.9379V8.43794C0 8.28876 0.0592633 8.14568 0.164753 8.04019C0.270242 7.9347 0.413316 7.87544 0.5625 7.87544C0.711684 7.87544 0.854758 7.9347 0.960247 8.04019C1.06574 8.14568 1.125 8.28876 1.125 8.43794V12.3754H12.375V8.43794C12.375 8.28876 12.4343 8.14568 12.5398 8.04019C12.6452 7.9347 12.7883 7.87544 12.9375 7.87544C13.0867 7.87544 13.2298 7.9347 13.3352 8.04019C13.4407 8.14568 13.5 8.28876 13.5 8.43794ZM4.33547 3.77341L6.1875 1.92068V8.43794C6.1875 8.58713 6.24676 8.7302 6.35225 8.83569C6.45774 8.94118 6.60082 9.00044 6.75 9.00044C6.89918 9.00044 7.04226 8.94118 7.14775 8.83569C7.25324 8.7302 7.3125 8.58713 7.3125 8.43794V1.92068L9.16453 3.77341C9.27008 3.87896 9.41323 3.93825 9.5625 3.93825C9.71177 3.93825 9.85492 3.87896 9.96047 3.77341C10.066 3.66786 10.1253 3.52471 10.1253 3.37544C10.1253 3.22618 10.066 3.08302 9.96047 2.97747L7.14797 0.164974C7.09573 0.112674 7.03369 0.0711849 6.9654 0.0428775C6.89712 0.0145701 6.82392 0 6.75 0C6.67608 0 6.60288 0.0145701 6.5346 0.0428775C6.46631 0.0711849 6.40427 0.112674 6.35203 0.164974L3.53953 2.97747C3.43398 3.08302 3.37469 3.22618 3.37469 3.37544C3.37469 3.52471 3.43398 3.66786 3.53953 3.77341C3.64508 3.87896 3.78823 3.93825 3.9375 3.93825C4.08677 3.93825 4.22992 3.87896 4.33547 3.77341Z"
            fill="#97262A"
          ></path>
        </svg>
        <span>Upload Now</span>
      </button>
    </div>
  );
};

const getLifestyleFields = (data = {}) => {
  const fields = [
    {
      fieldReference: "weight",
      label: "Weight",
      validation: { required: true },
    },
    {
      fieldReference: "height",
      label: "Height",
      validation: { required: true },
    },
    {
      fieldReference: "isAlcohol",
      label: "Alcohol",
      validation: { required: true },
    },
    {
      fieldReference: "hasHealthConditions",
      label: "HealthConditions",
      validation: { required: true },
    },
    {
      fieldReference: "hasNarcotics",
      label: "Narcotics",
      validation: { required: true },
    },
    {
      fieldReference: "hasHazard",
      label: "Hazard",
      validation: { required: true },
    },
    {
      fieldReference: "isTravel",
      label: "Travel",
      validation: { required: true },
    },
    {
      fieldReference: "isTobacco",
      label: "Tobacco",
      validation: { required: true },
    },

  ];

  if (data.isTobacco === true) {
    fields.push(
      {
        fieldReference: "tobaccoOption",
        label: "Tobacco Consumed As",
        validation: { required: true },
      },
      {
        fieldReference: "tobaccoQuantity",
        label: "Tobacco per day",
        validation: { required: true },
      },
      {
        fieldReference: "tobaccoYears",
        label: "Since how many years",
        validation: { required: true },
      },
    );
  }

  if (data.isAlcohol === true) {
    fields.push(
      {
        fieldReference: "alcoholOption",
        label: "Alcohol Consumed As",
        validation: { required: true },
      },
      {
        fieldReference: "alcoholQuantity",
        label: "Quantity per day",
        validation: { required: true },
      },
      {
        fieldReference: "alcoholYears",
        label: "Since how many years",
        validation: { required: true },
      },
    );
  }

  if (data.hasHealthConditions === true) {
    fields.push({
      fieldReference: "healthConditionsDesc",
      label: "Health Conditions",
      validation: { required: true },
    });
  }

  if (data.hasNarcotics === true) {
    fields.push({
      fieldReference: "narcoticsDesc",
      label: "Narcotics",
      validation: { required: true },
    });
  }

  if (data.hasHazard === true) {
    fields.push({
      fieldReference: "hazardTypes",
      label: "Hazard Types",
      validation: { required: true },
    });
  }

  if (data.isTravel === true) {
    fields.push(
      {
        fieldReference: "travelCountry",
        label: "Country",
        validation: { required: true },
      },
      {
        fieldReference: "travelPurpose",
        label: "Purpose",
        validation: { required: true },
      },
      {
        fieldReference: "travelDuration",
        label: "Duration in Years",
        validation: { required: true },
      },
    );
  }

  return fields;
};

/* ═══════════════════════════════════════════════════
   LIFESTYLE DETAILS
   ═══════════════════════════════════════════════════ */
const TOBACCO_OPTIONS = [
  "Beedi",
  "Cigarette",
  "Cigars",
  "E-Cigarette",
  "Gutka/Tobacco",
  "All of the above",
];
const TOBBACO_QUANTITY_OPTIONS = [
  { label: "<5 Units", value: "4" },
  { label: "6-10 Units", value: "10" },
  { label: ">10 Units", value: "15" },
];
const ALCOHOL_OPTIONS = [
  "Wine (Glasses)",
  "Hard-Liquor (Pegs)",
  "Beer (Bottles)",
  "All of the above",
];
const ALCOHOL_QUANTITY_OPTIONS = [
  { label: "1-2 per day", value: "2" },
  { label: ">2 per day", value: "5" },
  { label: "Occasional", value: "Occasional" },
];
const HAZARD_OPTIONS = [
  "Working at heights",
  "Diving",
  "Offshore/oil and natural gas",
  "Flying other than as a fare-paying passenger",
  "Working in mines",
  "Any other hazardous avocation/occupation",
  "Working underground",
  "Mountaineering",
  "Using Explosives",
];
const VISIT_REASONS = [
  "Higher studies",
  "Official",
  "Personal vacation",
  "Project work",
  "Resident of the Country",
];

const LifestyleDetails = ({ formData, errors, onChange, onContinue }) => {
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const consumedTobacco = useSelector(
    (state) => state.journey?.leadFormData?.tobaccoConsumption,
  );
  const isTobaccoLocked =
    consumedTobacco === "Yes, I Do" ||
    consumedTobacco === "It's not for me" ||
    consumedTobacco === "It's No for me"||
    consumedTobacco === "No, I don't"

  const derivedTobaccoValue =
    consumedTobacco === "Yes, I Do"
      ? true
      : consumedTobacco === "It's not for me" ||
          consumedTobacco === "It's No for me" ||
          consumedTobacco === "No, I don't"
        ? false
        : formData.isTobacco;

  useEffect(() => {
    if (consumedTobacco === "Yes, I Do") {
      onChange("isTobacco", true);
    } else if (
      consumedTobacco === "It's not for me" ||
      consumedTobacco === "It's No for me" ||
      consumedTobacco === "No, I don't"
    ) {
      onChange("isTobacco", false);
    }
  }, [consumedTobacco]);

  const countryOptions = useMemo(() => {
    return (apiCache.countrydetails?.data || []).map((item) => ({
      label: item.countryName,
      value: item.countryName,
    }));
  }, [apiCache.countrydetails]);

  return (
    <div className="proposal-section">
      {/* Weight & Height */}
      <div className="proposal-form-grid">
        <h3 className="proposal-section__title">Lifestyle Details</h3>
        <p className="proposal-section__desc">{TEXT_CONTENT.MED_HEADING}</p>
        <div className="form-wraper">
          <FormField label="Weight" halfWidth error={errors?.weight}>
            <div className="proposal-unit-input">
              <TextInput
                name="weight"
                autoComplete="weight-off"
                value={formData.weight}
                onChange={(name, value) => {
                  if (
                    /^\d{0,3}$/.test(value) &&
                    (value === "" || Number(value) <= 250)
                  ) {
                    onChange(name, value);
                  }
                }}
                placeholder="70"
                maxLength={3}
              />
              <span className="proposal-unit-label">kg</span>
            </div>
          </FormField>
          <FormField label="Height" halfWidth error={errors?.height}>
            <div className="proposal-height-input">
              <div className="proposal-unit-input">
                <TextInput
                  name="heightFeet"
                  type="text"
                  autoComplete="height-feet-off"
                  value={formData.heightFeet}
                  onChange={(name, value) => {
                    if (/^\d*$/.test(value)) {
                      onChange("heightFeet", value);
                      const totalInches =
                        (Number(value) || 0) * 12 +
                        (Number(formData.heightInches) || 0);
                      onChange("height", Math.round(totalInches * 2.54));
                    }
                  }}
                  placeholder="5"
                  maxLength={1}
                />
                <span className="proposal-unit-label">Feet</span>
              </div>

              <div className="proposal-unit-input">
                <TextInput
                  name="heightInches"
                  type="text"
                  value={formData.heightInches}
                  onChange={(name, value) => {
                    if (/^\d*$/.test(value)) {
                      if (value === "" || Number(value) <= 11)
                        onChange("heightInches", value);
                      const totalInches =
                        (Number(formData.heightFeet) || 0) * 12 +
                        (Number(value) || 0);
                      onChange("height", Math.round(totalInches * 2.54));
                    }
                  }}
                  placeholder="10"
                  maxLength={2}
                />
                <span className="proposal-unit-label">Inches</span>
              </div>
            </div>
          </FormField>
          <div className="proposal-toggle-section bg-shadow-box">
            <div className="proposal-toggle-section-list">
              {/* Tobacco */}
              <ToggleSwitch
                label={TEXT_CONTENT.MED_TOGGLE_ONE}
                checked={derivedTobaccoValue}
                onChange={(val) => {
                  onChange("isTobacco", val);
                  if (!val) {
                    onChange("tobaccoOption", "");
                    onChange("tobaccoQuantity", "");
                    onChange("tobaccoYears", "");
                  }
                }}
                id="tobacco"
                disabled={isTobaccoLocked}
                toogleContent={
                  derivedTobaccoValue
                    ? "You've declared yourself as a smoker pre-quote"
                    : "You've declared yourself as non-smoker pre-quote"
                }
              />
              {formData.isTobacco === true && (
                <div className="form-wraper">
                  <FormField
                    label="Tobacco Consumed as"
                    halfWidth
                    error={errors?.tobaccoOption}
                  >
                    <SelectInput
                      name="tobaccoOption"
                      value={formData.tobaccoOption}
                      onChange={onChange}
                      options={TOBACCO_OPTIONS}
                    />
                  </FormField>
                  <FormField
                    label="Tobacco per day"
                    halfWidth
                    error={errors?.tobaccoQuantity}
                  >
                    <SelectInput
                      name="tobaccoQuantity"
                      value={formData.tobaccoQuantity}
                      onChange={onChange}
                      options={TOBBACO_QUANTITY_OPTIONS}
                    />
                  </FormField>
                  <FormField
                    label="Since how many years"
                    halfWidth
                    error={errors?.tobaccoYears}
                  >
                    <TextInput
                      name="tobaccoYears"
                      type="text"
                      value={formData.tobaccoYears}
                      onChange={(name, value) => {
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      placeholder="e.g: 3 or 4 years"
                      maxLength={2}
                      autoComplete="tob-off"
                    />
                  </FormField>
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Alcohol */}
              <ToggleSwitch
                label={TEXT_CONTENT.MED_TOGGLE_TWO}
                checked={formData.isAlcohol}
                error={errors?.isAlcohol}
                onChange={(val) => {
                  onChange("isAlcohol", val);
                  if (!val) {
                    onChange("alcoholOption", "");
                    onChange("alcoholQuantity", "");
                    onChange("alcoholYears", "");
                  }
                }}
                id="alcohol"
              />
              {formData.isAlcohol === true && (
                <div className="form-wraper">
                  <FormField
                    label="Alcohol consumed as"
                    halfWidth
                    error={errors?.alcoholOption}
                  >
                    <SelectInput
                      name="alcoholOption"
                      value={formData.alcoholOption}
                      onChange={onChange}
                      options={ALCOHOL_OPTIONS}
                    />
                  </FormField>
                  <FormField
                    label="Quantity per day"
                    halfWidth
                    error={errors?.alcoholQuantity}
                  >
                    <SelectInput
                      name="alcoholQuantity"
                      value={formData.alcoholQuantity}
                      onChange={onChange}
                      options={ALCOHOL_QUANTITY_OPTIONS}
                    />
                  </FormField>
                  <FormField
                    label="Since how many years"
                    halfWidth
                    error={errors?.alcoholYears}
                  >
                    <TextInput
                      name="alcoholYears"
                      autoComplete="year-off"
                      type="text"
                      value={formData.alcoholYears}
                      onChange={(name, value) => {
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      placeholder="e.g: 3 or 4 years"
                      maxLength={2}
                    />
                  </FormField>
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Health conditions */}
              <ToggleSwitch
                label={TEXT_CONTENT.MED_TOGGLE_THREE}
                checked={formData.hasHealthConditions}
                error={errors?.hasHealthConditions}
                onChange={(val) => {
                  onChange("hasHealthConditions", val);
                  if (!val) {
                    onChange("healthConditionsDesc", "");
                  }
                }}
                id="healthConditions"
                tooltip={TEXT_CONTENT.MED_TOOLTIP}
              />
              {formData.hasHealthConditions  === true && (
                <div className="form-wraper">
                  <TextArea
                    name="healthConditionsDesc"
                    value={formData.healthConditionsDesc}
                    onChange={onChange}
                    error={errors?.healthConditionsDesc}
                    maxLength={50}
                  />
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Narcotics */}
              <ToggleSwitch
                label={TEXT_CONTENT.MED_TOOFLE_FOUR}
                checked={formData.hasNarcotics}
                error={errors?.hasNarcotics}
                onChange={(val) => {
                  onChange("hasNarcotics", val);
                  if (!val) {
                    onChange("narcoticsDesc", "");
                  }
                }}
                id="narcotics"
                tooltip="Heroin, LSD, Cocaine, Cannabis/Ganja, Others"
              />
              {formData.hasNarcotics === true && (
                <div className="form-wraper">
                  <TextArea
                    name="narcoticsDesc"
                    value={formData.narcoticsDesc}
                    onChange={onChange}
                    error={errors?.narcoticsDesc}
                    maxLength={50}
                  />
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Hazard */}
              <ToggleSwitch
                label={TEXT_CONTENT.MED_TOGGLE_FIVE}
                checked={formData.hasHazard}
                error={errors?.hasHazard}
                onChange={(val) => onChange("hasHazard", val)}
                id="hazard"
                tooltip={TEXT_CONTENT.MED_TOOLTIPP}
              />
              {formData.hasHazard === true && (
                <div className="form-wraper">
                  <CheckboxGroup
                    options={HAZARD_OPTIONS}
                    selected={formData.hazardTypes || []}
                    onChange={(val) => onChange("hazardTypes", val)}
                    columns={2}
                    error={errors?.hazardTypes}
                  />
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Travel */}
              <ToggleSwitch
                label={TEXT_CONTENT.MED_TOOGLE_SIX}
                checked={formData.isTravel}
                error={errors?.isTravel}
                onChange={(val) => {
                  onChange("isTravel", val);
                  if (!val) {
                    onChange("travelCountry", "");
                    onChange("travelPurpose", "");
                    onChange("travelDuration", "");
                  }
                }}
                id="travel"
              />
              {formData.isTravel === true && (
                <div className="form-wraper">
                  <FormField
                    label="Country"
                    halfWidth
                    error={errors?.travelCountry}
                  >
                    <SelectInput
                      name="travelCountry"
                      value={formData.travelCountry}
                      onChange={onChange}
                      options={countryOptions}
                      placeholder="Select"
                    />
                  </FormField>
                  <FormField
                    label="Purpose of Visit"
                    halfWidth
                    error={errors?.travelPurpose}
                  >
                    <SelectInput
                      name="travelPurpose"
                      value={formData.travelPurpose}
                      onChange={onChange}
                      options={VISIT_REASONS}
                    />
                  </FormField>
                  <FormField
                    label="Enter No. of Years"
                    halfWidth
                    error={errors?.travelDuration}
                  >
                    <TextInput
                      name="travelDuration"
                      type="text"
                      value={formData.travelDuration}
                      onChange={(name, value) => {
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      placeholder="e.g: 1 or 2 years"
                      autoComplete="travel-dur-off"
                      maxLength={2}
                    />
                  </FormField>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="proposal-form-actions">
        <button
          className="proposal-btn-continue proposal-btn-continue btn primary-button regular"
          onClick={onContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
};



export const getMedicalFields = (data = {}) => {
  const safeData = {
    ...data,
    selectedExaminations: data.selectedExaminations || [],
    selectedConditions: data.selectedConditions || [],
  };

  const isFemale = String(safeData.gender).toLowerCase() === "female";


  const fields = [
    {
      fieldReference: "medication",
      label: "Medication",
      validation: { required: true },
    },
    {
      fieldReference: "physicalCondition",
      label: "Physical Condition",
      validation: { required: true },
    },
    {
      fieldReference: "conditionsDiagnosed",
      label: "Conditions Diagnosed",
      validation: { required: true },
    },
    {
      fieldReference: "surgery",
      label: "Surgery",
      validation: { required: true },
    },
    {
      fieldReference: "alcoholAdvice",
      label: "AlcoholAdvice",
      validation: { required: true },
    },
  ];

  if (isFemale) {
    fields.push({
      fieldReference: "pregnancy",
      label: "Pregnancy",
      validation: { required: true },
    })
  }
  
  if (safeData.medication === true) {
    fields.push(
      {
        fieldReference: "disease",
        label: "Disease Name",
        validation: { required: true },
      },
      {
        fieldReference: "treatment",
        label: "Treatment/Medication",
        validation: { required: true },
      },
      {
        fieldReference: "duration",
        label: "Duration",
        validation: { required: true },
      },
      {
        fieldReference: "selectedExaminations",
        label: "Examinations",
        validation: { required: true },
      },
    );
  }

  if (safeData.pregnancy === true && isFemale) {
    fields.push(
      {
        fieldReference: "months",
        label: "How many months?",
        validation: { required: true },
      },
      {
        fieldReference: "deliveryDate",
        label: "Expected delivery date",
        validation: { required: true },
      },
    );
  }

  if (safeData.physicalCondition === true) {
    fields.push({
      fieldReference: "physicalConditionDesc",
      label: "Physical Condition",
      validation: { required: true },
    });
  }

  if (safeData.conditionsDiagnosed === true) {
    fields.push({
      fieldReference: "selectedConditions",
      label: "Medical Conditions",
      validation: { required: true },
    });
  }

  if (safeData.selectedConditions?.length > 0) {
    fields.push({
      fieldReference: "conditionsDesc",
      label: "Condition Details",
      validation: { required: true },
    });
  }

  if (safeData.surgery === true) {
    fields.push({
      fieldReference: "surgeryDesc",
      label: "Surgery Details",
      validation: { required: true },
    });
  }

  if (safeData.alcoholAdvice === true) {
    fields.push({
      fieldReference: "alcoholAdviceDesc",
      label: "Alcohol/Drug Advice",
      validation: { required: true },
    });
  }

  return fields;
};

/* ═══════════════════════════════════════════════════
   MEDICAL DETAILS
   ═══════════════════════════════════════════════════ */
const CONDITIONS_LIST = [
  "Diabetes",
  "High blood pressure",
  "Abnormal Lipids / Cholesterol Levels",
  "Cancer",
  "Respiratory Disease (including asthma)",
  "Kidney Disease",
  "Liver Disease",
  "Stroke",
  "Any Nervous System Disorder",
  "Any Disorder of Digestive System",
  "Abnormality of Thyroid",
  "Any blood disorder",
  "Heart Problems",
  "Hepatitis B",
  "Tuberculosis",
  "Psychiatric Disorder",
  "Depression",
  "Mental",
  "Emotional Disorder",
  "Genito - Urinary Disorders",
  "HIV AIDS or a related infection",
  "Hypertension",
  "Deformity",
  "Arthritis",
];
const EXAMINATIONS_LIST = [
  "Medical or specialized examination (including chest x-rays, gynecological investigations, pap smear, or blood tests)",
  "Consultation",
  "Hospitalization",
  "Surgery",
];

const getConsentTexts = (ppt) => ({
  policyDocumentElectronicConsent:
    "I hereby choose to receive the policy document in electronic form. I am aware that this is a voluntary choice and not mandatory, and I may request a physical copy of the policy document at any time during the policy term.",
  premiumPaymentPolicyConsent: `I understand that this is a Regular/Limited premium payment policy and I would need to pay premiums for ${ppt || "XXX"} years.`,
  medicalAssessmentConsent:
    "I am aware that in order to enable the company to assess the risk, I need to undergo medicals as per IndusInd Nippon Life Insurance Company Limited requirements.",
  declarationReadConsent:
    "I have read and understood the content of the declarations.",
});

const CONSENT_KEYS = Object.keys(getConsentTexts());

const createDefaultDeclarationConsents = () =>
  CONSENT_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

const createAcceptedDeclarationConsents = () =>
  CONSENT_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

const normalizeDeclarationConsents = (consents) => {
  const normalized = createDefaultDeclarationConsents();

  if (Array.isArray(consents)) {
    CONSENT_KEYS.forEach((key, index) => {
      normalized[key] = normalizeBooleanField(consents[index]);
    });
    return normalized;
  }

  if (consents && typeof consents === "object") {
    CONSENT_KEYS.forEach((key) => {
      if (key in consents) {
        normalized[key] = normalizeBooleanField(consents[key]);
      }
    });
  }

  return normalized;
};

const areAllDeclarationConsentsAccepted = (consents) =>
  CONSENT_KEYS.every((key) => normalizeBooleanField(consents?.[key]));

const hydrateDeclarationConsents = (data) => {
  const normalizedConsents = normalizeDeclarationConsents(
    data?.declarationConsents ?? data?.formData?.declarationConsents,
  );

  if (areAllDeclarationConsentsAccepted(normalizedConsents)) {
    return normalizedConsents;
  }

  const declarationConsentsAccepted = normalizeBooleanField(
    data?.declarationConsentsAccepted ??
      data?.formData?.declarationConsentsAccepted,
  );

  return declarationConsentsAccepted
    ? createAcceptedDeclarationConsents()
    : normalizedConsents;
};

const hasHydratableMedicalData = (data) =>
  Boolean(
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    Object.keys(data).length > 0,
  );

const MedicalDetailsSection = ({
  formData,
  errors,
  onChange,
  onContinue,
  declarationConsents = {},
  onDeclarationConsentChange,
  userData,
  CONSENT_TEXTS,
}) => {
  const consents = normalizeDeclarationConsents(declarationConsents);
  const allConsentsChecked = areAllDeclarationConsentsAccepted(consents);

  const toggleConsent = (key) => {
    if (onDeclarationConsentChange) {
      onDeclarationConsentChange(key);
    }
  };

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  return (
    <div className="proposal-section">
      <div className="proposal-form-grid">
        <h3 className="proposal-section__title">Medical Details</h3>
        <p className="proposal-section__desc">
          Providing accurate medical details ensures proper policy assessment
          and a hassle-free claim experience for your family.
        </p>
        <div className="form-wraper">
          <div className="proposal-toggle-section bg-shadow-box">
            <div className="proposal-toggle-section-list">
              {/* Long-term medication */}
              <ToggleSwitch
                label="Are you taking any medication/treatment Currently or Any illness/injury needing hospitalization or tests in last 5 years?"
                checked={formData.medication}
                error={errors?.medication}
                onChange={(val) => {
                  onChange("medication", val);
                  if (!val) {
                    onChange("disease", "");
                    onChange("treatment", "");
                    onChange("duration", "");
                    onChange("selectedExaminations", []);
                  }
                }}
                id="medication"
                tooltip="Medication taken for more than 3 months"
              />
              {formData.medication === true && (
                <div className="form-wraper">
                  <FormField
                    label="Name of illness/disorder/injury/disability"
                    halfWidth
                    error={errors?.disease}
                  >
                    <TextInput
                      name="disease"
                      value={formData.disease}
                      // onChange={onChange}
                      onChange={(name, value) => {
                        if (/^[A-Za-z ]*$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      placeholder="Enter"
                      autoComplete="disease-name-off"
                      maxLength={50}
                    />
                  </FormField>
                  <FormField
                    label="Name of medication and drugs"
                    halfWidth
                    error={errors?.treatment}
                  >
                    <TextInput
                      name="treatment"
                      value={formData.treatment}
                      // onChange={onChange}
                      onChange={(name, value) => {
                        if (/^[A-Za-z ]*$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      placeholder="Enter"
                      autoComplete="treatment-off"
                      maxLength={50}
                    />
                  </FormField>
                  <FormField
                    label="Duration"
                    halfWidth
                    error={errors?.duration}
                  >
                    <TextInput
                      name="duration"
                      value={formData.duration}
                      // onChange={onChange}
                      onChange={(name, value) => {
                        if (/^\d*$/.test(value)) {
                          onChange(name, value);
                        }
                      }}
                      placeholder="Enter"
                      maxLength={2}
                      autoComplete="dur-off"
                    />
                  </FormField>
                  <FormField label="Type of medical or specialized investigation or examination done">
                    <CheckboxGroup
                      options={EXAMINATIONS_LIST}
                      selected={formData.selectedExaminations || []}
                      onChange={(val) => onChange("selectedExaminations", val)}
                      columns={2}
                      error={errors?.selectedExaminations}
                    />
                  </FormField>
                </div>
              )}
            </div>
            {/* Physical condition */}
            <div className="proposal-toggle-section-list">
              <ToggleSwitch
                label="Do you have any congenital/birth defects or injury developed that affects your ability to work currently or in the last 2 years for more than 5 days?"
                checked={formData.physicalCondition}
                error={errors?.physicalCondition}
                onChange={(val) => {
                  onChange("physicalCondition", val);
                  if (!val) {
                    onChange("physicalConditionDesc", "");
                  }
                }}
                id="physicalCondition"
                tooltip="Pain or problems in the back, spine, muscles or joint, Congenital Birth Defects, Severe injury, Arthritis, gout, Any Other physical disability"
              />
              {formData.physicalCondition === true && (
                <div className="form-wraper">
                  <TextArea
                    name="physicalConditionDesc"
                    value={formData.physicalConditionDesc}
                    onChange={onChange}
                    error={errors?.physicalConditionDesc}
                    maxLength={50}
                  />
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Conditions diagnosed */}
              <ToggleSwitch
                label="Do you suffer from any medical ailments?"
                checked={formData.conditionsDiagnosed}
                error={errors?.conditionsDiagnosed}
                onChange={(val) => onChange("conditionsDiagnosed", val)}
                id="conditionsDiagnosed"
              />
              {formData.conditionsDiagnosed === true && (
                <div className="form-wraper">
                  <CheckboxGroup
                    options={CONDITIONS_LIST}
                    selected={formData.selectedConditions || []}
                    onChange={(val) => {
                      onChange("selectedConditions", val);
                      if (!val || val.length === 0) {
                        onChange("conditionsDesc", "");
                      }
                    }}
                    columns={2}
                    error={errors?.selectedConditions}
                  />
                  {formData.selectedConditions?.length > 0 && (
                    <TextArea
                      name="conditionsDesc"
                      value={formData.conditionsDesc}
                      onChange={onChange}
                      placeholder="Describe your condition"
                      error={errors?.conditionsDesc}
                      maxLength={50}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Surgery */}
              <ToggleSwitch
                label="Any surgery planned or advised for medical tests in near future?"
                checked={formData.surgery}
                error={errors?.surgery}
                onChange={(val) => {
                  onChange("surgery", val);
                  if (!val) {
                    onChange("surgeryDesc", "");
                  }
                }}
                id="surgery"
              />
              {formData.surgery === true && (
                <div className="form-wraper">
                  <TextArea
                    name="surgeryDesc"
                    value={formData.surgeryDesc}
                    onChange={onChange}
                    error={errors?.surgeryDesc}
                    maxLength={50}
                  />
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              {/* Alcohol/drug advice */}
              <ToggleSwitch
                label="Ever suffered from drug or alcohol addiction or been advised by a doctor to reduce your alcohol/drug intake?"
                checked={formData.alcoholAdvice}
                error={errors?.alcoholAdvice}
                onChange={(val) => {
                  onChange("alcoholAdvice", val);
                  if (!val) {
                    onChange("alcoholAdviceDesc", "");
                  }
                }}
                id="alcoholAdvice"
              />
              {formData.alcoholAdvice === true && (
                <div className="form-wraper">
                  <TextArea
                    name="alcoholAdviceDesc"
                    value={formData.alcoholAdviceDesc}
                    onChange={onChange}
                    error={errors?.alcoholAdviceDesc}
                    placeholder="Please tell us why"
                    maxLength={50}
                    autoComplete="alco-off"
                  />
                </div>
              )}
            </div>
            {userData.gender === "female" ||
              (userData.gender === "Female" && (
                <div className="proposal-toggle-section-list">
                  {/* Long-term medication */}
                  <ToggleSwitch
                    label="Are you currently pregnant?"
                    checked={formData.pregnancy}
                    error={errors?.pregnancy}
                    onChange={(val) => {
                      onChange("pregnancy", val);
                    }}
                    id="pregnancy"
                  />
                  {formData.pregnancy === true && (
                    <div className="form-wraper">
                      <FormField
                        label="How many months?"
                        halfWidth
                        error={errors?.months}
                      >
                        <TextInput
                          name="months"
                          value={formData.months}
                          onChange={(name, value) => {
                            if (/^\d*$/.test(value)) {
                              onChange(name, value);
                            }
                          }}
                          placeholder="Enter"
                          autoComplete="disease-name-off"
                          maxLength={1}
                        />
                      </FormField>
                      <FormField
                        label="Expected delivery date"
                        halfWidth
                        error={errors?.deliveryDate}
                      >
                        <DateInput
                          name="deliveryDate"
                          value={formData.deliveryDate}
                          onChange={onChange}
                          min={formatDate(new Date())}
                          max={formatDate(
                            new Date(
                              new Date().setFullYear(
                                new Date().getFullYear() + 1,
                              ),
                            ),
                          )}
                          autoComplete="off"
                        />
                      </FormField>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
      {/* Consent */}
      <div className="proposal-form-grid">
        <h4 className="proposal-section__subtitle">Declaration</h4>
        <div className="form-wraper">
          <div className="proposal-toggle-section bg-shadow-box"></div>
          {Object.entries(CONSENT_TEXTS).map(([key, text]) => (
            <div key={key} className="proposal-toggle-section-list">
              <label className="proposal-consent-item checkbox">
                <input
                  type="checkbox"
                  checked={consents[key]}
                  onChange={() => toggleConsent(key)}
                />
                <span className="proposal-consent-text">{text}</span>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="proposal-form-actions">
        <button
          className="proposal-btn-continue proposal-btn-continue btn primary-button regular"
          onClick={onContinue}
          disabled={!allConsentsChecked}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MEDICAL TAB (MAIN)
   ═══════════════════════════════════════════════════ */
const INITIAL_MEDICAL_DATA = {
  weight: "",
  height: "",
  heightFeet: "",
  heightInches: "",
  isTobacco: null,
  tobaccoQuantity: "",
  tobaccoOption: "",
  tobaccoYears: "",
  isAlcohol: null,
  alcoholQuantity: "",
  alcoholOption: "",
  alcoholYears: "",
  hasHealthConditions: null,
  healthConditionsDesc: "",
  hasNarcotics: null,
  hasHazard: null,
  narcoticsDesc: "",
  isTravel: null,
  travelCountry: "",
  travelPurpose: "",
  travelDuration: "",
  medicationDesc: "",
  hospitalizationDesc: "",
  physicalConditionDesc: "",
  surgeryDesc: "",
  alcoholAdviceDesc: "",
  disease: "",
  treatment: "",
  duration: "",
  hazardTypes: [],
  medication: null,
  physicalCondition: null,
  conditionsDiagnosed: null,
  conditionsDesc: "",
  surgery: null,
  alcoholAdvice: null,
  selectedConditions: [],
  selectedExaminations: [],
  months: "",
  deliveryDate: "",
  pregnancy:null
};

const MedicalTab = forwardRef((props, ref) => {
  const quoteResult = useSelector((s) => s.journey?.quoteResult);
  const biPayload = quoteResult?.biPayload || {};
  const ppt = biPayload?.prPpt;
  const consentTexts = useMemo(() => getConsentTexts(ppt), [ppt]);

  const { onComplete, documents, setDocuments, onPersistStep, userData } =
    props;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState("lifestyle");
  const [completedSteps, setCompletedSteps] = useState([]);
  const medicalData = useSelector((state) => state.journey.stepData.medical);
  const lifeAssuredForm = useSelector(
    (state) => state.journey.stepData?.lifeAssured || {},
  );
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const [formData, setFormData] = useState(() =>
    hasHydratableMedicalData(medicalData)
      ? normalizeMedicalBooleanFields(medicalData)
      : INITIAL_MEDICAL_DATA,
  );
  const [declarationConsents, setDeclarationConsents] = useState(() =>
    hydrateDeclarationConsents(medicalData),
  );
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasHydratedInitialMedicalRef = useRef(false);

  const handleDeclarationConsentChange = useCallback((key) => {
    setDeclarationConsents((prev) => ({
      ...normalizeDeclarationConsents(prev),
      [key]: !normalizeBooleanField(prev?.[key]),
    }));
  }, []);

  useEffect(() => {
    if (hasHydratedInitialMedicalRef.current) return;
    if (hasHydratableMedicalData(medicalData)) {
      setFormData(normalizeMedicalBooleanFields(medicalData));
      setDeclarationConsents(hydrateDeclarationConsents(medicalData));
      hasHydratedInitialMedicalRef.current = true;
    }
  }, [medicalData]);

  useEffect(() => {
    if (location.state?.section) {
      setCurrentStep(location.state.section);
    }
  }, [location.state]);

  useEffect(() => {
    const nextStep = normalizeMedicalStep(
      location.state?.section || medicalData?.stepId,
    );
    setCurrentStep(nextStep);
    setCompletedSteps(getMedicalCompletedSteps(nextStep, medicalData));
  }, [location.state?.section, medicalData]);

  const handleChange = useCallback((name, value) => {
    const normalizedValue = BOOLEAN_TOGGLE_FIELDS.has(name)
      ? normalizeBooleanField(value)
      : value;
    setFormData((prev) => ({ ...prev, [name]: normalizedValue }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const STEP_VALIDATORS = {
    lifestyle: (data) => getLifestyleFields(data),
    medical: (data) => getMedicalFields(data),
  };

  const validateStep = useCallback(
    (stepId) => {
      const resolver = STEP_VALIDATORS[stepId];
      if (!resolver) return true;
      
      const fields = resolver(formData);

      const validationErrors = validateForm(formData, fields);
      setErrors(validationErrors);

      return Object.keys(validationErrors).length === 0;
    },
    [formData],
  );

  const leadData = useSelector((state) => state.journey?.leadFormData);
  const sumAssured = useSelector(
    (state) => state.journey?.quoteResult?.coverAmount,
  );
  const education = useSelector(
    (state) => state.journey?.leadFormData?.educationalLevel,
  );
  const organisationName =
    leadData?.organisation || leadData?.organization || "";
  const isOrgListed = useOrganisationListed(organisationName, apiCache);
  const showSurrogateIncomeDocs =
    (education === "Graduate" || education === "Post Graduate") &&
    Number(sumAssured) <= 20000000;

  const REQUIRED_DOCS = useMemo(() => {
    const baseDocs = [
      "photograph",
      "addressProof",
      "ageProof",
      "identificationProof",
    ];
    const orgDocs = isOrgListed
      ? ["companyIdFront", "companyIdBack"]
      : ["incomeProof"];
    const surrogateDocs = showSurrogateIncomeDocs
      ? ["surrogateIncomeProof"]
      : [];
    return [...baseDocs, ...orgDocs, ...surrogateDocs];
  }, [isOrgListed, showSurrogateIncomeDocs]);

  const DOC_LABELS = useMemo(() => ({
    photograph: "Photograph",
    addressProof: "Address Proof",
    ageProof: "Age Proof",
    incomeProof: "Financial/Income Documents",
    surrogateIncomeProof: "Surrogate Income Documents",
    identificationProof: "Identification Proof",
    companyIdFront: "Company ID Card (Front)",
    companyIdBack: "Company ID Card (Back)",
  }));

  const hasUploaded = (docKey) => {
    if (!documents) return false;
    if (docKey === "addressProof") {
      return (
        isProposalDocumentSlotFilled(documents.addressProof) ||
        isProposalDocumentSlotFilled(documents.addressProofPermanent) ||
        isProposalDocumentSlotFilled(documents.addressProofCommunication)
      );
    }
    if (docKey === "identificationProof") {
      return isProposalDocumentSlotFilled(
        documents.identificationProof || documents.idProof,
      );
    }
    if (docKey === "companyIdFront") {
      return isProposalDocumentSlotFilled(documents.companyIdFront);
    }
    if (docKey === "companyIdBack") {
      return isProposalDocumentSlotFilled(documents.companyIdBack);
    }
    if (docKey === "surrogateIncomeProof") {
      return isProposalDocumentSlotFilled(documents.surrogateIncomeProof);
    }
    return isProposalDocumentSlotFilled(documents?.[docKey]);
  };

  const pendingDocs = REQUIRED_DOCS.filter(
    (docKey) => !hasUploaded(docKey),
  ).map((docKey) => DOC_LABELS[docKey]);

  // Prefill the modal with the already-uploaded docs.
  // LifeAssured may store alternate slot ids (e.g. addressProofPermanent/idProof),
  // so we map them into the standard modal slot ids.
  const initialFiles = useMemo(() => {
    const addressProof =
      documents?.addressProof ||
      documents?.addressProofPermanent ||
      documents?.addressProofCommunication ||
      null;
    const identificationProof =
      documents?.identificationProof || documents?.idProof || null;

    const baseFiles = {
      ...(documents?.photograph ? { photograph: documents.photograph } : {}),
      ...(documents?.ageProof ? { ageProof: documents.ageProof } : {}),
      ...(addressProof ? { addressProof } : {}),
      ...(identificationProof ? { identificationProof } : {}),
      ...(documents?.fatcaDeclaration
        ? { fatcaDeclaration: documents.fatcaDeclaration }
        : {}),
    };
    if (isOrgListed) {
      return {
        ...baseFiles,
        ...(documents?.companyIdFront
          ? { companyIdFront: documents.companyIdFront }
          : {}),
        ...(documents?.companyIdBack
          ? { companyIdBack: documents.companyIdBack }
          : {}),
        ...(documents?.surrogateIncomeProof
          ? { surrogateIncomeProof: documents.surrogateIncomeProof }
          : {}),
      };
    }
    return {
      ...baseFiles,
      ...(documents?.incomeProof ? { incomeProof: documents.incomeProof } : {}),
      ...(documents?.surrogateIncomeProof
        ? { surrogateIncomeProof: documents.surrogateIncomeProof }
        : {}),
    };
  }, [documents, isOrgListed, showSurrogateIncomeDocs]);

  const validateFullTab = () => {
    const steps = ["lifestyle", "medical"];

    for (let step of steps) {
      const isValid = validateStep(step);
      if (!isValid) {
        setCurrentStep(step);
        return false;
      }
    }

    return true;
  };

  useImperativeHandle(ref, () => ({
    validateTab: validateFullTab,
  }));

  const handleStepComplete = useCallback(
    async (stepId) => {
      if (!validateStep(stepId)) return;
      const declarationConsentsAccepted =
        areAllDeclarationConsentsAccepted(declarationConsents);
      if (onPersistStep) {
        setLoading(true);
        try {
          const saved = await onPersistStep(`medical-${stepId}`, {
            tab: "medical",
            stepId,
            formData,
            declarationConsents:
              stepId === "medical" ? declarationConsents : undefined,
          });
          if (!saved) {
            return;
          }
        } finally {
          setLoading(false);
        }
      }

      setCompletedSteps((prev) =>
        prev.includes(stepId) ? prev : [...prev, stepId],
      );
      const stepIndex = SIDE_STEPSS.findIndex((s) => s.id === stepId);
      if (stepIndex < SIDE_STEPSS.length - 1) {
        const nextStep = SIDE_STEPSS[stepIndex + 1].id;
        setCurrentStep(nextStep);
        navigate("/proposal", {
          state: {
            tab: "medical",
            section: nextStep,
          },
          replace: true,
        });
      } else if (onComplete) {
        onComplete({
          ...formData,
          tab: "medical",
          stepId,
          declarationConsents,
          declarationConsentsAccepted,
        });
      }
    },
    [formData, declarationConsents, onComplete, onPersistStep],
  );

  const renderSection = () => {
    switch (currentStep) {
      case "lifestyle":
        return (
          <LifestyleDetails
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onContinue={() => {
              void handleStepComplete("lifestyle");
            }}
          />
        );
      case "medical":
        return (
          <MedicalDetailsSection
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onContinue={() => {
              void handleStepComplete("medical");
            }}
            declarationConsents={declarationConsents}
            onDeclarationConsentChange={handleDeclarationConsentChange}
            userData={userData}
            CONSENT_TEXTS={consentTexts}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="proposal-tab-layout">
      {loading && (
        <div
          className="quote-premium-loader-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <BrandLoader color="#97262A" loading={true} size={15} />
        </div>
      )}
      <SideProgress
        steps={SIDE_STEPSS}
        currentStepId={currentStep}
        completedSteps={completedSteps}
      />
      <div className="proposal-tab-main">
    
        {renderSection()}
        <PendingDocumentsBar
          docs={pendingDocs}
          onUploadClick={() => setShowUploadModal(true)}
        />
      </div>

      <UploadDocumentsModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        documentFormContext={{
          formData: lifeAssuredForm,
          apiCache,
        }}
        initialFiles={initialFiles}
        onUploadComplete={({ files: uploadedFiles, removedSlotIds }) => {
          const updatedDocs = {
            ...documents,
            ...uploadedFiles,
          };

          // Keep shared docs consistent with LifeAssured's alternate slot ids.
          (removedSlotIds || []).forEach((slotId) => {
            if (slotId === "addressProof") {
              delete updatedDocs.addressProof;
              delete updatedDocs.addressProofPermanent;
              delete updatedDocs.addressProofCommunication;
              return;
            }
            if (slotId === "identificationProof") {
              delete updatedDocs.identificationProof;
              delete updatedDocs.idProof;
              return;
            }
            delete updatedDocs[slotId];
          });

          setDocuments(updatedDocs);

          dispatch(journeyActions.setDocuments(updatedDocs));
        }}
      />
    </div>
  );
});

export default MedicalTab;
